// Riyadah — gym workout logger. Hayah → Exercise.
//
// Flow: Hub → Pick → Active → Finish → History
// Designed with the user's PT-led, 3x/week, circuit-style training in mind.
// Hub is the quiet landing screen; Pick chooses a routine; Active is the
// live logger with rest timer and ghost-row showing last session's numbers
// for progressive-overload visibility; Finish celebrates and saves;
// History shows everything so far.
//
// Visual DNA matches the design handoff in /sirat/project/Exercise Logging.html:
// dark warm palette (#050507 / #0A0A0E / cream ink), amber + teal + sage +
// coral accents, JetBrains Mono for numbers/labels, Fraunces serif italic
// for hero copy.

import { Fragment, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { registerPlugin } from "@capacitor/core";
import { hapticTap, hapticConfirm, hapticMilestone, hapticHayahSuccess, hapticHayahSelection, hapticHayahSwipe, hapticHayahToggle, hapticWarning, hapticError, hapticSelectionChange } from "../hayahHaptics";
import { getGeminiKey, callGemini } from "./geminiAI";
import { COACH_VOICE, EVIDENCE_STANDARD, OUTPUT_PLAIN_TEXT, OUTPUT_JSON_STRICT, OUTPUT_BULLETS_BOLD, composeSystem } from "./hayah/hayahPrompts";
import AIBulletText from "./hayah/AIBulletText";
import { getUserProfile, formatProfileForPrompt } from "./hayah/userProfile";
import { readAllData, buildPostWorkoutContext } from "./hayah/aiContext";
import { publishSources, workoutGenSources, postWorkoutSources, mobilitySources } from "./hayah/pipelineSources";
import AIAnalysingPipeline from "./hayah/AIAnalysingPipeline";
import { runJob as runAiJob, subscribe as subscribeAiJob, isInFlight as isAiJobInFlight, lastError as lastAiJobError } from "./hayah/aiBackgroundJob";
import { MODELS } from "./hayah/aiConfig";
import { loadReadinessCache } from "./hayah/readinessCache";
import { capByFeel, loadFeel } from "./hayah/readinessScore";
import { generateReadiness } from "./hayah/readinessAI";
import ReadinessCard from "./hayah/ReadinessCard";
import AICostBadge from "./hayah/AICostBadge";
import StreakInfoSheet from "./hayah/StreakInfoSheet";
import {
  goalStalenessMap, suggestGoal, suggestDuration, suggestEquipment,
  previewEstimate, estimateGenerationCostUSD, shouldShowFuelReminder,
  exercisePreferences,
} from "./hayah/aiWorkoutSmart";
import { getDriverExplanation } from "./hayah/driverGlossary";
import { computeReadinessInputs } from "./hayah/readiness";
import { computeCardioFitness } from "./hayah/cardioFitness";
import { loadExercisePicks, toggleLikedExercise, toggleHiddenExercise, youtubeHowToUrl, showHowToLinks, setShowHowToLinks } from "./hayah/exercisePicks";
import { getMounjaroDay } from "./hayah/mounjaroCycle";
import { drawExerciseShareCard } from "./ShareCard";
import { useToast } from "../ToastContext";
import {
  RIYADAH_TEMPLATES, RIYADAH_PICKER_EXERCISES, EXERCISE_LIBRARY,
  loadHistory, saveHistory, loadLastExerciseData,
  loadLastSessionPerformance, progressionTarget,
  loadGoals, saveGoals,
  loadRoutines, upsertRoutine, deleteRoutine, newRoutineId,
  loadHiddenTemplateIds, hideTemplate, unhideTemplate,
  snapshotSessionAsRoutine,
  loadActiveSession, saveActiveSession, clearActiveSession,
  loadCoachWorkout, clearCoachWorkout,
  calcVolumeFlat, calcSetsFlat, buildWorkout, suggestNext, extractMuscleGroups,
  computeWorkoutStreaks, maybeSeedHistory,
  fmtDuration, fmtDurHuman, todayLabel, nowTimeLabel,
} from "./riyadahData";
import {
  resolveExerciseId, resolveExerciseName, canonicaliseWorkoutExercises,
  mergeExercise,
} from "./exerciseCanonical";
import { importWorkoutFromText } from "./hayah/workoutImport";
import HayahSwipeRow from "./hayah/SwipeRow";
import { BodyCompositionPanel } from "./CalorieCounter";

// localStorage key for the most-recent mobility / stretching plan. Lives
// here at module scope so both the generator and the cache reader (which
// runs at component mount before any effects fire) can reference the same
// constant.
const MOBILITY_CACHE_KEY = "hayahMobilityPlan";

// Most-recent real body mass for burn estimates. Prefers HealthKit-synced
// bodyMassKg (newest day in calorieLog), then the manually-entered
// ghidhaProfile weight, then a last-ditch 80kg. No more silent 80kg default.
function realBodyMassKg() {
  try {
    const log = JSON.parse(localStorage.getItem("calorieLog") || "{}");
    for (const d of Object.keys(log).sort().reverse()) {
      const bm = parseFloat(log[d]?.bodyMassKg);
      if (bm > 0) return bm;
    }
  } catch {}
  try {
    const p = JSON.parse(localStorage.getItem("ghidhaProfile") || "{}");
    const w = parseFloat(p.weight);
    if (w > 0) return w;
  } catch {}
  return 80;
}

// Real age from the user's profile (ghidhaProfile.age), for HR-zone math.
// Returns null when unknown so callers can fall back to a sensible default.
function realUserAge() {
  try {
    const p = JSON.parse(localStorage.getItem("ghidhaProfile") || "{}");
    const a = parseInt(p.age, 10);
    if (a > 0 && a < 120) return a;
  } catch {}
  return null;
}

// Most-recent resting HR for Karvonen HR-reserve zones — today's value, then
// the recent fallback, then the latest day on record. Null when unknown.
function realRestingHr() {
  try {
    const log = JSON.parse(localStorage.getItem("calorieLog") || "{}");
    for (const d of Object.keys(log).sort().reverse()) {
      const rhr = log[d]?.restingHeartRate || log[d]?.restingHeartRateRecent;
      if (rhr > 0) return Math.round(rhr);
    }
  } catch {}
  return null;
}
// Per-stretch "done" state, keyed by the plan's ts so a regenerated plan
// resets progress automatically.
const MOBILITY_DONE_KEY  = "hayahMobilityDone";

// Mutable so dev/test code can swap in a mock. See
// __setGhidhaHealthForTests below — used by the "Mock import (dev)"
// button in HayahSettings so the import flow can be exercised on a
// phone with no Apple Watch / HKWorkouts.
let GhidhaHealth = registerPlugin("GhidhaHealth");
export function __setGhidhaHealthForTests(mock) {
  const prev = GhidhaHealth;
  GhidhaHealth = mock;
  return () => { GhidhaHealth = prev; };
}
const SiratWatch  = registerPlugin("SiratWatch");
const SiratRiyadahActivity = registerPlugin("SiratRiyadahActivity");

// Hayah Settings gates — read user prefs at call time so toggles take
// effect without remounting. Default true preserves historical behaviour.
function getHayahPref(group, key, fallback = true) {
  try {
    const p = JSON.parse(localStorage.getItem("hayahPrefs") || "null");
    const v = p?.[group]?.[key];
    return v == null ? fallback : v;
  } catch { return fallback; }
}
const watchRoutinePushOn = () => getHayahPref("watch", "routinePush", true);
const liveActivityOn     = () => getHayahPref("watch", "liveActivity", true);
const defaultRestSec     = () => Math.max(15, Math.min(600, getHayahPref("exercise", "defaultRestSec", 60)));
const autoProgressOn     = () => getHayahPref("exercise", "autoProgress", true);
const weightStepKg       = () => 2.5;
const repsStep           = () => 1;

// Apple Watch / HealthKit detection. The native plugin throws on web, so the
// promise rejection is the signal that we're running in the PWA. Cached at
// module level so we don't re-probe per render.
let _watchAvailableCache = null;
async function isWatchAvailable() {
  if (_watchAvailableCache !== null) return _watchAvailableCache;
  try { await GhidhaHealth.isAvailable(); _watchAvailableCache = true; }
  catch { _watchAvailableCache = false; }
  return _watchAvailableCache;
}

// Fuzzy exercise filter — matches case-insensitive against name, muscle
// group(s), and category. Returns all when the query is empty. Splits
// multi-word queries into substring tokens (e.g. "leg ext" matches
// "Leg Extension"), and also tries each token against the start of
// every word for prefix matching ("lat" → "Lat Pulldown").
function filterExercises(list, q) {
  const term = (q || "").trim().toLowerCase();
  if (!term) return list;
  const tokens = term.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return list;
  return list.filter(ex => {
    const fields = [
      ex.name || "",
      ex.muscles || "",
      ex.primary || "",
      Array.isArray(ex.secondary) ? ex.secondary.join(" ") : "",
      ex.category || "",
      ex.type || "",
    ];
    const hay = fields.join(" ").toLowerCase();
    return tokens.every(t => hay.includes(t));
  });
}

// Pull the HKWorkout (preferring HIIT) that overlaps the [startTs, endTs]
// session window. Returns { activityType, calories, avgHr, maxHr,
// hrZoneSeconds } or null if nothing matched. Window is generous: any
// workout with ±10 min overlap counts; if nothing overlaps, falls back to
// the most recent workout from the last 2h so an Apple Watch HIIT started
// just before opening the app still gets attached.
async function fetchSessionWatchData(startTs, endTs) {
  try {
    const lookbackMs = 2 * 60 * 60 * 1000;
    const since = new Date(Math.min(startTs, Date.now() - lookbackMs) - 60_000).toISOString();
    const res = await GhidhaHealth.readWorkouts({ since, age: realUserAge() || undefined, restingHr: realRestingHr() || undefined });
    const workouts = res?.workouts || [];
    if (workouts.length === 0) return null;
    const slack = 10 * 60 * 1000;
    const overlapping = workouts.filter(w => {
      const ws = new Date(w.startDate).getTime();
      const we = new Date(w.endDate).getTime();
      return ws <= endTs + slack && we >= startTs - slack;
    });
    const candidates = overlapping.length > 0
      ? overlapping
      : workouts.filter(w => new Date(w.endDate).getTime() >= Date.now() - lookbackMs);
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => {
      const aHiit = a.activityType === "HIIT" ? 1 : 0;
      const bHiit = b.activityType === "HIIT" ? 1 : 0;
      if (aHiit !== bHiit) return bHiit - aHiit;
      return (b.durationSec || 0) - (a.durationSec || 0);
    });
    const w = candidates[0];
    return {
      activityType: w.activityType,
      calories: Math.round(w.totalEnergyBurned || 0),
      avgHr: Math.round(w.avgHr || 0),
      maxHr: Math.round(w.maxHr || 0),
      hrZoneSeconds: w.hrZoneSeconds || null,
      hrRecoveryBpm: w.hrRecoveryBpm ? Math.round(w.hrRecoveryBpm) : null,
      durationSec: Math.round(w.durationSec || 0),
    };
  } catch { return null; }
}

// Imports HKWorkouts from the last `days` days into sirat-workouts as
// standalone history entries when they don't overlap an existing one
// (de-dup by ±15min start). Also re-attaches watch data to existing
// entries that had `watch: null` (e.g. when the user finished the app
// session before the watch synced its workout to the phone).
//
// Returns { added, attached, total } so the UI can confirm what changed.
export async function importAppleWatchWorkouts({ days = 7 } = {}) {
  let added = 0, attached = 0;
  try {
    const sinceMs = Date.now() - days * 24 * 60 * 60 * 1000;
    const since = new Date(sinceMs).toISOString();
    const res = await GhidhaHealth.readWorkouts({ since, age: realUserAge() || undefined, restingHr: realRestingHr() || undefined });
    const workouts = res?.workouts || [];
    let history = (() => { try { return JSON.parse(localStorage.getItem("sirat-workouts") || "[]") || []; } catch { return []; } })();
    const slack = 15 * 60 * 1000;
    for (const w of workouts) {
      const ws = new Date(w.startDate).getTime();
      const we = new Date(w.endDate).getTime();
      if (!Number.isFinite(ws) || !Number.isFinite(we)) continue;
      // Try to attach to an existing sirat-workout that overlaps.
      const matchIdx = history.findIndex(h => {
        const hts = h.timestamp;
        if (!Number.isFinite(hts)) return false;
        return hts >= ws - slack && hts <= we + slack;
      });
      if (matchIdx >= 0) {
        if (!history[matchIdx].watch) {
          history[matchIdx] = {
            ...history[matchIdx],
            watch: {
              activityType: w.activityType,
              calories: Math.round(w.totalEnergyBurned || 0),
              avgHr: Math.round(w.avgHr || 0),
              maxHr: Math.round(w.maxHr || 0),
              hrZoneSeconds: w.hrZoneSeconds || null,
              hrRecoveryBpm: w.hrRecoveryBpm ? Math.round(w.hrRecoveryBpm) : null,
              durationSec: Math.round(w.durationSec || 0),
            },
          };
          attached++;
        }
        continue;
      }
      // No match — import as a watch-only entry so it shows up in History.
      const d = new Date(we);
      history.unshift({
        name: `${w.activityType || "Workout"} (Watch)`,
        date: d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }),
        time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
        timestamp: we,
        duration: Math.round(w.durationSec || 0),
        sets: 0,
        volume: 0,
        exercises: [],
        prs: 0,
        cardio: null,
        watchOnly: true,
        watch: {
          activityType: w.activityType,
          calories: Math.round(w.totalEnergyBurned || 0),
          avgHr: Math.round(w.avgHr || 0),
          maxHr: Math.round(w.maxHr || 0),
          hrZoneSeconds: w.hrZoneSeconds || null,
          hrRecoveryBpm: w.hrRecoveryBpm ? Math.round(w.hrRecoveryBpm) : null,
          durationSec: Math.round(w.durationSec || 0),
        },
      });
      added++;
    }
    history.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    try {
      localStorage.setItem("sirat-workouts", JSON.stringify(history));
      window.dispatchEvent(new StorageEvent("storage", { key: "sirat-workouts" }));
    } catch {}
  } catch {}
  return { added, attached };
}

const HADITH = {
  en: '"The strong believer is better and more beloved to Allah than the weak believer."',
  ref: "Muslim 2664",
};
const PRE_WORKOUT_SNACK  = "ON Pre · Oats 30g · USN Iso Whey";
const POST_WORKOUT_SNACK = "USN Iso Whey 1–2 scoops";

// Pre-workout fuel options. Macros approximate, sourced from product labels:
// ON Gold Standard Pre-Workout (1 scoop ≈ 9g), instant oats raw, USN Blue Lab
// Iso Whey (1 scoop ≈ 33g). Logged into Ghidha (the Calorie Counter)
// directly via the existing `calorieLog` localStorage schema so it shows up
// in today's totals.
const FUEL_FOODS = {
  pre:        { name: "ON Pre-Workout",  brand: "Optimum Nutrition", calories: 5,   protein: 0,  carbs: 1,  fat: 0,   fiber: 0, sugar: 0,  sodium: 120, servingG: 9,   servingLabel: "1 scoop",  slot: "supplements" },
  oats:       { name: "Instant Oats",    brand: "Oats",              calories: 117, protein: 4,  carbs: 20, fat: 2,   fiber: 3, sugar: 0,  sodium: 0,   servingG: 30,  servingLabel: "30g",      slot: "breakfast"   },
  usn1:       { name: "USN Iso Whey",    brand: "USN",               calories: 100, protein: 25, carbs: 1,  fat: 0,   fiber: 0, sugar: 0,  sodium: 50,  servingG: 33,  servingLabel: "1 scoop",  slot: "whey" },
  usn2:       { name: "USN Iso Whey",    brand: "USN",               calories: 200, protein: 50, carbs: 2,  fat: 0,   fiber: 0, sugar: 0,  sodium: 100, servingG: 66,  servingLabel: "2 scoops", slot: "whey" },
  onWhey1:    { name: "Gold Standard Whey", brand: "Optimum Nutrition", calories: 120, protein: 24, carbs: 3, fat: 1, fiber: 1, sugar: 1, sodium: 50,  servingG: 30, servingLabel: "1 scoop",  slot: "whey" },
  onWhey2:    { name: "Gold Standard Whey", brand: "Optimum Nutrition", calories: 240, protein: 48, carbs: 6, fat: 2, fiber: 2, sugar: 2, sodium: 100, servingG: 60, servingLabel: "2 scoops", slot: "whey" },
  milk:       { name: "Full Cream Milk", brand: "Clover",            calories: 160, protein: 8,  carbs: 12, fat: 9,   fiber: 0, sugar: 12, sodium: 110, servingG: 250, servingLabel: "250ml",    slot: "whey" },
};

function todayKey() {
  const d = new Date();
  return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
}
function genFoodId() { return Math.random().toString(36).slice(2) + Date.now().toString(36); }

// Append the chosen pre-workout items to today's calorieLog. Items are written
// into the matching meal slot ("supplements" for ON pre / whey, "breakfast"
// for oats) so they surface naturally in Ghidha's daily breakdown.
function logFuelItems(items) {
  const today = todayKey();
  let log;
  try { log = JSON.parse(localStorage.getItem("calorieLog") || "{}"); } catch { log = {}; }
  const day = log[today] || { meals: { breakfast: [], lunch: [], dinner: [] }, water: 0, weight: null };
  day.meals = day.meals || {};
  const now = new Date().toISOString();
  for (const food of items) {
    const slot = food.slot || "supplements";
    const list = day.meals[slot] || [];
    list.push({
      id: genFoodId(),
      name: food.name, brand: food.brand,
      calories: food.calories, protein: food.protein, carbs: food.carbs,
      fat: food.fat, fiber: food.fiber, sugar: food.sugar, sodium: food.sodium,
      servingG: food.servingG, servingLabel: food.servingLabel,
      source: "sirat-riyadah", halal: null, loggedAt: now,
    });
    day.meals[slot] = list;
  }
  log[today] = day;
  try { localStorage.setItem("calorieLog", JSON.stringify(log)); } catch {}
}

// ───────── Theme ─────────
function useTheme(darkMode) {
  return useMemo(() => {
    if (!darkMode) {
      // Light variant kept conservative — the design is dark-first.
      return {
        bg0: "#f5f1ea", bg1: "#ffffff", bg2: "#fafafa", bg3: "#f0eee8", bg4: "#e8e6df",
        line: "#dcd9d0", lineSoft: "#e8e5dc",
        ink1: "#0a0a0e", ink2: "#3a3833", ink3: "#827e72", ink4: "#a8a59c",
        amber: "#b87008", amberSoft: "#c98a1f", amberDim: "rgba(184,112,8,0.10)",
        teal: "#009e87", tealDim: "rgba(0,158,135,0.10)",
        coral: "#c95a48", coralDim: "rgba(201,90,72,0.10)",
        sage: "#5ba484", sageDim: "rgba(91,164,132,0.10)",
      };
    }
    return {
      bg0: "#050507", bg1: "#0A0A0E", bg2: "#101015", bg3: "#16161D", bg4: "#1C1C25",
      line: "#22222C", lineSoft: "#1A1A22",
      // Text tiers brightened for low-brightness bedtime readability.
      // ink3 was #827E72 (~50% lum) → #A6A294 (~64%); ink4 was #4A483F
      // (~30%) → #6F6C5F (~43%). Visual hierarchy preserved, but the
      // dim end is no longer washed out at minimum brightness.
      ink1: "#F4EFE0", ink2: "#C9C2B0", ink3: "#A6A294", ink4: "#6F6C5F",
      amber: "#E0A030", amberSoft: "#F0C26B", amberDim: "rgba(224,160,48,0.12)",
      teal: "#4FD9C2", tealDim: "rgba(79,217,194,0.12)",
      coral: "#D97A6A", coralDim: "rgba(217,122,106,0.12)",
      sage: "#7CC4A0", sageDim: "rgba(124,196,160,0.12)",
    };
  }, [darkMode]);
}

// ───────── Icons ─────────
function Icon({ name, size = 16, color = "currentColor" }) {
  const s = { width: size, height: size, display: "inline-block", verticalAlign: "middle", flexShrink: 0 };
  switch (name) {
    case "check":   return <svg style={s} viewBox="0 0 16 16" fill="none"><path d="M3 8.5L6.5 12L13 5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "plus":    return <svg style={s} viewBox="0 0 16 16" fill="none"><path d="M8 3V13M3 8H13" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "x":       return <svg style={s} viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke={color} strokeWidth="2" strokeLinecap="round"/></svg>;
    case "back":    return <svg style={s} viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8L10 13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "clock":   return <svg style={s} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke={color} strokeWidth="1.5"/><path d="M8 5V8.5L10.5 10" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></svg>;
    case "share":   return <svg style={s} viewBox="0 0 16 16" fill="none"><path d="M8 2V10M5 5L8 2L11 5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 9V13H13V9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "trash":   return <svg style={s} viewBox="0 0 16 16" fill="none"><path d="M3 4H13M6.5 4V2.5H9.5V4M5 4V13H11V4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "dots":    return <svg style={s} viewBox="0 0 16 16" fill={color}><circle cx="3" cy="8" r="1.4"/><circle cx="8" cy="8" r="1.4"/><circle cx="13" cy="8" r="1.4"/></svg>;
    case "edit":    return <svg style={s} viewBox="0 0 16 16" fill="none"><path d="M11.5 2.5L13.5 4.5L5 13H3V11L11.5 2.5Z" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    default: return null;
  }
}

// ───────── Block header (Active screen) ─────────
// Visual divider between exercise blocks (matches PT's WhatsApp "Block A —
// Pull · Power · Legs" formatting). Letter pill on the left, optional
// descriptive label, "3 rounds × 10" on the right.
function BlockHd({ theme, letter, label, rounds }) {
  return (
    <div style={{
      margin: "14px 16px 6px", padding: "10px 12px",
      background: `linear-gradient(135deg, rgba(224,160,48,0.10), rgba(224,160,48,0.04))`,
      border: `0.5px solid rgba(224,160,48,0.20)`, borderRadius: 12,
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: theme.amber, color: "#0A0A0E",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 13, fontWeight: 800, letterSpacing: "-0.02em",
      }}>{letter}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: theme.amber }}>
          Block {letter}
        </div>
        {label && (
          <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1, letterSpacing: "-0.01em" }}>{label}</div>
        )}
      </div>
      {rounds > 0 && (
        <span className="ry-mono" style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: theme.ink4 }}>
          {rounds} × 10
        </span>
      )}
    </div>
  );
}

// ───────── Section header ─────────
function SecHd({ theme, num, label, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 16px", margin: "18px 0 10px" }}>
      {num ? (
        <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10, fontWeight: 600, letterSpacing: "0.2em", color: theme.amber }}>{num}</span>
      ) : null}
      <span style={{ flex: 1, height: 0.5, background: theme.line }} />
      <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.ink4 }}>{label}</span>
      {right}
    </div>
  );
}

// ───────── Component ─────────
export default function Riyadah({ darkMode, isLandscape }) {
  const theme = useTheme(darkMode);

  // 'hub' | 'pick' | 'active' | 'finish' | 'activity' | 'goals'
  const [screen, setScreen] = useState(() => {
    // Persist Riyadah's screen across iOS WKWebView reaps so app-switcher
    // returns don't snap the user back to the hub from history/reports/goals.
    // 'active' is intentionally NOT restored — coming back to a stale active
    // session without its state would feel broken; we drop to 'hub' instead.
    // If an in-progress workout is persisted, jump straight back into it —
    // takes precedence over the lastScreen restore.
    try {
      const persisted = loadActiveSession();
      if (persisted) return "active";
    } catch {}
    try { if (loadCoachWorkout()) return "hub"; } catch {}
    try {
      const saved = localStorage.getItem("riyadahLastScreen");
      const lastTabAt = parseInt(localStorage.getItem("lastTabAt") || "0", 10);
      const fresh = saved && lastTabAt && (Date.now() - lastTabAt) < 4 * 60 * 60 * 1000;
      const valid = ["hub", "activity", "goals"];
      if (fresh && valid.includes(saved)) return saved;
    } catch {}
    return "hub";
  });
  useEffect(() => {
    try { localStorage.setItem("riyadahLastScreen", screen); } catch {}
  }, [screen]);

  // One-time history canonicalisation. PRs/bests are keyed by canonical
  // exercise id now (see resolveExerciseId), so we walk every saved
  // workout once and rewrite each ex.name + ex.id to the library's
  // canonical form. Steve's variant spellings ("Dumbbell Goblet Squats"
  // vs "DB Goblet Squat") collapse into a single stat line; unknown
  // names get learned into the custom library with a stable id.
  // Version bumped on every alias-table expansion.
  useEffect(() => {
    // v3 — adds deep-norm (synonym + modifier-strip + word-sort) so old
    // entries like "Sumo Goblet Squat" / "Heavy Goblet" merge into the
    // canonical "DB Goblet Squat" row. Bump on every alias-table
    // expansion so existing user logs get re-canonicalised on launch.
    const VERSION = 3;
    try {
      const stored = parseInt(localStorage.getItem("riyadahNameMigrationV") || "0", 10);
      if (stored >= VERSION) return;
      const raw = localStorage.getItem("sirat-workouts");
      if (raw) {
        const hist = JSON.parse(raw);
        if (Array.isArray(hist)) {
          let dirty = false;
          for (const w of hist) {
            if (!Array.isArray(w?.exercises)) continue;
            const canon = canonicaliseWorkoutExercises(w.exercises);
            // Compare shallow to know if anything changed (preserves
            // the original array if no rename was needed).
            let changed = canon.length !== w.exercises.length;
            if (!changed) {
              for (let i = 0; i < canon.length; i++) {
                if (canon[i]?.name !== w.exercises[i]?.name ||
                    canon[i]?.id   !== w.exercises[i]?.id) { changed = true; break; }
              }
            }
            if (changed) { w.exercises = canon; dirty = true; }
          }
          if (dirty) localStorage.setItem("sirat-workouts", JSON.stringify(hist));
        }
      }
      localStorage.setItem("riyadahNameMigrationV", String(VERSION));
      // Bumping the version refreshes `history` from disk so the new
      // canonical names land in the rendered UI without a manual reload.
      setHistory(loadHistory());
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-purge empty routines on launch. These got saved as artifacts of
  // an earlier REST-day import that skipped every exercise (algorithm now
  // returns a deload instead — see workoutImport.js). One-time cleanup
  // version-stamped so we don't repeat the scan on every mount.
  useEffect(() => {
    try {
      const V = 1;
      const stored = parseInt(localStorage.getItem("riyadahEmptyRoutinePurgeV") || "0", 10);
      if (stored >= V) return;
      const list = loadRoutines();
      const cleaned = list.filter(r => Array.isArray(r?.exercises_list) && r.exercises_list.length > 0);
      if (cleaned.length !== list.length) {
        // Use the existing save path so the routines store updates
        // identically to a user-driven edit.
        for (const r of list) {
          if (!Array.isArray(r?.exercises_list) || r.exercises_list.length === 0) {
            deleteRoutine(r.id);
          }
        }
      }
      localStorage.setItem("riyadahEmptyRoutinePurgeV", String(V));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Resume an in-progress workout if one was persisted (≤24h old). Carries
  // the template + every exercise/set + the original startTs so the elapsed
  // clock keeps ticking from the right baseline.
  const persistedActive = useMemo(() => loadActiveSession(), []);
  const [activeTemplate, setActiveTemplate] = useState(persistedActive ? persistedActive.template : null);
  const [resumeSession,  setResumeSession]  = useState(persistedActive || null);
  // Remember where the user entered Active from so back-arrow returns there
  // instead of always defaulting to Hub. 'pick' covers the routine picker,
  // 'hub' covers Resume CTAs / watch launches.
  const [activeOrigin, setActiveOrigin] = useState("hub");
  const [completedWorkout, setCompletedWorkout] = useState(null);
  const [history, setHistory] = useState(() => {
    const seeded = maybeSeedHistory();
    return seeded || loadHistory();
  });
  const [goals, setGoals] = useState(() => loadGoals());

  // When set, PickScreen auto-opens its AI Workout Generator modal on
  // mount. Hub's primary "Start Workout" CTA uses this so the user
  // lands directly inside the AI flow; manual template browsing is
  // still one tap away (just close the modal).
  const [pickAutoOpenAi, setPickAutoOpenAi] = useState(false);

  // Pending workout handed over from the AI Coach ("Open in Exercise").
  // Surfaced as a Start card on the Hub. Refreshes on the bridge event in case
  // this screen is already mounted when the Coach fires.
  const [coachWorkout, setCoachWorkout] = useState(() => loadCoachWorkout());
  useEffect(() => {
    const refresh = () => { const wk = loadCoachWorkout(); setCoachWorkout(wk); if (wk) setScreen("hub"); };
    window.addEventListener("sirat:coachWorkout", refresh);
    return () => window.removeEventListener("sirat:coachWorkout", refresh);
  }, []);

  const goStart   = () => { hapticHayahSelection(); setPickAutoOpenAi(true); setScreen("pick"); };
  const goStartManual = () => { hapticHayahSelection(); setPickAutoOpenAi(false); setScreen("pick"); };
  const goResume = () => {
    hapticTap();
    // Always pull the latest persisted state — the in-memory resumeSession
    // could be stale if the user already navigated through Active and made
    // changes since this Riyadah parent first mounted.
    const fresh = loadActiveSession();
    if (fresh) {
      setResumeSession(fresh);
      setActiveTemplate(fresh.template);
    }
    setActiveOrigin(screen === "pick" ? "pick" : "hub");
    setScreen("active");
  };
  const goPick    = (tmpl) => {
    hapticTap();
    // Starting a new template clears any resumed session.
    setResumeSession(null);
    clearActiveSession();
    setActiveTemplate(tmpl);
    setActiveOrigin("pick");
    setScreen("active");
  };
  // Start the workout the AI Coach built (surfaced as a Hub card). Runs through
  // the normal Active flow, then clears the pending hand-off.
  const startCoachWorkout = () => {
    if (!coachWorkout) return;
    const tmpl = coachWorkout;
    clearCoachWorkout();
    setCoachWorkout(null);
    goPick(tmpl);
  };
  const dismissCoachWorkout = () => {
    clearCoachWorkout();
    setCoachWorkout(null);
  };
  const goFinish  = (workout) => {
    hapticMilestone();
    // Canonicalise every exercise before persistence — stamps ex.id +
    // ex.name to the library's preferred forms, and auto-adds any
    // unknown exercises to the custom library so the NEXT session
    // sharing the same name (or alias) lands on the same canonical
    // entry. Without this, history splits across spellings ("Goblet
    // Squats" vs "DB Goblet Squat") and PR tracking gets fragmented.
    const canonExercises = canonicaliseWorkoutExercises(workout.exercises);
    const canonical = { ...workout, exercises: canonExercises };
    // Persist immediately — previously the save only happened on goDone
    // (after the user tapped Done on the FinishScreen). If the app was
    // closed / reaped between Finish and Done, the workout was lost.
    const updated = [canonical, ...history];
    setHistory(updated);
    saveHistory(updated);
    setCompletedWorkout(canonical);
    clearActiveSession();
    setResumeSession(null);
    setScreen("finish");
    generateAiName(canonical).catch(() => {});
  };
  const goDone    = () => {
    // Workout is already in history (saved on goFinish). Just clean up
    // the celebration screen state and head to the Hub.
    setCompletedWorkout(null); setActiveTemplate(null); setScreen("hub");
  };
  // Attach a session RPE (1–10) to the just-finished workout. The workout
  // is history[0] (goFinish prepends it). Foundation only — captured for
  // future progression/adaptation; nothing reads it yet.
  const setSessionRPE = (rpe) => {
    setHistory(prev => {
      if (!prev.length) return prev;
      const updated = [{ ...prev[0], sessionRPE: rpe }, ...prev.slice(1)];
      saveHistory(updated);
      return updated;
    });
    setCompletedWorkout(cw => (cw ? { ...cw, sessionRPE: rpe } : cw));
  };
  const goBack    = () => { hapticHayahSelection(); setScreen("hub"); };
  // Back-arrow on the Active screen: return to wherever the user entered
  // from (Pick or Hub). The in-progress workout stays persisted so the
  // user can resume via the Hub Resume CTA. Never used to discard work.
  const goBackFromActive = () => {
    hapticHayahSelection();
    setScreen(activeOrigin === "pick" ? "pick" : "hub");
  };
  // Explicit discard: drop the active workout without saving and return
  // to the origin. Triggered from the "Discard workout" button inside the
  // finish-confirm sheet on the Active screen.
  const goCancel = () => {
    hapticWarning();
    clearActiveSession();
    setResumeSession(null);
    setActiveTemplate(null);
    setScreen(activeOrigin === "pick" ? "pick" : "hub");
  };
  const goActivity = () => { hapticHayahSelection(); setScreen("activity"); };
  const goGoals    = () => { hapticHayahSelection(); setScreen("goals"); };

  const deleteSession = (idx) => {
    hapticConfirm();
    const updated = history.filter((_, i) => i !== idx);
    setHistory(updated); saveHistory(updated);
  };
  // Log a session that didn't go through the normal Active → Finish flow
  // (e.g. mobility / stretching, logged walk, rest day). Prepends to history
  // so the Activity screen + Today dashboard pick it up immediately.
  const logExternalSession = (workout) => {
    const updated = [workout, ...history];
    setHistory(updated);
    saveHistory(updated);
  };
  const upsertGoals = (next) => { setGoals(next); saveGoals(next); };

  // Pull HKWorkouts from the watch and back-fill `watch: null` sessions plus
  // import any watch-only workouts that don't yet exist in history. Safe to
  // call repeatedly — importAppleWatchWorkouts dedupes by ±15min start.
  const [watchSyncing, setWatchSyncing] = useState(false);
  const [watchSyncToast, setWatchSyncToast] = useState(null);
  const syncWatchHistory = async ({ silent = true } = {}) => {
    if (watchSyncing) return;
    setWatchSyncing(true);
    try {
      const res = await importAppleWatchWorkouts({ days: 7 });
      setHistory(loadHistory());
      if (!silent) {
        if (res.added + res.attached > 0) {
          setWatchSyncToast(`⌚ Synced · ${res.attached} attached · ${res.added} added`);
        } else {
          setWatchSyncToast("⌚ Already up to date");
        }
        setTimeout(() => setWatchSyncToast(null), 2400);
      }
    } catch {
      if (!silent) {
        setWatchSyncToast("⌚ Watch sync failed");
        setTimeout(() => setWatchSyncToast(null), 2400);
      }
    } finally {
      setWatchSyncing(false);
    }
  };
  // Auto-sync once on mount so existing sessions get their watch data
  // re-attached without the user thinking about it.
  useEffect(() => {
    isWatchAvailable().then(ok => { if (ok) syncWatchHistory({ silent: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateAiName = async (workout) => {
    if (!getGeminiKey()) return;
    try {
      const muscles = extractMuscleGroups(workout, 6);
      const exNames = (workout.exercises || []).filter(e => e.sets?.length).map(e => e.name).slice(0, 10);
      // v2: include volume + set count + PR signal so the name can reflect
      // intensity ("Heavy Push", "PR Pull Day", "Recovery Lower") not just
      // muscle group. Kept terse on flash-lite for cost.
      const volume = workout.volume || 0;
      const sets = workout.sets || 0;
      const prs = workout.prs || 0;
      const prTag = prs > 0 ? ` · ${prs} PR${prs > 1 ? "s" : ""}` : "";
      const userPrompt = `Name this gym workout in 3-5 words. Be specific to what was trained AND the intensity. Examples: "Heavy Push Day", "PR Pull + Core", "Recovery Lower". No greeting, no quotes.

Muscles: ${muscles.join(", ")}
Exercises: ${exNames.join(", ")}
Volume: ${Math.round(volume).toLocaleString()} kg·rep · ${sets} sets${prTag}`;
      const raw = await callGemini(userPrompt, {
        model: MODELS.workoutName,
        maxOutputTokens: 32,
        feature: "workoutName",
      });
      const name = raw.trim().replace(/^["']|["']$/g, "").slice(0, 40);
      if (!name) return;
      const current = loadHistory();
      const ts = workout.timestamp;
      const idx = current.findIndex(w => w.timestamp === ts);
      if (idx < 0) return;
      current[idx] = { ...current[idx], aiName: name, aiNameAt: Date.now() };
      saveHistory(current);
      setHistory(current);
    } catch {}
  };

  const onSubTab = (i) => {
    if (i === 0) setScreen("hub");
    // "+ Log" subtab opens the picker WITHOUT auto-launching the AI modal —
    // that route is for users explicitly browsing manual templates.
    else if (i === 1) goStartManual();
    else if (i === 2) goActivity();
    else if (i === 3) goGoals();
  };

  // ── Watch bridge (parent-level) ───────────────────────────────────────────
  // Push the routine catalogue to the watch so its empty-state picker has
  // something to show, and listen for "riyadahStart" so the watch can pick a
  // template + jump straight into the active screen.
  useEffect(() => {
    if (typeof SiratWatch?.sendRiyadahRoutines !== "function") return;
    const customs = (() => { try { return loadRoutines(); } catch { return []; } })();
    const built   = RIYADAH_TEMPLATES.filter(t => t && t.id && t.name && t.id !== "quick-start");
    const routines = [
      ...customs.map(r => ({ id: r.id, name: r.name, emoji: r.emoji || "💪", custom: true })),
      ...built  .map(t => ({ id: t.id, name: t.name, emoji: t.emoji || "💪", custom: false })),
    ];
    if (!watchRoutinePushOn()) return;
    try {
      const p = SiratWatch.sendRiyadahRoutines({ routines });
      if (p && typeof p.catch === "function") {
        p.catch(err => console.warn("[SiratWatch] sendRiyadahRoutines rejected:", err));
      }
    } catch (err) { console.warn("[SiratWatch] sendRiyadahRoutines threw:", err); }
  }, [history.length]); // re-push when history grows (cheap proxy for app foreground)

  // Watch → iPhone: tap a routine on the watch picker → start it here.
  useEffect(() => {
    if (typeof SiratWatch?.addListener !== "function") return;
    let handle = null;
    let cancelled = false;
    const customs = (() => { try { return loadRoutines(); } catch { return []; } })();
    const lookup = (id) =>
      RIYADAH_TEMPLATES.find(t => t.id === id) || customs.find(r => r.id === id);
    try {
      const p = SiratWatch.addListener("riyadahStart", ({ routineId }) => {
        if (cancelled || !routineId) return;
        const tmpl = lookup(routineId);
        if (tmpl) goPick(tmpl);
        else console.warn("[SiratWatch] riyadahStart unknown routine:", routineId);
      });
      if (p && typeof p.then === "function") {
        p.then(h => { handle = h; }).catch(err => console.warn("[SiratWatch] addListener(start) failed:", err));
      } else { handle = p; }
    } catch (err) { console.warn("[SiratWatch] addListener(start) threw:", err); }
    return () => { cancelled = true; try { handle?.remove?.(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
      background: theme.bg0, color: theme.ink1, overflowY: "hidden", overflowX: "hidden",
      fontFamily: "Inter, system-ui, sans-serif",
    }}>
      <style>{`
        .ry-sb::-webkit-scrollbar{display:none}
        .ry-sb{scrollbar-width:none;-ms-overflow-style:none}
        .ry-mono{font-family:'JetBrains Mono',ui-monospace,monospace}
        .ry-serif{font-family:'Fraunces',ui-serif,Georgia,serif}
        .ry-btn{font-family:inherit;cursor:pointer;-webkit-tap-highlight-color:transparent}
        .ry-btn:active{opacity:0.85;transform:scale(0.98)}
        @keyframes ry-slide-up{from{transform:translateY(16px);opacity:0}to{transform:none;opacity:1}}
        @keyframes ry-fade-in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
        @keyframes ry-pulse{0%,100%{box-shadow:0 0 6px ${theme.amber}}50%{box-shadow:0 0 14px ${theme.amber}}}
        @keyframes ry-celebrate{0%{transform:scale(0.92);opacity:0}60%{transform:scale(1.04);opacity:1}100%{transform:scale(1);opacity:1}}
        @keyframes ry-blink{0%,100%{opacity:1}50%{opacity:0.25}}
        .ry-slide{animation:ry-slide-up 320ms cubic-bezier(.22,.8,.36,1) both}
        .ry-fade{animation:ry-fade-in 220ms ease both}
        .ry-celebrate{animation:ry-celebrate 520ms cubic-bezier(.22,.8,.36,1) both}
        .ry-input{
          background:${theme.bg3};border:0.5px solid ${theme.line};border-radius:8px;
          color:${theme.ink1};font-family:'JetBrains Mono',ui-monospace,monospace;
          font-size:14px;font-weight:500;text-align:center;padding:7px 6px;width:100%;
          -webkit-appearance:none;appearance:none;outline:none;
          transition:border-color .15s,background .15s;
        }
        .ry-input:focus{border-color:${theme.amber};background:rgba(224,160,48,0.06)}
      `}</style>

      {screen === "hub"      && <HubScreen     theme={theme} history={history} onStart={goStart} onHistory={goActivity} onSubTab={onSubTab} resume={resumeSession} onResume={goResume} coachWorkout={coachWorkout} onStartCoachWorkout={startCoachWorkout} onDismissCoachWorkout={dismissCoachWorkout} />}
      {screen === "pick"     && <PickScreen    theme={theme} onBack={goBack} onPick={goPick} onSubTab={onSubTab} history={history} onDeleteSession={deleteSession} onLogSession={logExternalSession} autoOpenAi={pickAutoOpenAi} onAutoOpenConsumed={() => setPickAutoOpenAi(false)} />}
      {screen === "active"   && activeTemplate && <ActiveScreen theme={theme} template={activeTemplate} resume={resumeSession} onFinish={goFinish} onBack={goBackFromActive} onDiscard={goCancel} />}
      {screen === "finish"   && completedWorkout && <FinishScreen theme={theme} workout={completedWorkout} onDone={goDone} onSetRPE={setSessionRPE} />}
      {screen === "activity" && <ActivityScreen theme={theme} history={history} onBack={goBack} onStartNew={goStart} onSubTab={onSubTab} onDeleteSession={deleteSession} onSyncWatch={() => syncWatchHistory({ silent: false })} watchSyncing={watchSyncing} watchSyncToast={watchSyncToast} />}
      {screen === "goals"    && <GoalsScreen   theme={theme} history={history} goals={goals} setGoals={upsertGoals} onSubTab={onSubTab} />}
    </div>
  );
}

// ───────── BodyStatsRow ─────────
// Three compact tiles surfaced on the Today dashboard: weight + 7-day
// delta, today's protein vs goal, today's steps vs goal. Reads directly
// from the localStorage keys that the rest of Hayah already writes to
// (calorieWeightLog, calorieLog, calorieGoals) so there's no extra data
// plumbing. Read-only in v1.
function BodyStatsRow({ theme }) {
  const stats = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const todayKey = `${y}-${m}-${d}`;
    const parse = (k, fb) => { try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(fb)); } catch { return fb; } };
    const weightLog = parse("calorieWeightLog", []);
    const calorieLog = parse("calorieLog", {});
    const goals = parse("calorieGoals", {});
    const proteinGoal = +goals.protein || 150;
    const stepsGoal   = +goals.steps   || 10000;

    // Weight + 7d delta — compare today's most recent reading against the
    // 7-day rolling average ending ~7 days ago. Falls back to "no delta"
    // if either side has fewer than 1 reading.
    const sortedW = (Array.isArray(weightLog) ? weightLog : [])
      .filter(w => w && Number.isFinite(+w.kg))
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const latest = sortedW[sortedW.length - 1] || null;
    let delta = null;
    if (latest) {
      const latestTs = Date.parse(`${latest.date}T12:00:00`);
      const sevenAgo = latestTs - 7 * 86400000;
      const window = sortedW
        .map(w => ({ ts: Date.parse(`${w.date}T12:00:00`), kg: +w.kg }))
        .filter(w => Number.isFinite(w.ts));
      // mean of the 3 readings closest to "7 days ago"
      const earlier = window
        .filter(w => w.ts <= sevenAgo)
        .slice(-3);
      if (earlier.length > 0) {
        const mean = earlier.reduce((s, w) => s + w.kg, 0) / earlier.length;
        delta = +(latest.kg - mean).toFixed(1);
      }
    }

    // Protein today + steps today.
    const todayEntry = calorieLog[todayKey] || {};
    const meals = todayEntry.meals || {};
    const proteinToday = Math.round(
      Object.values(meals).flat().reduce((s, e) => s + (+e?.protein || 0), 0)
    );
    const hasMeals = Object.values(meals).some(arr => Array.isArray(arr) && arr.length > 0);
    const stepsToday = +todayEntry.steps || 0;

    return { latest, delta, proteinToday, proteinGoal, hasMeals, stepsToday, stepsGoal };
  }, []);

  const tile = (children) => ({
    flex: 1, minWidth: 0,
    background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 14,
    padding: "10px 12px",
    display: "flex", flexDirection: "column", gap: 4,
    ...children,
  });

  const labelStyle = { fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color: theme.ink4, fontFamily: "monospace", textTransform: "uppercase" };
  const valueStyle = { fontSize: 18, fontWeight: 800, color: theme.ink1, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 };
  const subStyle   = { fontSize: 10, color: theme.ink3, fontVariantNumeric: "tabular-nums" };
  const emptySub   = { fontSize: 10, color: theme.ink4, fontStyle: "italic" };

  const proteinPct = stats.hasMeals
    ? Math.min(100, Math.round((stats.proteinToday / stats.proteinGoal) * 100))
    : 0;
  const stepsPct = stats.stepsToday > 0
    ? Math.min(100, Math.round((stats.stepsToday / stats.stepsGoal) * 100))
    : 0;

  const deltaColor = stats.delta == null ? theme.ink4 : stats.delta < 0 ? theme.teal : stats.delta > 0 ? theme.coral : theme.ink3;
  const deltaArrow = stats.delta == null ? "" : stats.delta < 0 ? "▼" : stats.delta > 0 ? "▲" : "·";

  return (
    <div style={{ display: "flex", gap: 8 }}>
      {/* Weight */}
      <div style={tile()}>
        <div style={labelStyle}>Weight</div>
        {stats.latest ? (
          <>
            <div style={valueStyle}>{(+stats.latest.kg).toFixed(1)}<span style={{ fontSize: 11, color: theme.ink3, fontWeight: 600, marginLeft: 2 }}>kg</span></div>
            {stats.delta != null ? (
              <div style={{ ...subStyle, color: deltaColor, fontWeight: 700 }}>{deltaArrow} {Math.abs(stats.delta)} kg · 7d</div>
            ) : (
              <div style={emptySub}>need 7d of data</div>
            )}
          </>
        ) : (
          <>
            <div style={valueStyle}>—</div>
            <div style={emptySub}>log to track</div>
          </>
        )}
      </div>

      {/* Protein */}
      <div style={tile()}>
        <div style={labelStyle}>Protein</div>
        {stats.hasMeals ? (
          <>
            <div style={valueStyle}>{stats.proteinToday}<span style={{ fontSize: 11, color: theme.ink3, fontWeight: 600 }}>/{stats.proteinGoal}g</span></div>
            <div style={{ height: 4, background: theme.bg3, borderRadius: 99, overflow: "hidden" }}>
              <div style={{ width: `${proteinPct}%`, height: "100%", background: proteinPct >= 80 ? theme.teal : theme.amber, transition: "width 0.4s ease" }}/>
            </div>
          </>
        ) : (
          <>
            <div style={valueStyle}>—</div>
            <div style={emptySub}>log to track</div>
          </>
        )}
      </div>

      {/* Steps */}
      <div style={tile()}>
        <div style={labelStyle}>Steps</div>
        {stats.stepsToday > 0 ? (
          <>
            <div style={valueStyle}>{stats.stepsToday.toLocaleString()}</div>
            <div style={subStyle}>{stepsPct}% of {stats.stepsGoal.toLocaleString()}</div>
          </>
        ) : (
          <>
            <div style={valueStyle}>—</div>
            <div style={emptySub}>sync to track</div>
          </>
        )}
      </div>
    </div>
  );
}

// ───────── HubScreen ─────────
function HubScreen({ theme, history, onStart, onHistory, onSubTab, resume, onResume, coachWorkout, onStartCoachWorkout, onDismissCoachWorkout }) {
  const last = history[0];
  // Sessions this week (Sun-Sat).
  const weeklyDoneByDay = useMemo(() => {
    const arr = [false, false, false, false, false, false, false];
    const now = new Date();
    const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - now.getDay()); startOfWeek.setHours(0, 0, 0, 0);
    for (const w of history) {
      if (!w.timestamp) continue;
      const d = new Date(w.timestamp);
      if (d >= startOfWeek && d <= now) arr[d.getDay()] = true;
    }
    return arr;
  }, [history]);
  const sessionsThisWeek = weeklyDoneByDay.filter(Boolean).length;
  const todayDow = new Date().getDay();

  // Pull-to-refresh — touch handler on the scroll container that fires
  // the Readiness card's refreshSignal when the user pulls down past
  // threshold while at scrollTop=0. iOS WKWebView gives us bouncy
  // overscroll for free; this layer adds the explicit "release to
  // refresh" affordance + the actual trigger.
  const scrollRef = useRef(null);
  const startYRef = useRef(null);
  const [pullY, setPullY] = useState(0);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const PULL_THRESHOLD = 70;
  const onTouchStart = (e) => {
    const el = scrollRef.current;
    if (!el || el.scrollTop > 0) { startYRef.current = null; return; }
    startYRef.current = e.touches[0].clientY;
  };
  const onTouchMove = (e) => {
    if (startYRef.current == null) return;
    const dy = e.touches[0].clientY - startYRef.current;
    if (dy <= 0) { setPullY(0); return; }
    // Resistance so it doesn't track 1:1 — feels like a real pull.
    setPullY(Math.min(dy * 0.5, 110));
  };
  const onTouchEnd = () => {
    if (pullY >= PULL_THRESHOLD) {
      setRefreshing(true);
      try { hapticTap(); } catch {}
      setRefreshSignal(s => s + 1);
      // Reset the in-flight indicator after a short window — the
      // ReadinessCard owns the actual job state, this is just visual.
      setTimeout(() => setRefreshing(false), 1500);
    }
    setPullY(0);
    startYRef.current = null;
  };

  return (
    <div className="ry-slide" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {/* Sub tabs */}
      <SubTabs theme={theme} active={0} onChange={onSubTab} />

      <div
        ref={scrollRef}
        className="ry-sb"
        style={{ flex: 1, overflowY: "auto", paddingBottom: "calc(96px + env(safe-area-inset-bottom))" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Pull-to-refresh indicator — only visible while the user is
            actively pulling or a refresh is in flight. */}
        {(pullY > 0 || refreshing) && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            height: refreshing ? 48 : Math.min(pullY, 110),
            color: theme.teal, fontFamily: "monospace", fontSize: 10,
            letterSpacing: "0.16em", textTransform: "uppercase",
            transition: refreshing ? "height 0.25s ease" : "none",
          }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              opacity: refreshing ? 1 : Math.min(1, pullY / PULL_THRESHOLD),
            }}>
              <span style={{
                display: "inline-block",
                transform: refreshing ? "rotate(0deg)" : `rotate(${(pullY / PULL_THRESHOLD) * 180}deg)`,
                transition: refreshing ? "none" : "transform 0.06s linear",
                animation: refreshing ? "ry-spin 1s linear infinite" : "none",
              }}>↻</span>
              <span>{refreshing ? "Refreshing…" : pullY >= PULL_THRESHOLD ? "Release to refresh" : "Pull to refresh"}</span>
            </span>
            <style>{`@keyframes ry-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
          </div>
        )}
        {/* Coach-built workout — handed over from AI Coach's "Open in
            Exercise". Tap Start to run it through the normal Active flow. */}
        {coachWorkout && Array.isArray(coachWorkout.exercises_list) && coachWorkout.exercises_list.length > 0 && (
          <div style={{ padding: "12px 16px 0" }}>
            <div style={{ borderRadius: 14, border: `1px solid ${theme.teal}55`, background: `${theme.teal}10`, overflow: "hidden" }}>
              <div style={{ padding: "12px 14px 8px", display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ fontSize: 20, lineHeight: 1 }}>🤖</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: theme.teal }}>From your Coach</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: theme.ink1, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{coachWorkout.name}</div>
                  <div style={{ fontSize: 11, color: theme.ink4, marginTop: 1 }}>{coachWorkout.exercises_list.length} exercises · {coachWorkout.exercises_list.map(e => e.name).slice(0, 3).join(", ")}{coachWorkout.exercises_list.length > 3 ? "…" : ""}</div>
                </div>
                <button className="ry-btn" onClick={onDismissCoachWorkout} aria-label="Dismiss"
                  style={{ width: 26, height: 26, borderRadius: 8, background: theme.bg3, border: `0.5px solid ${theme.line}`, color: theme.ink4, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="x" size={12} />
                </button>
              </div>
              <button className="ry-btn" onClick={onStartCoachWorkout}
                style={{ width: "100%", padding: "13px", border: "none", background: theme.teal, color: "#04201b", fontSize: 14, fontWeight: 900, cursor: "pointer", letterSpacing: "0.02em" }}>
                ▶ Start workout
              </button>
            </div>
          </div>
        )}
        {/* Resume in-progress workout — shows whenever an active session is
            still on disk (max 24h freshness). Clicking it returns to the
            Active screen with all set state and the original startTs. */}
        {resume && resume.template && (
          <div style={{ padding: "12px 16px 0" }}>
            <button className="ry-btn" onClick={onResume}
              style={{
                width: "100%",
                padding: "14px 16px",
                background: `linear-gradient(135deg, ${theme.amber}, ${theme.amber}cc)`,
                border: 0, borderRadius: 14,
                color: "#0A0A0E",
                display: "flex", alignItems: "center", gap: 12,
                cursor: "pointer", textAlign: "left",
                boxShadow: `0 6px 22px ${theme.amber}44`,
              }}>
              <div style={{ fontSize: 24, lineHeight: 1 }}>▶︎</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.16em", opacity: 0.7 }}>WORKOUT IN PROGRESS</div>
                <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{resume.template.name || "Resume"}</div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em" }}>RESUME →</span>
            </button>
          </div>
        )}
        {/* Readiness card — weekly streak + day-dot strip moved INTO
            the card body via topAccessory; outer header line dropped so
            the ↻ button and "READINESS" label no longer take a row.
            Pull-to-refresh on the scroll container drives the refresh
            via refreshSignal. */}
        <div style={{ padding: "16px 14px 0" }}>
          <ReadinessCard
            showHeader={false}
            refreshSignal={refreshSignal}
            topAccessory={
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <span style={{ fontSize: 12, color: theme.amber }}>🔥</span>
                  <span className="ry-mono" style={{ fontSize: 12, fontWeight: 700, color: theme.amber }}>{sessionsThisWeek}</span>
                </span>
                <span style={{ display: "flex", gap: 4, alignItems: "flex-end" }}>
                  {["S","M","T","W","T","F","S"].map((label, i) => {
                    const done = weeklyDoneByDay[i];
                    const isToday = i === todayDow;
                    return (
                      <span
                        key={i}
                        aria-label={`${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][i]}${done ? ' done' : ''}${isToday ? ' (today)' : ''}`}
                        style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
                      >
                        <span style={{
                          fontFamily: "monospace",
                          fontSize: 8, lineHeight: 1,
                          color: isToday ? theme.amber : theme.ink4,
                          fontWeight: isToday ? 700 : 600,
                          letterSpacing: "0.04em",
                        }}>{label}</span>
                        <span style={{
                          width: 11, height: 11, borderRadius: "50%",
                          background: done ? theme.teal : "transparent",
                          border: isToday ? `1.5px solid ${theme.amber}` : `1px solid ${theme.line}`,
                          display: "inline-block",
                        }}/>
                      </span>
                    );
                  })}
                </span>
              </div>
            }
          />
        </div>

        {/* Body stats — weight trend / today's protein / today's steps.
            Read-only in v1; routing back into Hayah surfaces will follow
            once HayahHub exposes a nav listener. */}
        <div style={{ padding: "10px 14px 0" }}>
          <BodyStatsRow theme={theme} />
        </div>

        {/* Start CTA */}
        <div style={{ padding: "10px 14px 0" }}>
          <button className="ry-btn" onClick={onStart} style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            background: theme.amber, color: "#0A0A0E", border: 0, borderRadius: 14,
            fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em",
            padding: "17px 20px", width: "100%",
          }}>
            <Icon name="plus" size={18} color="#0A0A0E"/>
            Start Workout
          </button>
        </div>

        {history.length === 0 && (
          <div style={{ padding: "32px 16px 8px", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>💪</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.ink1, marginBottom: 6 }}>First session awaits</div>
            <div style={{ fontSize: 13, color: theme.ink3, lineHeight: 1.5 }}>Tap Start Workout to log your first gym session with your PT</div>
          </div>
        )}
      </div>

    </div>
  );
}

function SubTabs({ theme, active = 0, onChange }) {
  const items = ["Readiness", "Workout", "Activity", "Goals"];
  return (
    <div className="ry-sb" style={{
      display: "flex", gap: 0, padding: 0,
      borderBottom: `0.5px solid ${theme.line}`, background: theme.bg0, flexShrink: 0,
    }}>
      {items.map((t, i) => (
        <button key={t} className="ry-btn" onClick={() => onChange?.(i)}
          style={{
            flex: 1, minWidth: 0,
            padding: "11px 6px 10px", border: 0, background: "transparent",
            fontSize: 12, fontWeight: 500, letterSpacing: "-0.01em",
            color: i === active ? theme.amber : theme.ink3,
            whiteSpace: "nowrap", textAlign: "center",
            borderBottom: `1.5px solid ${i === active ? theme.amber : "transparent"}`,
            marginBottom: -0.5,
          }}>
          {t}
        </button>
      ))}
    </div>
  );
}

function SnackRow({ theme, icon, lead, body, action, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} className={onClick ? "ry-btn" : undefined} style={{
      margin: "0 16px 10px", padding: "12px 14px",
      background: `linear-gradient(135deg, rgba(124,196,160,0.08), rgba(124,196,160,0.04))`,
      border: `0.5px solid rgba(124,196,160,0.20)`, borderRadius: 12,
      display: "flex", alignItems: "center", gap: 10,
      width: onClick ? "calc(100% - 32px)" : undefined,
      color: "inherit", textAlign: "left",
    }}>
      <div style={{ width: 32, height: 32, borderRadius: 10, background: theme.sageDim, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: theme.sage, letterSpacing: "-0.01em" }}>{lead}</div>
        <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1 }}>{body}</div>
      </div>
      {action && (
        <div style={{ marginLeft: "auto", fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.ink4 }}>
          {action}
        </div>
      )}
    </Tag>
  );
}

function HistCard({ theme, workout }) {
  return (
    <div className="ry-fade" style={{ margin: "0 16px 10px", background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 16, overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 12px", borderBottom: `0.5px solid ${theme.line}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.ink1, letterSpacing: "-0.01em" }}>{workout.name}</div>
          <div style={{ fontSize: 11, color: theme.ink3, marginTop: 2 }}>{workout.date} · {workout.time}</div>
        </div>
        {workout.prs > 0 && <PrPill theme={theme}>🏆 {workout.prs} PR</PrPill>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", padding: "12px 16px", gap: 8 }}>
        <Stat theme={theme} value={fmtDurHuman(workout.duration)} label="Duration" />
        <Stat theme={theme} value={workout.sets} label="Sets" valueColor={theme.teal} />
        <Stat theme={theme} value={workout.volume > 0 ? (workout.volume >= 1000 ? `${(workout.volume / 1000).toFixed(1)}t` : `${workout.volume}kg`) : "—"} label="Volume" />
      </div>
    </div>
  );
}

// Apple Watch instructional + status card. Shows differently based on whether
// HealthKit is available (native iOS) vs PWA. On Active screen `live` shows a
// pulsing dot to indicate the recording is in flight.
function WatchCard({ theme, available, live = false, compact = false }) {
  const teal = theme.teal;
  return (
    <div style={{
      margin: compact ? "0 16px 10px" : "0 16px 12px",
      padding: compact ? "11px 14px" : "13px 14px",
      background: available
        ? `linear-gradient(135deg, rgba(79,217,194,0.10), rgba(79,217,194,0.04))`
        : theme.bg2,
      border: `0.5px solid ${available ? "rgba(79,217,194,0.25)" : theme.line}`,
      borderRadius: 12,
      display: "flex", alignItems: "center", gap: 12,
    }}>
      <div style={{ fontSize: compact ? 20 : 22, flexShrink: 0 }}>⌚</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: compact ? 12 : 13, fontWeight: 600,
          color: available ? teal : theme.ink2, letterSpacing: "-0.01em" }}>
          {live ? "Recording on Apple Watch" : "Start HIIT on Apple Watch"}
        </div>
        <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1, lineHeight: 1.35 }}>
          {available
            ? (live
                ? "Heart rate + calories will attach when you finish"
                : "Open Workout app · tap HIIT · then start here")
            : "Available in the iOS app"}
        </div>
      </div>
      {available && (
        <div style={{ width: 8, height: 8, borderRadius: "50%",
          background: teal,
          boxShadow: live ? `0 0 8px ${teal}` : "none",
          animation: live ? "ry-pulse 1.6s ease-in-out infinite" : "none",
          flexShrink: 0 }}/>
      )}
    </div>
  );
}

// Inline summary of a workout's merged Apple Watch data, used in FinishScreen
// and the History detail view.
function WatchSummary({ theme, watch, compact = false }) {
  if (!watch) return null;
  return (
    <div style={{
      margin: compact ? "8px 0" : "10px 16px",
      padding: compact ? "10px 12px" : "12px 14px",
      background: `linear-gradient(135deg, rgba(79,217,194,0.10), rgba(79,217,194,0.04))`,
      border: `0.5px solid rgba(79,217,194,0.25)`, borderRadius: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>⌚</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: theme.teal, letterSpacing: "-0.01em" }}>
            Apple Watch · {watch.activityType || "Workout"}
          </span>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <div style={{ textAlign: "center" }}>
          <div className="ry-mono" style={{ fontSize: 14, color: theme.ink1 }}>{watch.calories || 0}</div>
          <div style={{ fontSize: 9, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 1 }}>kcal</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div className="ry-mono" style={{ fontSize: 14, color: theme.ink1 }}>{watch.avgHr || 0}</div>
          <div style={{ fontSize: 9, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 1 }}>Avg HR</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div className="ry-mono" style={{ fontSize: 14, color: theme.coral }}>{watch.maxHr || 0}</div>
          <div style={{ fontSize: 9, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 1 }}>Max HR</div>
        </div>
      </div>
    </div>
  );
}

function Stat({ theme, value, label, valueColor }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div className="ry-mono" style={{ fontSize: 16, fontWeight: 500, color: valueColor || theme.ink1 }}>{value}</div>
      <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.ink4 }}>{label}</div>
    </div>
  );
}

function PrPill({ theme, children }) {
  return (
    <span className="ry-mono" style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: theme.amberDim, color: theme.amber,
      fontSize: 9, fontWeight: 600, letterSpacing: "0.18em",
      padding: "2px 7px", borderRadius: 4, border: `0.5px solid rgba(224,160,48,0.25)`,
    }}>{children}</span>
  );
}

// ───────── PickScreen ─────────
function PickScreen({ theme, onBack, onPick, onSubTab, history = [], onDeleteSession, onLogSession, autoOpenAi = false, onAutoOpenConsumed }) {
  const [fuelOpen, setFuelOpen] = useState(false);
  const [aiGenOpen, setAiGenOpen] = useState(false);
  const [aiGenGoal, setAiGenGoal] = useState("Full Body");
  const [aiGenDuration, setAiGenDuration] = useState(45);
  const [aiGenEquip, setAiGenEquip] = useState("Full Gym");
  // Exercise-picker overlay (like/hide candidates for the chosen goal) +
  // a tick so it and the modal re-read picks after each toggle.
  const [exercisePickerOpen, setExercisePickerOpen] = useState(false);
  const [picksTick, setPicksTick] = useState(0);
  // Tracks which inputs the user has manually overridden so we don't keep
  // recomputing smart defaults on top of their choices. Reset each open.
  const [userTouched, setUserTouched] = useState({ goal: false, duration: false, equip: false });
  // Expanded glossary term inside the readiness rationale; null = none.
  const [expandedRationaleTerm, setExpandedRationaleTerm] = useState(null);
  const [aiGenLoading, setAiGenLoading] = useState(false);
  const [aiGenError, setAiGenError] = useState(null);
  const [aiGenRawDebug, setAiGenRawDebug] = useState(null);
  const [aiGenResult, setAiGenResult] = useState(null);
  const [treadmillOpen, setTreadmillOpen] = useState(false);
  const [treadmillLoading, setTreadmillLoading] = useState(false);
  const [treadmillResult, setTreadmillResult] = useState(null);
  const [treadmillError, setTreadmillError] = useState(null);
  // Mobility / stretching flow — offered on REST days from the recovery
  // options. Gemini generates a guided routine with step-by-step cues; the
  // user can log it as a session into sirat-workouts.
  //
  // The generation runs as an aiBackgroundJob keyed by MOBILITY_CACHE_KEY,
  // so closing the modal mid-generation does NOT cancel it — the request
  // keeps running, the result is persisted to localStorage, and a haptic
  // fires on completion. Reopening the modal re-subscribes and shows the
  // cached state immediately.
  const [mobilityOpen, setMobilityOpen] = useState(false);
  const [mobilityResult, setMobilityResult] = useState(() => {
    try { return JSON.parse(localStorage.getItem(MOBILITY_CACHE_KEY) || "null"); }
    catch { return null; }
  });
  const [mobilityLoading, setMobilityLoading] = useState(() => isAiJobInFlight(MOBILITY_CACHE_KEY));
  const [mobilityError, setMobilityError] = useState(() => lastAiJobError(MOBILITY_CACHE_KEY));
  const [mobilityLogged, setMobilityLogged] = useState(false);
  // Set<index> of stretches the user has marked complete. Mirrors the
  // "tap the circle to mark a set done" pattern from the normal workout
  // flow. Lazy-initialised from localStorage, scoped to the current
  // plan's ts so a regenerate resets progress automatically.
  const [mobilityDone, setMobilityDone] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(MOBILITY_DONE_KEY) || "null");
      const plan = JSON.parse(localStorage.getItem(MOBILITY_CACHE_KEY) || "null");
      if (cached && plan && cached.ts === plan.ts && Array.isArray(cached.done)) {
        return new Set(cached.done);
      }
    } catch {}
    return new Set();
  });
  const persistMobilityDone = (next, planTs) => {
    try {
      localStorage.setItem(MOBILITY_DONE_KEY, JSON.stringify({ ts: planTs, done: Array.from(next) }));
    } catch {}
  };
  const toggleStretchDone = (idx) => {
    if (!mobilityResult) return;
    const next = new Set(mobilityDone);
    const wasDone = next.has(idx);
    if (wasDone) next.delete(idx); else next.add(idx);
    setMobilityDone(next);
    persistMobilityDone(next, mobilityResult.ts);
    try { hapticHayahSelection(); } catch {}
    // Milestone haptic when the user completes the last one.
    const total = (mobilityResult.stretches || []).length;
    if (!wasDone && next.size === total && total > 0) {
      try { hapticMilestone(); } catch {}
    }
  };

  // Subscribe to module-level job state for the mobility key so a generate
  // that finished while the modal was closed still surfaces on reopen.
  useEffect(() => {
    const sync = () => {
      setMobilityLoading(isAiJobInFlight(MOBILITY_CACHE_KEY));
      setMobilityError(lastAiJobError(MOBILITY_CACHE_KEY));
      if (!isAiJobInFlight(MOBILITY_CACHE_KEY)) {
        try {
          const fresh = JSON.parse(localStorage.getItem(MOBILITY_CACHE_KEY) || "null");
          setMobilityResult(fresh);
          // If the plan changed (regenerate), drop stale per-stretch done state.
          try {
            const cached = JSON.parse(localStorage.getItem(MOBILITY_DONE_KEY) || "null");
            if (fresh && cached && cached.ts === fresh.ts && Array.isArray(cached.done)) {
              setMobilityDone(new Set(cached.done));
            } else {
              setMobilityDone(new Set());
            }
          } catch { setMobilityDone(new Set()); }
        } catch {}
      }
    };
    sync();
    return subscribeAiJob(MOBILITY_CACHE_KEY, sync);
  }, []);
  const [aiGenReadiness, setAiGenReadiness] = useState(null);
  const [aiGenReadinessLoading, setAiGenReadinessLoading] = useState(false);
  // Apply the user's subjective feel (Off → MEDIUM, Drained → REST) as a
  // downward-only cap so the generator's banner, suggestions and the Gemini
  // prompt all honour "I feel drained" instead of the raw objective band.
  const applyFeelCap = (raw) => {
    if (!raw) return raw;
    try {
      const feel = loadFeel();
      const fc = capByFeel(raw.classification, feel);
      if (!fc.capped) return raw;
      return { ...raw, classification: fc.band, objectiveClassification: raw.classification, feelNote: fc.note, feel };
    } catch { return raw; }
  };
  const [watchAvail, setWatchAvail] = useState(false);
  // ── Paste-from-text import ────────────────────────────────────
  // Steve sends a workout in WhatsApp; user pastes here; Gemini
  // parses it; the local progressive-overload algorithm fills in
  // weights × reps from history + readiness. Removes the need to
  // ask Claude / rebuild / TestFlight upload to log a new workout.
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importResult, setImportResult] = useState(null); // { routine, suggestions, finisher }
  const [routines, setRoutines] = useState(() => loadRoutines());
  const [editing, setEditing] = useState(null); // routine | null
  const [hiddenIds, setHiddenIds] = useState(() => loadHiddenTemplateIds());
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  useEffect(() => { isWatchAvailable().then(setWatchAvail); }, []);

  // Auto-open the AI Workout Generator modal if the parent asked us to
  // (Hub's primary "Start Workout" CTA). The flag is single-shot: we
  // notify the parent so re-renders don't reopen the modal after the
  // user closes it.
  useEffect(() => {
    if (!autoOpenAi) return;
    setAiGenOpen(true);
    setAiGenError(null);
    onAutoOpenConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenAi]);

  // When the AI Workout modal opens, fetch/generate readiness immediately so
  // the user sees their training classification before picking options.
  useEffect(() => {
    if (!aiGenOpen) return;
    setAiGenReadiness(applyFeelCap(loadReadinessCache()));
    setAiGenReadinessLoading(false);
    if (loadReadinessCache()) return; // already have today's result
    setAiGenReadinessLoading(true);
    (async () => {
      try {
        const ctx = await generateReadiness(readAllData(), { steveToday: false });
        setAiGenReadiness(applyFeelCap(ctx));
      } catch { /* silent — generate button will retry */ }
      finally { setAiGenReadinessLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiGenOpen]);

  // When the modal opens, reset "userTouched" so smart defaults apply
  // afresh for this session. (Pre-fill happens in the effect below once
  // readiness lands.)
  useEffect(() => {
    if (aiGenOpen) {
      setUserTouched({ goal: false, duration: false, equip: false });
      setExpandedRationaleTerm(null);
    }
  }, [aiGenOpen]);

  // Smart defaults: as soon as readiness is known (cache or freshly
  // generated), pre-pick the chip the system would recommend. Only
  // applies to inputs the user hasn't touched in this session.
  useEffect(() => {
    if (!aiGenOpen || aiGenReadinessLoading) return;
    const cls = aiGenReadiness?.classification;
    const history = loadHistory();
    const nowHour = new Date().getHours();
    if (!userTouched.goal) {
      const g = suggestGoal(cls, history);
      if (g) setAiGenGoal(g);
    }
    if (!userTouched.duration) {
      const d = suggestDuration(cls, nowHour);
      if (d) setAiGenDuration(d);
    }
    if (!userTouched.equip) {
      const { equipment } = suggestEquipment(history);
      if (equipment) setAiGenEquip(equipment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiGenOpen, aiGenReadinessLoading, aiGenReadiness?.classification]);

  // Memoised smart-data the UI renders against. Recomputes when modal
  // opens or readiness changes — both cheap lookups against localStorage.
  const smartCtx = useMemo(() => {
    if (!aiGenOpen) return null;
    const history = loadHistory();
    const stalenessMap = goalStalenessMap(history);
    const { equipment: suggestedEquip, last30 } = suggestEquipment(history);
    const cls = aiGenReadiness?.classification;
    const suggestedGoal = suggestGoal(cls, history);
    const suggestedDuration = suggestDuration(cls, new Date().getHours());
    let readinessInputs = null;
    try { readinessInputs = computeReadinessInputs(readAllData()); } catch {}
    const fuelReminder = shouldShowFuelReminder({
      classification: cls,
      ...(readinessInputs || {}),
    });
    return { stalenessMap, suggestedEquip, last30, suggestedGoal, suggestedDuration, readinessInputs, fuelReminder };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiGenOpen, aiGenReadiness?.classification]);

  const refreshRoutines = () => setRoutines(loadRoutines());
  const visibleBuiltIns = RIYADAH_TEMPLATES.filter(t => !hiddenIds.includes(t.id));
  const hideBuiltIn = (id) => { setHiddenIds(hideTemplate(id) || []); setConfirmDeleteId(null); };
  const restoreHidden = () => {
    hiddenIds.forEach(id => unhideTemplate(id));
    setHiddenIds([]);
  };
  const deleteCustom = (id) => { deleteRoutine(id); refreshRoutines(); setConfirmDeleteId(null); };

  const handleTreadmillGenerate = async () => {
    setTreadmillLoading(true);
    setTreadmillError(null);
    try {
      const readiness = loadReadinessCache();
      const mounjaroDay = getMounjaroDay((() => {
        try { return JSON.parse(localStorage.getItem("calorieLog") || "{}"); } catch { return {}; }
      })());
      const TREADMILL_SCHEMA = {
        type: "object",
        properties: {
          duration_min: { type: "integer" },
          blocks: {
            type: "array",
            items: {
              type: "object",
              properties: {
                type: { type: "string", enum: ["warmup", "steady", "interval_work", "interval_rest", "cooldown"] },
                minutes: { type: "number" },
                speed_kmh: { type: "number" },
                incline_pct: { type: "number" },
                notes: { type: "string" },
              },
              required: ["type", "minutes"],
            },
          },
          rationale: { type: "string" },
        },
        required: ["duration_min", "blocks", "rationale"],
      };
      const readinessNote = readiness
        ? (readiness.classification === "EASY" || readiness.classification === "REST"
            ? `Readiness: ${readiness.classification} — keep intensity lower, Zone 2 heart-rate only. No high-intensity intervals.`
            : `Readiness: ${readiness.classification} — full intensity session is appropriate.`)
        : "";
      const mounjaroNote = mounjaroDay ? `Mounjaro day ${mounjaroDay.dayOfCycle}/7 — appetite suppressed; keep session moderate.` : "";
      const sys = composeSystem([
        "You are a running coach designing a structured 30-minute treadmill session. Prescribe specific speeds in km/h and incline as percent grade (0 = flat). Keep sessions achievable — this user does PT-style gym training; running is supplementary cardio.",
        formatProfileForPrompt(getUserProfile()),
        OUTPUT_JSON_STRICT,
      ]);
      const prompt = `Design a 30-minute structured treadmill workout. Blocks must sum to exactly 30 minutes.
${readinessNote}
${mounjaroNote}
Include warmup, main section (intervals or steady-state), and cooldown. Interval work/rest pairs should have matching durations (e.g. 1min work / 1min rest).`;
      const raw = await callGemini(prompt, {
        systemInstruction: sys,
        responseSchema: TREADMILL_SCHEMA,
        model: MODELS.homeTreadmill,
        maxOutputTokens: 512,
        temperature: 0.4,
        feature: "homeTreadmill",
      });
      let parsed;
      try { parsed = JSON.parse(raw); } catch { throw new Error("Could not parse treadmill plan"); }
      setTreadmillResult(parsed);
      try { hapticHayahSuccess(); } catch {}
    } catch (e) {
      setTreadmillError(e.message || "Failed to generate treadmill plan");
      try { hapticError(); } catch {}
    } finally {
      setTreadmillLoading(false);
    }
  };

  const handleAiGenerate = async () => {
    setAiGenLoading(true);
    setAiGenError(null);
    setAiGenRawDebug(null);
    try {
      const _ri = aiGenReadiness || loadReadinessCache();
      publishSources("workout-gen", workoutGenSources({
        readiness: _ri?.classification || null,
        goal: aiGenGoal, duration: aiGenDuration, equip: aiGenEquip,
        acwr: smartCtx?.readinessInputs?.training?.acwr,
        lastWorkout: smartCtx?.readinessInputs?.training?.lastWorkout,
        muscleRecency: smartCtx?.readinessInputs?.training?.muscleRecency,
      }));
    } catch {}
    let _rawResponse = "";
    try {
      // Use the readiness already fetched when the modal opened (aiGenReadiness).
      // If still missing (rare — no cache + modal opened before fetch finished), proceed without.
      const readinessCtx = aiGenReadiness || applyFeelCap(loadReadinessCache());
      const mounjaroDay = getMounjaroDay((() => {
        try { return JSON.parse(localStorage.getItem("calorieLog") || "{}"); } catch { return {}; }
      })());
      const readinessCls = readinessCtx?.classification || null;
      // The classification is already feel-capped; tell the model WHY so it
      // scales intensity down and explains it rather than ignoring how the user feels.
      const feelLine = readinessCtx?.feel && readinessCtx?.objectiveClassification && readinessCtx.objectiveClassification !== readinessCls
        ? ` The user reports feeling ${readinessCtx.feel} today, so readiness was lowered from ${readinessCtx.objectiveClassification} to ${readinessCls} — respect that, scale volume/intensity down accordingly, and do not prescribe PRs.`
        : "";
      const readinessLine = readinessCls
        ? `Today's readiness: ${readinessCls}.${feelLine} ${readinessCtx.rationale || ""}${mounjaroDay ? ` Mounjaro day ${mounjaroDay.dayOfCycle}/7.` : ""}`
        : (mounjaroDay ? `Mounjaro day ${mounjaroDay.dayOfCycle}/7.` : "");

      // HR autoregulation signals fed straight to the generator (not just the
      // readiness label) so it can cap intensity on a high-RHR / sluggish-
      // recovery day even when the classification reads HARD.
      const ri = smartCtx?.readinessInputs;
      const cardioFit = (() => { try { return computeCardioFitness(); } catch { return null; } })();
      const hrLine = (() => {
        const parts = [];
        if (ri?.rhr?.deltaBpm != null) parts.push(`RHR ${ri.rhr.deltaBpm >= 0 ? "+" : ""}${ri.rhr.deltaBpm}bpm vs 14d baseline`);
        if (ri?.recovery?.lastWorkoutHrRecoveryBpm) parts.push(`last 1-min HR recovery ${ri.recovery.lastWorkoutHrRecoveryBpm}bpm`);
        if (ri?.activity?.walkingHrDeltaBpm != null) parts.push(`walking HR ${ri.activity.walkingHrDeltaBpm >= 0 ? "+" : ""}${ri.activity.walkingHrDeltaBpm}bpm vs baseline`);
        if (cardioFit?.hasData) parts.push(`cardio-fitness trend ${cardioFit.verdict}`);
        return parts.length ? `HR signals: ${parts.join("; ")}.` : "";
      })();

      const GOAL_MUSCLES = {
        "Upper Body":    ["Chest","Back","Shoulders","Triceps","Biceps"],
        "Push":          ["Chest","Shoulders","Triceps"],
        "Pull":          ["Back","Biceps"],
        "Lower Body":    ["Legs","Glutes"],
        "Legs":          ["Legs","Glutes"],
        "Cardio & Core": ["Core","Cardio"],
        "Full Body":     ["Chest","Back","Shoulders","Legs","Glutes","Triceps","Biceps","Core"],
      };
      const goalMuscles = GOAL_MUSCLES[aiGenGoal] || null;

      // Does an exercise match the chosen goal + equipment? (cap-free predicate)
      const matchesGoalEquip = (e) => {
        if (aiGenEquip === "Bodyweight" && e.category !== "Bodyweight" && e.category !== "Cardio") return false;
        if (aiGenEquip === "Dumbbells Only" && e.category !== "Free weights" && e.category !== "Bodyweight" && e.category !== "Cardio") return false;
        if (goalMuscles && !goalMuscles.includes(e.primary)) return false;
        return true;
      };

      // Behavioural (skip/swap) + EXPLICIT (the user's like/hide picker)
      // preferences combine: hidden → excluded from the pool, liked → always
      // offered (bypasses the per-muscle cap) and flagged preferred.
      const prefs = (() => { try { return exercisePreferences(loadHistory()); } catch { return { avoid: [], prefer: [], stats: {} }; } })();
      const explicitPicks = loadExercisePicks();
      const avoidSet = new Set([...prefs.avoid, ...Object.keys(explicitPicks.hidden)]);
      const likedSet = new Set([...prefs.prefer, ...Object.keys(explicitPicks.liked)]);

      // Build the candidate pool: liked exercises first (always kept), then up
      // to 3 per muscle group, skipping anything avoided/hidden.
      const matching = EXERCISE_LIBRARY.filter(matchesGoalEquip);
      const groupCount = {};
      const pickedPool = [];
      for (const e of matching) {
        if (avoidSet.has(e.id)) continue;
        const liked = likedSet.has(e.id);
        groupCount[e.primary] = (groupCount[e.primary] || 0) + 1;
        if (liked || groupCount[e.primary] <= 3) pickedPool.push(e);
      }
      // Liked first so the model sees them at the top of the id list.
      pickedPool.sort((a, b) => (likedSet.has(b.id) ? 1 : 0) - (likedSet.has(a.id) ? 1 : 0));
      // Never produce an empty pool — if the user hid almost everything, fall
      // back to the goal/equipment matches ignoring hides.
      const pool = pickedPool.length ? pickedPool : matching;
      const idList = pool.map(e => e.id).join(",");

      // v2: combine generator (#14) + rationale (#15) into one schema-locked
      // call. Adds recent training history (last 5 sessions + muscles hit),
      // last-performance per exercise (from history), active-session
      // awareness, and training-day macros — all so the model can avoid
      // hitting muscles trained yesterday or duplicating an in-progress
      // session. Pipe-delimited parser deleted.
      const recentHistory = (() => {
        const h = loadHistory().slice(0, 5);
        return h.map(w => ({
          name: w.aiName || w.name,
          daysAgo: w.timestamp ? Math.round((Date.now() - w.timestamp) / 86400000) : null,
          muscles: extractMuscleGroups(w, 6),
          volume: w.volume || 0,
        }));
      })();
      const lastPerf = (() => {
        // Last-performance map for exercises in the candidate pool only.
        const all = loadLastExerciseData();
        const out = {};
        for (const ex of pool) {
          if (all[ex.name]) out[ex.id] = all[ex.name];
        }
        return out;
      })();
      // Double-progression target per candidate, derived from the user's LAST
      // session for that exercise (not the all-time PR). Tells the model
      // whether to push (+load), hold, or deload — so it programs real
      // progression instead of just echoing the last weights.
      const progression = (() => {
        const lastSession = loadLastSessionPerformance();
        const out = {};
        for (const ex of pool) {
          const last = lastSession[ex.id] || lastSession[ex.name];
          const prog = progressionTarget(last, ex);
          if (prog) out[ex.id] = prog;
        }
        return out;
      })();
      const active = (() => {
        try { return loadActiveSession() ? "yes" : "no"; } catch { return "no"; }
      })();
      const goals = (() => {
        try { return JSON.parse(localStorage.getItem("calorieGoals") || "{}") || {}; } catch { return {}; }
      })();
      const userPrompt = `Generate a ${aiGenDuration}min ${aiGenGoal} workout using ${aiGenEquip}. Pick exactly 7 exercises from the candidate ids.
${readinessLine ? `\n${readinessLine}\n` : ""}${hrLine ? `${hrLine}\n` : ""}
Candidate ids (MUST use these): ${idList}

Last 5 sessions (avoid duplicating yesterday's muscles): ${JSON.stringify(recentHistory)}
All-time best set per candidate id (context only): ${JSON.stringify(lastPerf)}
Progression target per candidate id (double progression from LAST session, RPE-autoregulated — use suggestedKg × suggestedReps as the working set on HARD/MEDIUM days, then apply readiness scaling; a "hold"/"deload" action with an RPE note means the user maxed out last time, so respect it): ${JSON.stringify(progression)}
Preferred exercises (user explicitly likes these OR keeps swapping them in — favour them, include them when they fit the goal): ${JSON.stringify(pool.filter(e => likedSet.has(e.id)).map(e => e.id))}
Avoid exercises (user repeatedly skips these — do NOT include): ${JSON.stringify(prefs.avoid)}
Session in progress: ${active}
Training-day macros (informational): ${JSON.stringify({ calories: goals.training?.calories, protein: goals.training?.protein })}

Return JSON with: exercises (array of 7 entries, each { id, weight, reps }), rationale (2-3 sentences — specific and clinical, cite readiness if relevant), and optionally skip: true + skipReason if REST readiness and training is inadvisable.`;
      const WORKOUT_GEN_SCHEMA = {
        type: "object",
        properties: {
          skip: { type: "boolean" },
          skipReason: { type: "string" },
          exercises: {
            type: "array",
            minItems: 1,
            maxItems: 7,
            items: {
              type: "object",
              properties: {
                id: { type: "string" },
                weight: { type: "number" },
                reps: { type: "integer" },
              },
              required: ["id", "weight", "reps"],
            },
          },
          rationale: { type: "string" },
        },
        required: ["rationale"],
      };
      const sys = composeSystem([
        `You are a strength and conditioning coach designing a workout. Pick exercises that avoid redundantly hitting muscles trained in the last 24h, and use the muscle-group spread from recentHistory to balance the week.

PROGRESSIVE OVERLOAD (primary directive for weight selection):
- For each exercise, the "Progression target" map gives suggestedKg, suggestedReps and an action (increase / hold / deload) computed by double progression from the user's LAST session. Use suggestedKg × suggestedReps as the prescribed working set on HARD/MEDIUM days. Do NOT simply repeat the all-time best or last weights — the target already encodes the next step.
- action "increase" = they hit the top of the rep range last time, so the small load bump is appropriate. "hold" = repeat the weight and chase the reps. "deload" = back off; do not add load.
- The action is already RPE-autoregulated: if it carries an RPE note (e.g. "RPE 9 — hit reps but maxed"), the user logged that the last session was very hard, so a "hold"/"deload" is deliberate — do NOT override it back to an increase. When you hold or deload for this reason, say so briefly in the rationale.
- Where no progression target exists (new exercise), use the candidate's sensible defaults.
- Apply the READINESS-BASED VOLUME RULES below ON TOP of the progression target.

EXERCISE SELECTION FROM FEEDBACK:
- Favour the user's "Preferred exercises" where they fit the goal (they keep choosing these).
- Never include anything in the "Avoid exercises" list — the user repeatedly skips those.

HR AUTOREGULATION (from the "HR signals" line, if present):
- RHR elevated ≥ +5 bpm vs baseline OR last 1-min HR recovery < 15 bpm OR walking HR ≥ +10 bpm vs baseline → the cardiovascular system is under-recovered. Trim total volume ~10–15%, keep top sets sub-maximal (no PRs), and lean toward steadier tempo / an aerobic (Z2) finisher rather than high-intensity intervals — even if the readiness class is HARD. Say so in one line of the rationale.
- A "cardio-fitness trend declining" signal with heavy recent training reinforces backing off; "improving" supports a normal-to-slightly-harder session.

READINESS-BASED VOLUME RULES:
- HARD: Full prescribed volume and intensity. PR attempts are appropriate.
- MEDIUM: Full prescribed volume. Standard session.
- EASY: Reduce to ~80% of typical sets/volume. Use 85-90% of normal weights. No new PRs.
- REST: Return skip: true with a brief skipReason. If user insists on training, prescribe 2 sets per exercise, very light weights (50-60% normal), and note this is active recovery only.`,
        formatProfileForPrompt(getUserProfile()),
        OUTPUT_JSON_STRICT,
      ]);
      const raw = await callGemini(userPrompt, {
        systemInstruction: sys,
        responseSchema: WORKOUT_GEN_SCHEMA,
        model: MODELS.workoutGen,
        maxOutputTokens: 1024,
        feature: "workoutGen",
      });
      _rawResponse = raw;

      let parsed;
      try { parsed = JSON.parse(raw); }
      catch { parsed = { exercises: [], rationale: "" }; }

      // REST day: model recommends skipping training
      if (parsed.skip === true) {
        hapticHayahSuccess();
        setAiGenResult({ skipped: true, reason: parsed.skipReason || "Rest is the best training for today.", rationale: parsed.rationale || "" });
        return;
      }

      const libMap = Object.fromEntries(EXERCISE_LIBRARY.map(e => [e.id, e]));
      const libMapCI = Object.fromEntries(
        EXERCISE_LIBRARY.map(e => [e.id.toLowerCase().replace(/[\s-]+/g, '_'), e])
      );
      const resolveId = (raw) => {
        const t = (raw || "").trim();
        if (libMap[t]) return t;
        const ci = t.toLowerCase().replace(/[\s-]+/g, '_');
        return libMapCI[ci]?.id || null;
      };
      const picks = (parsed.exercises || [])
        .map(e => ({ id: resolveId(e.id), w: Number(e.weight) || 0, r: parseInt(e.reps, 10) || 10 }))
        .filter(p => p.id)
        .slice(0, 7);

      // Fill remaining slots from the filtered pool (dedup by id)
      if (picks.length < 7) {
        const used = new Set(picks.map(p => p.id));
        for (const e of pool) {
          if (picks.length >= 7) break;
          if (!used.has(e.id)) {
            picks.push({ id: e.id, w: e.defaults?.weight ?? 0, r: e.defaults?.reps ?? 10 });
            used.add(e.id);
          }
        }
      }

      const exercises_list = picks.map((p, i) => {
        const lib = libMap[p.id] || pool[i] || pool[0];
        // Derive the movement-type badge from the exercise, not a hardcoded
        // "push" (which mislabelled Leg Press / Lat Pulldown as PUSH).
        const exType =
          lib.category === "Cardio" ? "cardio"
          : (lib.primary === "Back" || lib.primary === "Biceps") ? "pull"
          : (lib.primary === "Legs" || lib.primary === "Glutes") ? "legs"
          : (lib.primary === "Core") ? "core"
          : "push";
        return {
          block: ["A","B","C","D"][Math.floor(i / 3)] || "A",
          id: lib.id,
          name: lib.name,
          muscles: [lib.primary, ...(lib.secondary || [])].join(", "),
          defaultWeight: Number.isFinite(p.w) ? p.w : (lib.defaults?.weight ?? 0),
          defaultReps: Number.isFinite(p.r) ? p.r : (lib.defaults?.reps ?? 10),
          type: exType,
        };
      });

      const EMOJIS = { "Upper Body":"💪","Push":"🔼","Pull":"🔽","Lower Body":"🦵","Legs":"🦵","Cardio & Core":"🔥","Full Body":"⚡" };
      const template = {
        id: `ai_${Date.now()}`,
        name: `AI ${aiGenGoal}`,
        emoji: EMOJIS[aiGenGoal] || "🤖",
        desc: `${aiGenDuration}min · ${aiGenGoal} · AI generated`,
        circuits: 3,
        exercises_list,
      };

      // v2: rationale arrives in the schema response (combined call).
      const rationale = String(parsed.rationale || "").trim();

      hapticHayahSuccess();
      setAiGenResult({ template, rationale });
    } catch (e) {
      setAiGenError(e.message || "Failed to generate workout");
      if (_rawResponse) setAiGenRawDebug(_rawResponse);
    } finally {
      setAiGenLoading(false);
    }
  };

  // ── Mobility / stretching generator ─────────────────────────────
  // REST-day recovery option. Asks Gemini for a guided joint-by-joint
  // mobility flow with explicit step-by-step cues per stretch so the
  // user can perform every move from the on-screen instructions alone.
  // Tuned for the readiness signal — if HRV is suppressed we bias
  // toward parasympathetic / breath-led stretches, not loaded mobility.
  const handleMobilityGenerate = () => {
    if (isAiJobInFlight(MOBILITY_CACHE_KEY)) return;
    if (!getGeminiKey()) {
      setMobilityError("Add a Gemini API key in Hayah Settings → AI Assistant.");
      return;
    }
    // Snapshot every input the generator depends on — runJob runs at module
    // scope and may outlive this component (closing the modal must not
    // cancel it). Closing inputs into the snapshot keeps the call stable.
    const readinessSnap = aiGenReadiness || loadReadinessCache();
    const smartSnap     = smartCtx;
    runAiJob(MOBILITY_CACHE_KEY, async () => {
      const readinessCtx = readinessSnap;
      const readinessLine = readinessCtx?.classification
        ? `Today's readiness: ${readinessCtx.classification}. ${readinessCtx.rationale || ""}`
        : "";
      // Surface the most-recent workout's primary muscles so the routine
      // can prioritise areas that were just trained.
      const lastWorkout = (() => {
        const h = loadHistory();
        if (!h.length) return null;
        const w = h[0];
        return {
          name: w.aiName || w.name,
          daysAgo: w.timestamp ? Math.round((Date.now() - w.timestamp) / 86400000) : null,
          muscles: extractMuscleGroups(w, 6),
        };
      })();

      // Publish AIAnalysingPipeline sources so the loading state shows the
      // real signals being analysed, same pattern as readiness / workout-gen.
      try {
        const ri = readinessCtx?.inputs || smartSnap?.readinessInputs;
        publishSources("mobility-flow", mobilitySources({
          readiness: readinessCtx?.classification || null,
          lastWorkout,
          focusAreas: lastWorkout?.muscles || null,
          duration: 12,
          hrv: ri?.hrv,
          sleepHours: ri?.sleep?.totalHours,
        }));
      } catch {}

      const MOBILITY_SCHEMA = {
        type: "object",
        properties: {
          title: { type: "string" },
          duration_min: { type: "integer" },
          intro: { type: "string" },
          stretches: {
            type: "array",
            minItems: 5,
            maxItems: 10,
            items: {
              type: "object",
              properties: {
                name: { type: "string" },
                target: { type: "string" },
                duration_sec: { type: "integer" },
                sides: { type: "string", enum: ["both", "one", "n/a"] },
                sets: { type: "integer" },
                cues: { type: "array", items: { type: "string" }, minItems: 3, maxItems: 6 },
                breath: { type: "string" },
                avoid: { type: "string" },
              },
              required: ["name", "target", "duration_sec", "cues"],
            },
          },
          closing_note: { type: "string" },
        },
        required: ["title", "duration_min", "stretches"],
      };

      const sys = composeSystem([
        `You are a world-class movement coach designing a 12-minute guided mobility / stretching flow for a recovery / REST day. Prioritise joint-by-joint mobility (ankles → hips → t-spine → shoulders → neck) plus dynamic and static stretches on muscles trained recently. Stretches must be performable in a small space with no equipment.

For every stretch, write 3–6 numbered cues so the user can perform the move from the instructions alone — be specific about body position (foot placement, hand position, gaze, hip angle), what to feel, and where to feel it. Include breathing pattern and one common mistake to avoid. No emoji in cues. No equipment cues. No music references.`,
        formatProfileForPrompt(getUserProfile()),
        OUTPUT_JSON_STRICT,
      ]);
      const prompt = `Design a 10–15 minute mobility & stretching routine for today.
${readinessLine ? `\n${readinessLine}\n` : ""}
${lastWorkout ? `Most recent workout (${lastWorkout.daysAgo}d ago): ${lastWorkout.name} — muscles hit: ${(lastWorkout.muscles || []).join(", ")}. Prioritise opening these areas.\n` : ""}
Return JSON with title, duration_min (total minutes), intro (1 sentence), an array of 6–8 stretches, and closing_note. Each stretch must have name, target (muscles/joints), duration_sec (per side if applicable), sides ("both" | "one" | "n/a"), sets (optional, default 1), cues (3–6 specific step-by-step instructions), breath (e.g. "Inhale 4s, exhale 6s"), and avoid (one common mistake).`;

      const raw = await callGemini(prompt, {
        systemInstruction: sys,
        responseSchema: MOBILITY_SCHEMA,
        model: MODELS.mobilityFlow,
        maxOutputTokens: 8192,
        temperature: 0.5,
        feature: "mobilityFlow",
        // Disable Gemini 2.5 Flash's implicit thinking budget. With it on,
        // the model silently spent ~7K of the 8K output cap on reasoning
        // tokens, leaving only ~50 tokens of JSON before truncation.
        // Structured-schema output doesn't need reasoning anyway.
        thinkingConfig: { thinkingBudget: 0 },
      });
      // Robust parse: Gemini occasionally wraps the JSON in a ```json fence
      // or prepends a short preamble despite responseMimeType=application/json.
      // Strip both before parsing; if still malformed, surface a snippet of
      // the raw output instead of a useless "could not parse".
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        const cleaned = String(raw || "")
          .replace(/^﻿/, "")
          .replace(/^```(?:json)?\s*/i, "")
          .replace(/\s*```\s*$/i, "")
          .trim();
        const first = cleaned.indexOf("{");
        const last  = cleaned.lastIndexOf("}");
        if (first >= 0 && last > first) {
          try { parsed = JSON.parse(cleaned.slice(first, last + 1)); } catch {}
        }
        if (!parsed) {
          const snippet = (raw || "").slice(0, 160).replace(/\s+/g, " ");
          throw new Error(`Could not parse mobility plan. Raw: ${snippet}`);
        }
      }
      if (!parsed.stretches || !parsed.stretches.length) {
        throw new Error("Mobility plan came back empty");
      }
      try {
        localStorage.setItem(MOBILITY_CACHE_KEY, JSON.stringify({ ...parsed, ts: Date.now() }));
      } catch {}
      try { publishSources("mobility-flow", []); } catch {}
    });
  };

  const handleMobilityLog = () => {
    if (!mobilityResult || mobilityLogged) return;
    try {
      const stretches = mobilityResult.stretches || [];
      const durationSec = Math.max(60, Math.round((Number(mobilityResult.duration_min) || 12) * 60));
      const workout = {
        name: mobilityResult.title || "Mobility & Stretching",
        aiName: mobilityResult.title || "Mobility & Stretching",
        templateId: null,
        kind: "mobility",
        date: todayLabel(),
        time: nowTimeLabel(),
        timestamp: Date.now(),
        duration: durationSec,
        sets: stretches.length,
        volume: 0,
        exercises: stretches.map((s, i) => ({
          name: s.name,
          id: null,
          muscles: s.target || "",
          type: "stretch",
          block: ["A","B","C","D"][Math.floor(i / 3)] || "A",
          sets: [{ weight: 0, reps: Math.max(1, Number(s.duration_sec) || 30), unit: "sec" }],
        })),
        prs: 0,
        cardio: null,
        watch: null,
      };
      onLogSession && onLogSession(workout);
      try { hapticMilestone(); } catch {}
      setMobilityLogged(true);
    } catch (e) {
      setMobilityError("Couldn't log mobility session: " + (e.message || "unknown error"));
    }
  };

  return (
    <div className="ry-slide" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {onSubTab && <SubTabs theme={theme} active={1} onChange={onSubTab} />}
      <div className="ry-sb" style={{ flex: 1, overflowY: "auto", paddingBottom: "calc(96px + env(safe-area-inset-bottom))" }}>
        {/* Header — heading on its own line; actions stacked below in a
            horizontal scrollable row so the title never wraps awkwardly. */}
        <div style={{ padding: "12px 16px 14px" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.03em" }}>What are we doing today?</div>
          <div style={{ fontSize: 13, color: theme.ink3, marginTop: 4 }}>{todayLabel()} · {nowTimeLabel()}</div>
          {getGeminiKey() && (
            <div style={{ display: "flex", gap: 8, marginTop: 12, overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}>
              <button
                onClick={() => { try { hapticTap(); } catch {} setImportOpen(true); setImportError(null); setImportResult(null); setImportText(""); }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "9px 14px", borderRadius: 20,
                  border: `1px solid ${theme.amber}44`, background: `${theme.amber}18`, color: theme.amber,
                  fontSize: 12.5, fontWeight: 700, letterSpacing: "0.3px", cursor: "pointer",
                  whiteSpace: "nowrap", flexShrink: 0,
                }}
              >
                📥 Paste Workout
              </button>
              <button
                onClick={() => { try { hapticTap(); } catch {} setAiGenOpen(true); setAiGenError(null); }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "9px 14px", borderRadius: 20,
                  border: `1px solid ${theme.teal}44`, background: `${theme.teal}12`, color: theme.teal,
                  fontSize: 12.5, fontWeight: 700, letterSpacing: "0.3px", cursor: "pointer",
                  whiteSpace: "nowrap", flexShrink: 0,
                }}
              >
                ✦ AI Workout
              </button>
              <button
                onClick={() => { try { hapticTap(); } catch {} setTreadmillOpen(true); setTreadmillError(null); setTreadmillResult(null); }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "9px 14px", borderRadius: 20,
                  border: `1px solid ${theme.amber}44`, background: `${theme.amber}12`, color: theme.amber,
                  fontSize: 12.5, fontWeight: 700, letterSpacing: "0.3px", cursor: "pointer",
                  whiteSpace: "nowrap", flexShrink: 0,
                }}
              >
                🏃 Home
              </button>
            </div>
          )}
        </div>

        <WatchCard theme={theme} available={watchAvail} live={false} />

        {routines.length > 0 && (
          <>
            <SecHd theme={theme} num="01" label={`Your routines · ${routines.length}`} />
            <div style={{ padding: "0 16px 4px", display: "flex", flexDirection: "column", gap: 10 }}>
              {routines.map(tmpl => (
                <div key={tmpl.id} style={{ display: "flex", gap: 8 }}>
                  {/* Swipe-left-to-delete wraps the main row. Edit button
                      stays outside the swipe so a tap on it never gets
                      misinterpreted as a horizontal pan. */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <HayahSwipeRow
                      onDelete={() => { hapticHayahSelection(); deleteRoutine(tmpl.id); refreshRoutines(); }}
                      onTap={() => onPick(tmpl)}
                      ariaLabel={`Routine: ${tmpl.name} — tap to start, swipe left to delete`}
                    >
                      <div style={{
                        background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 16,
                        padding: 16, display: "flex", alignItems: "center", gap: 14,
                      }}>
                        <div style={{
                          width: 44, height: 44, borderRadius: 12, background: theme.tealDim,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 22, flexShrink: 0,
                        }}>{tmpl.emoji || "💪"}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 600, color: theme.ink1, letterSpacing: "-0.01em", marginBottom: 3 }}>{tmpl.name}</div>
                          <div style={{ fontSize: 12, color: theme.ink3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tmpl.desc || `${tmpl.exercises_list.length} exercises`}</div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div className="ry-mono" style={{ fontSize: 11, color: theme.ink4 }}>{tmpl.circuits}×{tmpl.exercises_list.length}</div>
                          <div style={{ fontSize: 10, color: theme.ink4, marginTop: 1 }}>circuits</div>
                        </div>
                      </div>
                    </HayahSwipeRow>
                  </div>
                  <button className="ry-btn" onClick={() => { hapticHayahSelection(); setEditing(tmpl); }}
                    aria-label={`Edit ${tmpl.name}`}
                    style={{ width: 36, background: theme.bg2, border: `0.5px solid ${theme.line}`,
                      borderRadius: 16, color: theme.ink3, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name="edit" size={14}/>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Recent Sessions — restartable cards from finished workouts.
            Each card lets the user kick off the same workout again
            pre-populated with the weights/reps from that session. PR
            targets surface during the workout via loadLastExerciseData
            (canonical-aware) so the heaviest-ever set across ANY
            workout for each exercise is what they're chasing. */}
        {(() => {
          // Group recent history by templateId so multiple sessions of
          // "Full Body 3" collapse into one card showing the latest.
          // Workouts without a templateId fall back to name-only matching.
          // We carry the original history index so swipe-delete maps
          // back to the right entry.
          const seen = new Map();
          history.forEach((w, idx) => {
            if (!w?.timestamp) return;
            if (!Array.isArray(w.exercises) || w.exercises.length === 0) return;
            const key = w.templateId || `name:${w.name || "Untitled"}`;
            if (!seen.has(key)) seen.set(key, { w, idx });
          });
          const recent = [...seen.values()]
            .sort((a, b) => (b.w.timestamp || 0) - (a.w.timestamp || 0))
            .slice(0, 8);
          if (recent.length === 0) return null;
          const relTime = (ts) => {
            const dt = Date.now() - (ts || 0);
            const days = Math.floor(dt / 86400000);
            if (days === 0) return "Today";
            if (days === 1) return "Yesterday";
            if (days < 7)   return `${days}d ago`;
            const wk = Math.floor(days / 7);
            return wk === 1 ? "1wk ago" : `${wk}wk ago`;
          };
          return (
            <>
              <SecHd theme={theme} num={routines.length > 0 ? "02" : "01"} label={`Restart a recent session · ${recent.length}`} />
              <div style={{ padding: "0 16px 4px", display: "flex", flexDirection: "column", gap: 10 }}>
                {recent.map(({ w, idx: historyIdx }, i) => (
                  <HayahSwipeRow
                    key={i}
                    onDelete={() => { hapticHayahSelection(); onDeleteSession && onDeleteSession(historyIdx); }}
                    ariaLabel={`Session: ${w.name || "Workout"} — tap to restart, swipe left to delete`}
                    onTap={() => {
                      hapticHayahSelection();
                      // Build a routine-shaped object from the saved
                      // workout. The active session uses defaultWeight /
                      // defaultReps to pre-fill the input rows; PR
                      // detection still walks loadLastExerciseData so the
                      // PR target is canonical-best across all history.
                      const exList = (w.exercises || []).map((ex, exi) => {
                        const top = (ex.sets || []).reduce((b, s) => {
                          const bw = parseFloat(b?.weight) || 0;
                          const sw = parseFloat(s?.weight) || 0;
                          if (sw > bw) return s;
                          if (sw === bw && (parseInt(s?.reps, 10) || 0) > (parseInt(b?.reps, 10) || 0)) return s;
                          return b;
                        }, ex.sets?.[0]) || {};
                        return {
                          block: ex.block || "A",
                          id: ex.id || resolveExerciseId(ex.name, ex.id),
                          name: ex.name,
                          type: ex.type || "push",
                          muscles: ex.muscles || null,
                          defaultReps: parseInt(top.reps, 10) || 10,
                          defaultWeight: parseFloat(top.weight) || 0,
                        };
                      });
                      const replay = {
                        // Carry the original templateId so the next
                        // finish records as the same lineage. Falls
                        // back to a deterministic key on name so
                        // freeform restarts still group.
                        id: w.templateId || `replay-${(w.name || "").toLowerCase().replace(/\s+/g, "-")}`,
                        name: w.name || "Workout",
                        emoji: "↺",
                        desc: `Last done ${relTime(w.timestamp)} · ${w.sets} sets`,
                        circuits: Math.max(1, Math.round((w.sets || 0) / Math.max(1, exList.length))),
                        blockLabels: {},
                        exercises_list: exList,
                        cardio: w.cardio || null,
                        isReplay: true,
                      };
                      onPick(replay);
                    }}
                  >
                    <div style={{
                      background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 16,
                      padding: 14, display: "flex", alignItems: "center", gap: 12,
                    }}>
                      <div style={{
                        width: 42, height: 42, borderRadius: 12, background: theme.amberDim,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 20, flexShrink: 0,
                      }}>↺</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14.5, fontWeight: 600, color: theme.ink1, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.name || "Workout"}</div>
                        <div style={{ fontSize: 11.5, color: theme.ink3, marginTop: 2 }}>
                          {relTime(w.timestamp)} · {w.exercises.length} ex · {w.sets || 0} sets
                        </div>
                      </div>
                      <span style={{ fontSize: 10, color: theme.amber, fontWeight: 800, letterSpacing: "0.12em", padding: "4px 8px", border: `1px solid ${theme.amber}55`, borderRadius: 8 }}>RESTART →</span>
                    </div>
                  </HayahSwipeRow>
                ))}
              </div>
            </>
          );
        })()}

        <SecHd theme={theme} num={(() => {
          // Section number bumps dynamically based on what's visible above.
          let n = 1;
          if (routines.length > 0) n++;
          // Recent sessions section also bumps n.
          const hasRecent = history.some(w => Array.isArray(w.exercises) && w.exercises.length > 0);
          if (hasRecent) n++;
          return String(n).padStart(2, "0");
        })()} label="Quick start"
          right={hiddenIds.length > 0 ? (
            <button onClick={restoreHidden}
              style={{ background: "transparent", border: 0, color: theme.ink3, fontSize: 11, cursor: "pointer", padding: "2px 6px" }}>
              Restore {hiddenIds.length} hidden
            </button>
          ) : undefined} />
        <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {visibleBuiltIns.filter(t => t.id === "quick-start").map(tmpl => (
            <div key={tmpl.id} style={{ display: "flex", gap: 8 }}>
              <button className="ry-btn" onClick={() => onPick(tmpl)}
                style={{
                  flex: 1, background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 16,
                  padding: 16, display: "flex", alignItems: "center", gap: 14,
                  textAlign: "left", color: "inherit",
                }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: tmpl.tag === "pull" ? theme.tealDim : tmpl.tag === "legs" ? theme.sageDim : theme.amberDim,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 22, flexShrink: 0,
                }}>{tmpl.emoji}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: theme.ink1, letterSpacing: "-0.01em" }}>{tmpl.name}</span>
                    {tmpl.id !== "quick-start" && <ExBadge theme={theme} type={tmpl.tag} />}
                  </div>
                  <div style={{ fontSize: 12, color: theme.ink3 }}>{tmpl.desc}</div>
                </div>
                {tmpl.id !== "quick-start" && (
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div className="ry-mono" style={{ fontSize: 11, color: theme.ink4 }}>{tmpl.circuits}×{tmpl.exercises_list.length}</div>
                    <div style={{ fontSize: 10, color: theme.ink4, marginTop: 1 }}>circuits</div>
                  </div>
                )}
              </button>
              {tmpl.id !== "quick-start" && (
                <button className="ry-btn"
                  onClick={() => { hapticHayahSelection(); confirmDeleteId === tmpl.id ? hideBuiltIn(tmpl.id) : setConfirmDeleteId(tmpl.id); }}
                  aria-label={`Hide ${tmpl.name}`}
                  style={{ width: 36, background: confirmDeleteId === tmpl.id ? "#5a1a1a" : theme.bg2,
                    border: `0.5px solid ${confirmDeleteId === tmpl.id ? "#ff6b6b" : theme.line}`,
                    borderRadius: 16, color: confirmDeleteId === tmpl.id ? "#ff8c8c" : theme.ink4,
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="trash" size={14}/>
                </button>
              )}
            </div>
          ))}
          <button className="ry-btn"
            onClick={() => { hapticHayahSelection(); setEditing({ id: newRoutineId(), name: "", emoji: "💪", desc: "", circuits: 3, exercises_list: [], tag: "push", custom: true, blockLabels: { A: "Custom" } }); }}
            style={{
              background: "transparent", border: `1px dashed ${theme.line}`, borderRadius: 16,
              padding: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              color: theme.ink3, width: "100%",
            }}>
            <Icon name="plus" size={14}/>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Build a custom routine</span>
          </button>
        </div>

        <SecHd theme={theme} num="02" label="Pre-workout" />
        <button className="ry-btn" onClick={() => { hapticHayahSelection(); setFuelOpen(true); }}
          style={{
            margin: "0 16px", padding: "13px 14px",
            background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 14,
            display: "flex", alignItems: "center", gap: 12, width: "calc(100% - 32px)",
            color: "inherit", textAlign: "left",
          }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: theme.amberDim, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>🥣</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.ink1, letterSpacing: "-0.01em" }}>Log pre-workout fuel</div>
            <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1 }}>{PRE_WORKOUT_SNACK}</div>
          </div>
          <span style={{ display: "inline-flex", padding: "3px 9px", borderRadius: 999, background: theme.amberDim, color: theme.amberSoft, fontSize: 11, fontWeight: 500 }}>Ghidha</span>
        </button>
      </div>

      {fuelOpen && createPortal(
        <Sheet onClose={() => setFuelOpen(false)} theme={theme}>
          <FuelForm theme={theme} onClose={() => setFuelOpen(false)} />
        </Sheet>,
        document.body)}

      {/* ── Paste Workout from Steve's WhatsApp ─────────────────────
          Two modes:
            - Step 1 (no importResult): textarea + Parse button
            - Step 2 (importResult set): preview with readiness banner,
              per-exercise prescription, Save + Start Now actions */}
      {importOpen && createPortal(
        <div style={{
          position: "fixed", inset: 0, zIndex: 200,
          background: theme.bg0,
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 10px", borderBottom: `0.5px solid ${theme.line}` }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.02em" }}>{importResult ? "Workout ready" : "Paste workout from Steve"}</div>
              <div style={{ fontSize: 11, color: theme.ink3, marginTop: 2 }}>{importResult ? "Weights prescribed from your history" : "Copy the WhatsApp message and paste it below"}</div>
            </div>
            <button
              onClick={() => { setImportOpen(false); setImportResult(null); setImportError(null); setImportText(""); }}
              style={{ background: "none", border: 0, color: theme.ink3, fontSize: 24, lineHeight: 1, cursor: "pointer", padding: "4px 8px" }}
            >×</button>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px 16px" }}>
            {!importResult ? (
              <>
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder={"Example:\n\nFull Body\n\n🔥 WARM-UP (10 Minutes)\nTreadmill Walk · Incline 12 · Speed 5 · 10 min\n\n🔴 Block A (3 Rounds)\n1️⃣ Leg Press (Heavy) – 12 reps\n2️⃣ DB Goblet Squats – 20 reps\n3️⃣ DB Shoulder Press – 20 reps\n..."}
                  rows={14}
                  style={{
                    width: "100%", background: theme.bg2, border: `0.5px solid ${theme.line}`,
                    borderRadius: 12, padding: "12px 14px", color: theme.ink1,
                    fontSize: 13, fontFamily: "inherit", lineHeight: 1.55, outline: "none",
                    resize: "vertical", letterSpacing: "-0.005em",
                  }}
                />
                {importError && (
                  <div style={{ marginTop: 10, padding: "10px 12px", background: "rgba(255,138,101,0.10)", border: "1px solid rgba(255,138,101,0.32)", borderRadius: 10, color: "#ff8a65", fontSize: 12, lineHeight: 1.5 }}>{importError}</div>
                )}
                <button
                  onClick={async () => {
                    if (!importText.trim()) { setImportError("Paste the workout text first"); return; }
                    try { hapticTap(); } catch {}
                    setImportLoading(true); setImportError(null);
                    try {
                      // History fetcher — walks loadHistory() once and
                      // groups sessions by canonical exercise id for
                      // O(1) lookup in suggestForExercise.
                      const hist = loadHistory();
                      const byCanon = new Map();
                      for (const w of hist) {
                        for (const ex of (w.exercises || [])) {
                          if (!ex?.sets?.length) continue;
                          const cId = resolveExerciseId(ex.name, ex.id);
                          if (!byCanon.has(cId)) byCanon.set(cId, { sessions: [] });
                          const setsArr = ex.sets;
                          const top = setsArr.reduce((b, s) => {
                            const bw = parseFloat(b?.weight) || 0;
                            const sw = parseFloat(s?.weight) || 0;
                            if (sw > bw) return s;
                            return b;
                          }, setsArr[0]);
                          byCanon.get(cId).sessions.push({
                            timestamp: w.timestamp,
                            bestWeight: parseFloat(top?.weight) || 0,
                            reps: parseInt(top?.reps, 10) || 0,
                          });
                        }
                      }
                      const getHistory = (cId) => byCanon.get(cId) || { sessions: [] };
                      const cached = loadReadinessCache();
                      const readiness = cached?.classification || "MEDIUM";
                      // computeReadinessInputs gives us sleep / HRV
                      // trends / muscle recency etc so the prescription
                      // algorithm can apply fatigue overrides beyond
                      // the readiness classification.
                      let readinessInputs = null;
                      try { readinessInputs = computeReadinessInputs(readAllData()); } catch {}
                      const result = await importWorkoutFromText(importText, getHistory, readiness, readinessInputs);
                      setImportResult(result);
                    } catch (e) {
                      setImportError(e.message || "Couldn't parse — try a clearer paste");
                    } finally {
                      setImportLoading(false);
                    }
                  }}
                  disabled={importLoading || !importText.trim()}
                  style={{
                    marginTop: 14, width: "100%", padding: "13px 16px",
                    background: importText.trim() && !importLoading ? theme.amber : theme.bg3,
                    color: importText.trim() && !importLoading ? "#000" : theme.ink4,
                    border: 0, borderRadius: 14, fontSize: 14, fontWeight: 700,
                    letterSpacing: "-0.01em", cursor: importLoading ? "default" : "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {importLoading ? "Parsing…" : "✦ Parse & Build Workout"}
                </button>
                <div style={{ marginTop: 10, fontSize: 10.5, color: theme.ink4, lineHeight: 1.55, textAlign: "center" }}>
                  Gemini parses the structure · your history fills in weights · readiness scales the bump
                </div>
              </>
            ) : (
              <>
                {/* Workout title — large, prominent. */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.03em", lineHeight: 1.1 }}>{importResult.routine.name}</div>
                  <div style={{ fontSize: 14, color: theme.ink3, marginTop: 6 }}>
                    {importResult.routine.exercises_list.length} exercises · {importResult.routine.circuits} rounds
                  </div>
                </div>

                {/* Readiness banner — bigger text, single panel. */}
                {(() => {
                  const r = loadReadinessCache();
                  const cls = r?.classification || "MEDIUM";
                  const tone = cls === "HARD" ? theme.teal : cls === "REST" ? "#ff8a65" : cls === "EASY" ? theme.amber : theme.ink2;
                  return (
                    <div style={{ padding: "14px 16px", background: `${tone}10`, border: `1px solid ${tone}44`, borderRadius: 14, marginBottom: 16 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: r?.rationale ? 10 : 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", color: tone, padding: "4px 10px", border: `1px solid ${tone}55`, borderRadius: 8 }}>READINESS · {cls}</span>
                        <span style={{ fontSize: 12, color: theme.ink3, lineHeight: 1.3 }}>{cls === "REST" ? "training not recommended" : "weights scaled accordingly"}</span>
                      </div>
                      {r?.rationale && (
                        <div style={{ fontSize: 13.5, color: theme.ink2, lineHeight: 1.55 }}>{r.rationale}</div>
                      )}
                    </div>
                  );
                })()}

                {/* Per-exercise prescription — bigger weight, bigger reason, full-width cards. */}
                {importResult.suggestions.length === 0 ? (
                  <div style={{ padding: "24px 18px", background: theme.bg2, border: `1px solid ${theme.line}`, borderRadius: 14, textAlign: "center", marginBottom: 16 }}>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>🛌</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: theme.ink1, marginBottom: 6 }}>No exercises to prescribe</div>
                    <div style={{ fontSize: 13.5, color: theme.ink3, lineHeight: 1.55, marginBottom: 14 }}>
                      The parse came back empty. Either the paste was missing exercise rows, or Gemini misread the format. Go back and edit the source, then try again.
                    </div>
                    <button
                      onClick={() => { setImportResult(null); setImportError(null); }}
                      style={{
                        padding: "10px 18px", background: theme.amber, color: "#000",
                        border: 0, borderRadius: 12, fontSize: 13, fontWeight: 700,
                        cursor: "pointer", fontFamily: "inherit",
                      }}
                    >← Edit & re-parse</button>
                  </div>
                ) : (() => {
                  const byBlock = {};
                  importResult.suggestions.forEach(s => { (byBlock[s.block] ||= []).push(s); });
                  const blockKeys = Object.keys(byBlock).sort();
                  return blockKeys.map(blk => (
                    <div key={blk} style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.20em", color: theme.amber, marginBottom: 10 }}>BLOCK {blk}{importResult.routine.blockLabels?.[blk] ? ` · ${importResult.routine.blockLabels[blk]}` : ""}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {byBlock[blk].map((s, i) => {
                          const PROG = {
                            WEIGHT_UP: { text: "🏆 WEIGHT UP", color: theme.amber, bg: "rgba(224,160,48,0.14)" },
                            REPS_UP:   { text: "↑ +1 REP",     color: theme.teal,  bg: "rgba(0,201,167,0.12)" },
                            EARN_IT:   { text: "EARN IT",      color: "#ff8a65",   bg: "rgba(255,138,101,0.12)" },
                            DELOAD:    { text: "DELOAD",       color: "#7ec8ff",   bg: "rgba(126,200,255,0.12)" },
                            MAINTAIN:  { text: "MAINTAIN",     color: theme.ink3,  bg: "rgba(255,255,255,0.04)" },
                            BASELINE:  { text: "BASELINE",     color: theme.ink4,  bg: "rgba(255,255,255,0.05)" },
                            REST:      { text: "REST DAY",     color: "#ff8a65",   bg: "rgba(255,138,101,0.10)" },
                          }[s.progression] || { text: s.progression || "—", color: theme.ink4, bg: "rgba(255,255,255,0.04)" };
                          return (
                            <div key={i} style={{ padding: "14px 16px", background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 14 }}>
                              {/* Row 1 — exercise name (large) + weight × reps (large) */}
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
                                <span style={{ fontSize: 16, fontWeight: 600, color: theme.ink1, letterSpacing: "-0.01em", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.canonName}</span>
                                <span className="ry-mono" style={{ fontSize: 17, fontWeight: 700, color: s.isPRAttempt ? theme.amber : theme.ink1, flexShrink: 0, letterSpacing: "-0.01em" }}>
                                  {s.weight > 0 ? `${s.weight}kg × ${s.reps}` : `${s.reps} reps`}
                                </span>
                              </div>
                              {/* Row 2 — progression badge + reason */}
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.12em", color: PROG.color, background: PROG.bg, border: `0.5px solid ${PROG.color}55`, borderRadius: 5, padding: "3px 7px", flexShrink: 0 }}>{PROG.text}</span>
                                <span style={{ fontSize: 12.5, color: theme.ink2, lineHeight: 1.5, flex: 1, minWidth: 0 }}>{s.reason}</span>
                              </div>
                              {s.warnings && s.warnings.length > 0 && (
                                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `0.5px solid ${theme.lineSoft}`, display: "flex", flexDirection: "column", gap: 4 }}>
                                  {s.warnings.map((w, wi) => (
                                    <div key={wi} style={{ fontSize: 11.5, color: "#ffb84d", lineHeight: 1.45, display: "flex", alignItems: "center", gap: 6 }}>
                                      <span style={{ fontSize: 13 }}>⚠︎</span><span>{w}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()}

                {importResult.finisher && (
                  <div style={{ padding: "14px 16px", background: `${theme.coral}10`, border: `1px solid ${theme.coral}33`, borderRadius: 14, marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: theme.coral, marginBottom: 6 }}>⚡ FINISHER</div>
                    <div style={{ fontSize: 14, color: theme.ink2, lineHeight: 1.55 }}>{importResult.finisher}</div>
                  </div>
                )}

                {/* Bottom-anchored sticky action bar so the buttons are
                    always reachable as the user scrolls. */}
                <div style={{ height: 12 }}/>
              </>
            )}
          </div>

          {/* Sticky action bar — only visible in preview mode WITH at
              least one exercise. Empty parses get their own inline CTA
              ('← Edit & re-parse') so we never let the user persist a
              zero-exercise routine. */}
          {importResult && importResult.suggestions.length > 0 && (
            <div style={{
              borderTop: `0.5px solid ${theme.line}`,
              background: theme.bg0,
              padding: "10px 16px calc(10px + env(safe-area-inset-bottom))",
            }}>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => { setImportResult(null); setImportError(null); }}
                  style={{
                    padding: "14px 16px", background: "none", border: `0.5px solid ${theme.line}`,
                    borderRadius: 14, color: theme.ink3, fontSize: 13, fontWeight: 600,
                    cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
                  }}
                  aria-label="Edit the paste"
                >←</button>
                <button
                  onClick={() => {
                    try { hapticTap(); } catch {}
                    upsertRoutine(importResult.routine);
                    refreshRoutines();
                    setImportOpen(false);
                    setImportResult(null);
                    setImportText("");
                  }}
                  style={{
                    flex: 1, padding: "14px 16px", background: theme.bg2, color: theme.ink1,
                    border: `0.5px solid ${theme.line}`, borderRadius: 14,
                    fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  }}
                >💾 Save</button>
                <button
                  onClick={() => {
                    try { hapticMilestone(); } catch {}
                    upsertRoutine(importResult.routine);
                    refreshRoutines();
                    setImportOpen(false);
                    setImportResult(null);
                    setImportText("");
                    onPick && onPick(importResult.routine);
                  }}
                  style={{
                    flex: 1.6, padding: "14px 16px", background: theme.amber, color: "#000",
                    border: 0, borderRadius: 14,
                    fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                    letterSpacing: "-0.01em",
                  }}
                >▶ Start now</button>
              </div>
            </div>
          )}
        </div>,
        document.body)}

      {aiGenOpen && createPortal(
        <div style={{
          position: "fixed", inset: 0, zIndex: 200,
          background: theme.bg0,
          display: "flex", flexDirection: "column",
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px 10px", borderBottom: `0.5px solid ${theme.line}`, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>{aiGenResult?.skipped ? "🛌" : "✦"}</span>
              <span style={{ fontSize: 17, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.02em" }}>
                {aiGenResult?.skipped ? "Rest Day" : aiGenResult ? `${aiGenResult.template.emoji} ${aiGenResult.template.name}` : "AI Workout Generator"}
              </span>
            </div>
            <button
              onClick={() => { setAiGenOpen(false); setAiGenError(null); setAiGenRawDebug(null); setAiGenResult(null); }}
              style={{ background: theme.bg3, border: "none", borderRadius: 20, padding: "7px 14px", color: theme.ink3, fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Close
            </button>
          </div>

          {/* Scrollable body */}
          <div className="ry-sb" style={{ flex: 1, overflowY: "auto", padding: "20px 20px 0" }}>
            {!aiGenResult && !aiGenLoading && (
              <button
                onClick={() => {
                  try { hapticTap(); } catch {}
                  setAiGenOpen(false); setAiGenError(null); setAiGenRawDebug(null); setAiGenResult(null);
                  setImportOpen(true); setImportError(null); setImportResult(null); setImportText("");
                }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%",
                  padding: "13px 16px", borderRadius: 12, marginBottom: 18, cursor: "pointer",
                  border: `1px solid ${theme.amber}55`, background: `${theme.amber}14`, color: theme.amber,
                  fontSize: 14, fontWeight: 700, letterSpacing: "0.2px",
                }}
              >
                📥 Training with Steve? Paste his workout instead
              </button>
            )}
            {aiGenResult?.skipped ? (
              <>
                <div style={{ padding: "28px 20px", textAlign: "center" }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🛌</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: theme.ink1, marginBottom: 10 }}>Rest is training</div>
                  <div style={{ fontSize: 14, color: theme.ink3, lineHeight: 1.75, marginBottom: 20 }}>{aiGenResult.reason}</div>
                  {aiGenResult.rationale && (
                    <div style={{ fontSize: 13, color: theme.ink4, lineHeight: 1.65, fontStyle: "italic" }}>{aiGenResult.rationale}</div>
                  )}
                </div>
                <div style={{ height: 80 }} />
              </>
            ) : aiGenResult ? (
              <>
                <div style={{ fontSize: 13, color: theme.ink4, marginBottom: 20 }}>
                  {aiGenDuration}min · {aiGenGoal} · {aiGenEquip}
                </div>
                {aiGenResult.rationale && (
                  <div style={{ padding: "18px 20px", borderRadius: 14, background: `${theme.teal}12`, border: `1px solid ${theme.teal}28`, marginBottom: 24 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: theme.teal, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 12 }}>About this workout</div>
                    <div style={{ fontSize: 15, color: theme.ink2, lineHeight: 1.75 }}>{aiGenResult.rationale}</div>
                  </div>
                )}
                {/* Actuals — recomputed from the workout the AI actually
                    produced (not the pre-generation 7/3 estimate). */}
                {(() => {
                  const list = aiGenResult.template.exercises_list || [];
                  const cls = aiGenReadiness?.classification;
                  const actual = previewEstimate({
                    duration: aiGenDuration, goal: aiGenGoal, readinessClass: cls,
                    weightKg: realBodyMassKg(), exerciseCount: list.length,
                  });
                  return (
                    <div style={{ marginBottom: 20, padding: "12px 14px", borderRadius: 12, background: theme.bg2, border: `1px solid ${theme.line}` }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: theme.ink4, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 8 }}>
                        Your workout
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                        {[
                          { k: "Exercises", v: list.length },
                          { k: "Blocks",    v: actual.blockCount },
                          { k: "Effort",    v: `${actual.estEffortMin}m` },
                          { k: "Est. burn", v: `${actual.estBurnKcal}` },
                        ].map((it, i) => (
                          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                            <span style={{ fontSize: 8.5, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase" }}>{it.k}</span>
                            <span style={{ fontSize: 14, fontWeight: 700, color: theme.ink1, fontFamily: "JetBrains Mono, ui-monospace, monospace" }}>{it.v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: theme.ink4, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 12 }}>
                    {aiGenResult.template.exercises_list.length} exercises · {aiGenResult.template.circuits} circuits
                  </div>
                  {aiGenResult.template.exercises_list.map((ex, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `0.5px solid ${theme.line}` }}>
                      <div style={{ width: 32, height: 32, borderRadius: 10, background: theme.tealDim, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: theme.teal, flexShrink: 0 }}>
                        {i + 1}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: theme.ink1, marginBottom: 2 }}>{ex.name}</div>
                        <div style={{ fontSize: 11, color: theme.ink4 }}>{ex.muscles}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: theme.amber }}>{ex.defaultWeight > 0 ? `${ex.defaultWeight}kg` : "BW"}</div>
                        <div style={{ fontSize: 11, color: theme.ink4 }}>{ex.defaultReps} reps</div>
                      </div>
                      {showHowToLinks() && (
                        <button title="Watch how-to" onClick={() => { try { hapticTap(); } catch {} window.open(youtubeHowToUrl(ex.name), "_blank"); }}
                          style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 16, border: `1px solid ${theme.line}`, background: theme.bg2, color: theme.coral, fontSize: 12, cursor: "pointer" }}>▶</button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : aiGenLoading ? (
              /* Rich loading state — shows what the AI is weighing so the user
                 sees real reasoning rather than a spinner. */
              <div style={{ padding: "40px 4px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 12, background: theme.teal, boxShadow: `0 0 10px ${theme.teal}`, animation: "ry-blink 1.2s ease-in-out infinite" }} />
                  <div style={{ fontSize: 18, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.02em" }}>
                    Designing your workout…
                  </div>
                </div>
                {(() => {
                  const cls = aiGenReadiness?.classification;
                  const inputs = smartCtx?.readinessInputs;
                  const acwr = inputs?.training?.acwr;
                  const lastW = inputs?.training?.lastWorkout;
                  const items = [
                    { label: "Readiness", value: cls ? `${cls}` : "—" },
                    { label: "Goal",      value: aiGenGoal },
                    { label: "Duration",  value: `${aiGenDuration}m` },
                    { label: "Equipment", value: aiGenEquip },
                    acwr != null ? { label: "ACWR", value: acwr.toFixed(2) } : null,
                    lastW?.hoursAgo != null ? { label: "Since last", value: `${Math.round(lastW.hoursAgo)}h` } : null,
                  ].filter(Boolean);
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {items.map((it, i) => (
                        <div key={i} style={{
                          display: "flex", justifyContent: "space-between", alignItems: "baseline",
                          padding: "8px 12px", borderRadius: 8,
                          background: theme.bg2, border: `1px solid ${theme.line}`,
                          animation: `ry-fadein 320ms cubic-bezier(.22,.8,.36,1) ${i * 80}ms both`,
                        }}>
                          <span style={{ fontSize: 10.5, color: theme.ink4, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 }}>
                            ✓ {it.label}
                          </span>
                          <span style={{ fontSize: 12.5, color: theme.ink1, fontWeight: 600, fontFamily: "JetBrains Mono, ui-monospace, monospace" }}>
                            {it.value}
                          </span>
                        </div>
                      ))}
                      <div style={{
                        marginTop: 10, padding: "8px 12px", borderRadius: 8,
                        background: `${theme.teal}10`, border: `1px solid ${theme.teal}33`,
                        fontSize: 11, color: theme.teal, letterSpacing: "0.04em",
                        animation: `ry-fadein 320ms cubic-bezier(.22,.8,.36,1) ${items.length * 80}ms both`,
                      }}>
                        Considering muscle recency, last 5 sessions, last-performance numbers, and training-day macros…
                      </div>
                    </div>
                  );
                })()}
                <style>{`
                  @keyframes ry-fadein { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
                  @keyframes ry-blink  { 0%,100% { opacity: 1 } 50% { opacity: 0.3 } }
                `}</style>
              </div>
            ) : (
              <>
                {/* Readiness banner — terms in rationale tappable for glossary */}
                {(() => {
                  const cls = aiGenReadiness?.classification;
                  const clsColor = cls === "HARD" ? theme.teal : cls === "MEDIUM" ? theme.teal : cls === "EASY" ? theme.amber : cls === "REST" ? theme.coral : theme.ink4;
                  const clsLabel = { HARD: "Peak day — go hard", MEDIUM: "Good to train", EASY: "Light session recommended", REST: "Rest day — recover" };
                  // Glossary-aware rationale: split on word boundaries and
                  // wrap any token that matches a known driver entry.
                  // Stable list of regexes derived from driverGlossary.
                  function renderRationale(text) {
                    if (!text) return null;
                    const TERMS = [
                      { rx: /\bACWR\b/g,                   key: "ACWR" },
                      { rx: /\bHRV\b/g,                    key: "HRV" },
                      { rx: /\bRHR\b/g,                    key: "RHR" },
                      { rx: /\bsleep debt\b/gi,            key: "sleep debt" },
                      { rx: /\bovernight delta\b/gi,       key: "overnight weight" },
                      { rx: /\bcarbohydrates?\b/gi,        key: "carbs" },
                      { rx: /\bmax\s?HR\b/gi,              key: "max HR" },
                    ];
                    // Collect all match positions, sort, slice.
                    const matches = [];
                    for (const t of TERMS) {
                      t.rx.lastIndex = 0;
                      let m;
                      while ((m = t.rx.exec(text)) !== null) {
                        matches.push({ start: m.index, end: m.index + m[0].length, label: m[0], key: t.key });
                      }
                    }
                    matches.sort((a, b) => a.start - b.start);
                    // De-overlap (first wins)
                    const final = [];
                    let lastEnd = 0;
                    for (const m of matches) {
                      if (m.start < lastEnd) continue;
                      final.push(m); lastEnd = m.end;
                    }
                    const parts = [];
                    let cursor = 0;
                    final.forEach((m, i) => {
                      if (m.start > cursor) parts.push({ text: text.slice(cursor, m.start) });
                      parts.push({ text: m.label, term: m.key });
                      cursor = m.end;
                    });
                    if (cursor < text.length) parts.push({ text: text.slice(cursor) });
                    return parts.map((p, i) => {
                      if (p.term) {
                        return (
                          <button key={i}
                            onClick={() => setExpandedRationaleTerm(t => t === p.term ? null : p.term)}
                            style={{
                              padding: 0, margin: 0, background: "transparent", border: "none",
                              borderBottom: `1px dotted ${clsColor}aa`,
                              color: theme.ink2, font: "inherit", cursor: "pointer",
                            }}>
                            {p.text}
                          </button>
                        );
                      }
                      return <Fragment key={i}>{p.text}</Fragment>;
                    });
                  }
                  const expl = expandedRationaleTerm ? getDriverExplanation(expandedRationaleTerm) : null;
                  return (
                    <div style={{ marginBottom: 24, padding: "14px 16px", borderRadius: 14, background: cls ? clsColor + "12" : theme.bg2, border: `1px solid ${cls ? clsColor + "35" : theme.line}`, display: "flex", alignItems: "flex-start", gap: 12 }}>
                      {aiGenReadinessLoading ? (
                        <>
                          <div style={{ width: 8, height: 8, borderRadius: 8, background: theme.teal, flexShrink: 0, marginTop: 4, animation: "ry-blink 1.2s ease-in-out infinite" }} />
                          <span style={{ fontSize: 13, color: theme.ink3 }}>Analysing your readiness…</span>
                        </>
                      ) : cls ? (
                        <>
                          <div style={{ width: 10, height: 10, borderRadius: 10, background: clsColor, flexShrink: 0, marginTop: 6, boxShadow: `0 0 6px ${clsColor}` }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: clsColor, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 3 }}>{cls} · {clsLabel[cls]}</div>
                            {aiGenReadiness.feelNote && (
                              <div style={{ fontSize: 12.5, color: clsColor, lineHeight: 1.55, marginBottom: 6, fontWeight: 600 }}>{aiGenReadiness.feelNote}</div>
                            )}
                            {aiGenReadiness.rationale && (
                              <div style={{ fontSize: 12, color: theme.ink3, lineHeight: 1.6 }}>
                                {aiGenReadiness.feelNote && <span style={{ color: theme.ink4, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>OBJECTIVE READ · </span>}
                                {renderRationale(aiGenReadiness.rationale)}
                              </div>
                            )}
                            {expl && (
                              <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "rgba(255,255,255,0.04)", border: `1px solid ${theme.line}` }}>
                                <div style={{ fontSize: 9, fontWeight: 700, color: clsColor, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 4 }}>{expl.title}</div>
                                <div style={{ fontSize: 11.5, color: theme.ink3, lineHeight: 1.55 }}>
                                  {expl.body.replace(/\*\*/g, "")}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <span style={{ fontSize: 12, color: theme.ink4 }}>No readiness data — workout will use default volume.</span>
                      )}
                    </div>
                  );
                })()}

                {/* REST-day flow: replace the picker with recovery options */}
                {aiGenReadiness?.classification === "REST" && (() => {
                  const cards = [
                    { key: "walk", emoji: "🚶", title: "30 min walk", body: "Easy zone-2 cardio. Promotes recovery, doesn't tap the CNS.", action: "Log walk" },
                    { key: "mobility", emoji: "🧘", title: "Mobility / stretching", body: "10–15 min joint-by-joint flow. Improves recovery without loading.", action: "Open mobility" },
                    { key: "rest", emoji: "🛌", title: "Full rest day", body: "Recovery IS training. Sleep, hydrate, eat protein. Come back stronger tomorrow.", action: "Log rest day" },
                  ];
                  return (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: theme.coral, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 12 }}>Recovery options</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {cards.map((c, i) => (
                          <button key={i}
                            onClick={() => { try { hapticHayahSelection(); } catch {}
                              if (c.key === "mobility") {
                                setAiGenOpen(false);
                                setMobilityLogged(false);
                                setMobilityOpen(true);
                                // Only kick off a fresh generation if there isn't
                                // already one running and we have no cached plan.
                                // Otherwise the modal opens onto the existing state.
                                if (!isAiJobInFlight(MOBILITY_CACHE_KEY) && !mobilityResult) {
                                  handleMobilityGenerate();
                                }
                                return;
                              }
                              // Walk + Rest day: not wired yet — just close.
                              setAiGenOpen(false);
                            }}
                            style={{
                              padding: "14px 16px", borderRadius: 14, border: `1px solid ${theme.line}`,
                              background: theme.bg2, cursor: "pointer", fontFamily: "inherit",
                              display: "flex", alignItems: "center", gap: 14, textAlign: "left",
                            }}>
                            <div style={{ fontSize: 26 }}>{c.emoji}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, color: theme.ink1, marginBottom: 2 }}>{c.title}</div>
                              <div style={{ fontSize: 11.5, color: theme.ink3, lineHeight: 1.5 }}>{c.body}</div>
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: theme.teal, letterSpacing: "0.04em" }}>→</div>
                          </button>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: theme.ink4, marginTop: 12, textAlign: "center", lineHeight: 1.55 }}>
                        Need to train anyway? Scroll down — you can still pick goal/duration/equipment.
                      </div>
                    </div>
                  );
                })()}

                {/* Pre-workout fuel reminder when low on carbs going into a hard day */}
                {smartCtx?.fuelReminder && (
                  <button onClick={() => { try { hapticHayahSelection(); } catch {} setFuelOpen(true); }}
                    style={{
                      width: "100%", marginBottom: 20, padding: "12px 14px", borderRadius: 12,
                      border: `1px solid ${theme.amber}44`, background: `${theme.amber}10`,
                      cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                      display: "flex", alignItems: "center", gap: 12,
                    }}>
                    <div style={{ fontSize: 22 }}>🥣</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: theme.amber, letterSpacing: "0.06em" }}>Fuel up first</div>
                      <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1, lineHeight: 1.45 }}>
                        Low carbs on board for a {aiGenReadiness?.classification?.toLowerCase() || "training"} session. {PRE_WORKOUT_SNACK}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: theme.amber, letterSpacing: "0.08em" }}>LOG IT</span>
                  </button>
                )}

                {/* GOAL — chips annotated with muscle staleness + smart-pick tag */}
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: theme.ink4, letterSpacing: "0.18em", textTransform: "uppercase" }}>Goal</span>
                  {smartCtx?.suggestedGoal && (
                    <span style={{ fontSize: 9, color: theme.teal, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 }}>
                      ✦ suggested: {smartCtx.suggestedGoal}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
                  {["Full Body", "Upper Body", "Lower Body", "Push", "Pull", "Legs", "Cardio & Core"].map(g => {
                    const isSel  = aiGenGoal === g;
                    const isSugg = smartCtx?.suggestedGoal === g;
                    const info   = smartCtx?.stalenessMap?.[g];
                    // Recency annotation: show freshest muscle in the goal
                    // (the one most recently trained — the overtrain risk).
                    let recencyLabel = null;
                    if (info?.freshest && info.freshest.daysAgo <= 7) {
                      const m = info.freshest.muscle;
                      const d = info.freshest.daysAgo;
                      const warn = d <= 1;
                      recencyLabel = { text: `${m} ${d}d`, warn };
                    } else if (info && !info.freshest) {
                      recencyLabel = { text: "all fresh", warn: false };
                    }
                    return (
                      <button key={g} onClick={() => { setAiGenGoal(g); setUserTouched(t => ({ ...t, goal: true })); }}
                        style={{
                          padding: "8px 14px 9px", borderRadius: 18, border: isSugg && !isSel ? `1px dashed ${theme.teal}77` : "none",
                          cursor: "pointer", fontFamily: "inherit",
                          background: isSel ? theme.teal : theme.bg3, color: isSel ? theme.bg0 : theme.ink3,
                          display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2,
                          minWidth: 0,
                        }}>
                        <span style={{ fontSize: 13.5, fontWeight: isSel ? 700 : 500, letterSpacing: "-0.005em" }}>{g}</span>
                        {recencyLabel && (
                          <span style={{
                            fontSize: 9, lineHeight: 1.1,
                            color: isSel
                              ? (recencyLabel.warn ? theme.coral : theme.bg0)
                              : (recencyLabel.warn ? theme.coral : theme.ink4),
                            opacity: isSel ? (recencyLabel.warn ? 1 : 0.85) : 1,
                            letterSpacing: "0.04em",
                          }}>
                            {recencyLabel.warn ? "⚠ " : ""}{recencyLabel.text}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Customise which exercises the generator may pick for this
                    goal — like / hide, with how-to videos. */}
                {(() => {
                  const _picks = (picksTick, loadExercisePicks());
                  const likedN = Object.keys(_picks.liked).length;
                  const hiddenN = Object.keys(_picks.hidden).length;
                  return (
                    <button
                      onClick={() => { try { hapticTap(); } catch {} setExercisePickerOpen(true); }}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", marginBottom: 18, padding: "11px 14px", borderRadius: 12, border: `1px solid ${theme.line}`, background: theme.bg2, cursor: "pointer", fontFamily: "inherit" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14 }}>🎚️</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: theme.ink2 }}>Customise {aiGenGoal} exercises</span>
                      </span>
                      <span style={{ fontSize: 11, color: theme.ink4 }}>
                        {likedN || hiddenN ? `${likedN} liked${hiddenN ? ` · ${hiddenN} hidden` : ""} →` : "Pick favourites →"}
                      </span>
                    </button>
                  );
                })()}

                {/* DURATION — smart-default badge on the suggested chip */}
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: theme.ink4, letterSpacing: "0.18em", textTransform: "uppercase" }}>Duration</span>
                  {smartCtx?.suggestedDuration && (
                    <span style={{ fontSize: 9, color: theme.amber, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 }}>
                      ✦ suggested: {smartCtx.suggestedDuration}m{new Date().getHours() >= 21 ? " · evening wind-down" : ""}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
                  {[30, 45, 60, 75].map(d => {
                    const isSel  = aiGenDuration === d;
                    const isSugg = smartCtx?.suggestedDuration === d;
                    return (
                      <button key={d} onClick={() => { setAiGenDuration(d); setUserTouched(t => ({ ...t, duration: true })); }}
                        style={{
                          flex: 1, padding: "12px 0", borderRadius: 12,
                          border: isSugg && !isSel ? `1px dashed ${theme.amber}77` : "none",
                          cursor: "pointer", fontFamily: "inherit", fontSize: 14,
                          fontWeight: isSel ? 700 : 500,
                          background: isSel ? theme.amber : theme.bg3,
                          color: isSel ? theme.bg0 : theme.ink3,
                        }}>{d}m</button>
                    );
                  })}
                </div>

                {/* EQUIPMENT — last-30d usage badges + smart default */}
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: theme.ink4, letterSpacing: "0.18em", textTransform: "uppercase" }}>Equipment</span>
                  {smartCtx?.suggestedEquip && (
                    <span style={{ fontSize: 9, color: theme.sage, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 }}>
                      ✦ suggested: {smartCtx.suggestedEquip}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
                  {["Full Gym", "Dumbbells Only", "Bodyweight"].map(eq => {
                    const isSel  = aiGenEquip === eq;
                    const isSugg = smartCtx?.suggestedEquip === eq;
                    const usedN  = smartCtx?.last30?.[eq] || 0;
                    return (
                      <button key={eq} onClick={() => { setAiGenEquip(eq); setUserTouched(t => ({ ...t, equip: true })); }}
                        style={{
                          flex: 1, padding: "10px 6px 9px", borderRadius: 12,
                          border: isSugg && !isSel ? `1px dashed ${theme.sage}77` : "none",
                          cursor: "pointer", fontFamily: "inherit",
                          background: isSel ? theme.sage : theme.bg3,
                          color: isSel ? theme.bg0 : theme.ink3,
                          display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                        }}>
                        <span style={{ fontSize: 13, fontWeight: isSel ? 700 : 500, lineHeight: 1.15 }}>{eq}</span>
                        {usedN > 0 && (
                          <span style={{ fontSize: 9, color: isSel ? theme.bg0 : theme.ink4, opacity: isSel ? 0.7 : 1, letterSpacing: "0.04em" }}>
                            {usedN} in 30d
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Pre-flight preview — sets expectations before user taps Generate */}
                {(() => {
                  const weightKg = realBodyMassKg();
                  const cls = aiGenReadiness?.classification;
                  const preview = previewEstimate({ duration: aiGenDuration, goal: aiGenGoal, readinessClass: cls, weightKg });
                  const cost = estimateGenerationCostUSD();
                  return (
                    <div style={{ marginBottom: 20, padding: "12px 14px", borderRadius: 12, background: theme.bg2, border: `1px solid ${theme.line}` }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: theme.ink4, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 8 }}>
                        What you'll get · estimate
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                        {[
                          { k: "Exercises", v: preview.exerciseCount },
                          { k: "Blocks",    v: preview.blockCount },
                          { k: "Effort",    v: `${preview.estEffortMin}m` },
                          { k: "Est. burn", v: `${preview.estBurnKcal}` },
                        ].map((it, i) => (
                          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                            <span style={{ fontSize: 8.5, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase" }}>{it.k}</span>
                            <span style={{ fontSize: 14, fontWeight: 700, color: theme.ink1, fontFamily: "JetBrains Mono, ui-monospace, monospace" }}>{it.v}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: 9.5, color: theme.ink4, marginTop: 10, letterSpacing: "0.04em" }}>
                        Generation cost ≈ ${cost.toFixed(4)} · uses Flash-lite for speed
                      </div>
                    </div>
                  );
                })()}

                {/* Recent AI workouts — quick re-run / fork */}
                {(() => {
                  const recent = loadHistory().filter(w => /^ai\b|^ai\s|^✦|AI\s/.test(w.aiName || w.name || "")).slice(0, 2);
                  if (recent.length === 0) return null;
                  return (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: theme.ink4, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 8 }}>
                        Recent AI workouts
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {recent.map((w, i) => {
                          const mins = w.duration ? Math.round(w.duration / 60) : null;
                          const daysAgo = w.timestamp ? Math.max(0, Math.round((Date.now() - w.timestamp) / 86400000)) : null;
                          return (
                            <button key={i}
                              onClick={() => { try { hapticHayahSelection(); } catch {} handleAiGenerate(); }}
                              style={{
                                padding: "10px 12px", borderRadius: 10, border: `1px solid ${theme.line}`,
                                background: theme.bg3, cursor: "pointer", fontFamily: "inherit",
                                display: "flex", alignItems: "center", gap: 10, textAlign: "left",
                              }}>
                              <span style={{ fontSize: 16 }}>✦</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.ink1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {w.aiName || w.name}
                                </div>
                                <div style={{ fontSize: 10, color: theme.ink4, marginTop: 1 }}>
                                  {daysAgo === 0 ? "today" : daysAgo === 1 ? "yesterday" : `${daysAgo}d ago`}{mins ? ` · ${mins}m` : ""}{w.sets ? ` · ${w.sets} sets` : ""}
                                </div>
                              </div>
                              <span style={{ fontSize: 10, fontWeight: 700, color: theme.teal, letterSpacing: "0.08em" }}>RE-RUN</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {aiGenError && (
                  <div style={{ marginBottom: aiGenRawDebug ? 8 : 16, padding: "12px 16px", borderRadius: 12, background: `${theme.coral}20`, border: `1px solid ${theme.coral}50`, color: theme.coral, fontSize: 13 }}>{aiGenError}</div>
                )}
                {aiGenRawDebug && (
                  <div style={{ marginBottom: 16, borderRadius: 10, overflow: "hidden", border: `1px solid ${theme.line}` }}>
                    <div style={{ padding: "6px 10px", background: theme.bg3, fontSize: 10, fontWeight: 700, color: theme.ink4, letterSpacing: "0.15em", textTransform: "uppercase", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>Raw Gemini Response</span>
                      <span style={{ fontWeight: 400 }}>{aiGenRawDebug.length} chars</span>
                    </div>
                    <div style={{ padding: "10px 12px", background: theme.bg2, maxHeight: 200, overflowY: "auto", fontFamily: "monospace", fontSize: 11, color: theme.ink2, whiteSpace: "pre-wrap", wordBreak: "break-all", lineHeight: 1.5 }}>
                      {aiGenRawDebug}
                    </div>
                  </div>
                )}
              </>
            )}
            <div style={{ height: 120 }} />
          </div>

          {/* Fixed bottom action area */}
          <div style={{ padding: "12px 20px", borderTop: `0.5px solid ${theme.line}`, flexShrink: 0, background: theme.bg0 }}>
            {aiGenResult?.skipped ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button
                  onClick={() => { try { hapticConfirm(); } catch {} setAiGenOpen(false); setAiGenResult(null); }}
                  style={{ width: "100%", padding: "16px", borderRadius: 14, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 15, fontWeight: 700, background: theme.bg3, color: theme.ink2, letterSpacing: "0.2px" }}
                >
                  Got it
                </button>
                <button
                  onClick={() => { try { hapticTap(); } catch {} setAiGenResult(null); handleAiGenerate(); }}
                  style={{ width: "100%", padding: "13px", borderRadius: 14, border: `1px solid ${theme.teal}44`, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: "transparent", color: theme.teal }}
                >
                  Generate anyway
                </button>
              </div>
            ) : aiGenResult ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button
                  onClick={() => {
                    try { hapticConfirm(); } catch {}
                    // Stamp the chosen equipment mode + the planned exercise
                    // ids onto the template so the finished session can record
                    // swaps/skips against the AI's plan — by id, not by
                    // re-deriving from exercise names with regex.
                    const planned = (aiGenResult.template.exercises_list || []).map(e => e.id).filter(Boolean);
                    const t = { ...aiGenResult.template, source: "ai", equipmentMode: aiGenEquip, plannedExerciseIds: planned };
                    setAiGenOpen(false); setAiGenResult(null); onPick(t);
                  }}
                  style={{ width: "100%", padding: "16px", borderRadius: 14, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 15, fontWeight: 700, background: theme.teal, color: theme.bg0, letterSpacing: "0.2px" }}
                >
                  Start Workout
                </button>
                <button
                  onClick={() => { try { hapticTap(); } catch {} setAiGenResult(null); }}
                  style={{ width: "100%", padding: "13px", borderRadius: 14, border: `1px solid ${theme.border}`, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: "transparent", color: theme.ink3 }}
                >
                  Regenerate
                </button>
              </div>
            ) : (
              <button
                onClick={() => { try { hapticConfirm(); } catch {} handleAiGenerate(); }}
                disabled={aiGenLoading}
                style={{ width: "100%", padding: "16px", borderRadius: 14, border: "none", cursor: aiGenLoading ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: 15, fontWeight: 700, background: aiGenLoading ? theme.bg3 : theme.teal, color: aiGenLoading ? theme.ink3 : theme.bg0, letterSpacing: "0.2px" }}
              >
                {aiGenLoading ? "Generating…" : "Generate Workout"}
              </button>
            )}
          </div>
        </div>,
        document.body)}

      {exercisePickerOpen && createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 210, background: theme.bg0, display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px 10px", borderBottom: `0.5px solid ${theme.line}`, flexShrink: 0 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.02em" }}>{aiGenGoal} exercises</div>
              <div style={{ fontSize: 11, color: theme.ink4, marginTop: 1 }}>Like the ones to favour · hide the ones to skip · ▶ how-to</div>
            </div>
            <button onClick={() => { try { hapticTap(); } catch {} setExercisePickerOpen(false); }} style={{ background: theme.bg3, border: "none", borderRadius: 20, padding: "7px 14px", color: theme.ink2, fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>Done</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", borderBottom: `0.5px solid ${theme.line}`, flexShrink: 0 }}>
            <span style={{ fontSize: 12, color: theme.ink3 }}>Show ▶ how-to links on workouts</span>
            <button
              onClick={() => { try { hapticTap(); } catch {} setShowHowToLinks(!showHowToLinks()); setPicksTick(t => t + 1); }}
              style={{ width: 44, height: 26, borderRadius: 13, border: "none", cursor: "pointer", background: (picksTick, showHowToLinks()) ? theme.teal : theme.bg3, position: "relative", flexShrink: 0 }}>
              <span style={{ position: "absolute", top: 3, left: showHowToLinks() ? 21 : 3, width: 20, height: 20, borderRadius: 10, background: "#fff", transition: "left 0.15s" }} />
            </button>
          </div>
          <div className="ry-sb" style={{ flex: 1, overflowY: "auto", padding: "8px 16px 24px" }}>
            {(() => {
              const GM = {
                "Upper Body": ["Chest","Back","Shoulders","Triceps","Biceps"], "Push": ["Chest","Shoulders","Triceps"],
                "Pull": ["Back","Biceps"], "Lower Body": ["Legs","Glutes"], "Legs": ["Legs","Glutes"],
                "Cardio & Core": ["Core","Cardio"], "Full Body": ["Chest","Back","Shoulders","Legs","Glutes","Triceps","Biceps","Core"],
              };
              const gm = GM[aiGenGoal] || null;
              const eq = aiGenEquip;
              const cands = EXERCISE_LIBRARY.filter(e => {
                if (eq === "Bodyweight" && e.category !== "Bodyweight" && e.category !== "Cardio") return false;
                if (eq === "Dumbbells Only" && e.category !== "Free weights" && e.category !== "Bodyweight" && e.category !== "Cardio") return false;
                if (gm && !gm.includes(e.primary)) return false;
                return true;
              });
              const picks = (picksTick, loadExercisePicks());
              // Group by primary muscle for readability.
              const groups = {};
              for (const e of cands) (groups[e.primary] = groups[e.primary] || []).push(e);
              return Object.entries(groups).map(([muscle, list]) => (
                <div key={muscle} style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: theme.ink4, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 6 }}>{muscle}</div>
                  {list.map(e => {
                    const liked = !!picks.liked[e.id];
                    const hidden = !!picks.hidden[e.id];
                    return (
                      <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 2px", borderBottom: `0.5px solid ${theme.line}`, opacity: hidden ? 0.5 : 1 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: theme.ink1, textDecoration: hidden ? "line-through" : "none" }}>{e.name}</div>
                          <div style={{ fontSize: 11, color: theme.ink4 }}>{[e.primary, ...(e.secondary || [])].join(" · ")}</div>
                        </div>
                        <button title="Watch how-to" onClick={() => { try { hapticTap(); } catch {} window.open(youtubeHowToUrl(e.name), "_blank"); }}
                          style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 17, border: `1px solid ${theme.line}`, background: theme.bg2, color: theme.coral, fontSize: 13, cursor: "pointer" }}>▶</button>
                        <button title="Hide" onClick={() => { try { hapticTap(); } catch {} toggleHiddenExercise(e.id); setPicksTick(t => t + 1); }}
                          style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 17, border: `1px solid ${hidden ? theme.coral : theme.line}`, background: hidden ? `${theme.coral}22` : theme.bg2, color: hidden ? theme.coral : theme.ink4, fontSize: 14, cursor: "pointer" }}>⊘</button>
                        <button title="Like" onClick={() => { try { hapticHayahSelection(); } catch {} toggleLikedExercise(e.id); setPicksTick(t => t + 1); }}
                          style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 17, border: `1px solid ${liked ? theme.teal : theme.line}`, background: liked ? theme.teal : theme.bg2, color: liked ? theme.bg0 : theme.ink4, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>{liked ? "♥" : "♡"}</button>
                      </div>
                    );
                  })}
                </div>
              ));
            })()}
          </div>
        </div>,
        document.body)}

      {treadmillOpen && createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 200, background: theme.bg0, display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px 10px", borderBottom: `0.5px solid ${theme.line}`, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>🏃</span>
              <span style={{ fontSize: 17, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.02em" }}>Home Treadmill</span>
            </div>
            <button onClick={() => { setTreadmillOpen(false); setTreadmillResult(null); setTreadmillError(null); }} style={{ background: theme.bg3, border: "none", borderRadius: 20, padding: "7px 14px", color: theme.ink3, fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Close</button>
          </div>
          <div className="ry-sb" style={{ flex: 1, overflowY: "auto", padding: "20px 20px 0" }}>
            {treadmillResult ? (
              <>
                <div style={{ fontSize: 13, color: theme.ink4, marginBottom: 20 }}>{treadmillResult.duration_min} min · Treadmill · AI generated</div>
                {treadmillResult.rationale && (
                  <div style={{ padding: "14px 16px", borderRadius: 14, background: `${theme.amber}10`, border: `1px solid ${theme.amber}28`, marginBottom: 20 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: theme.amber, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 8 }}>Plan</div>
                    <div style={{ fontSize: 14, color: theme.ink2, lineHeight: 1.7 }}>{treadmillResult.rationale}</div>
                  </div>
                )}
                <div>
                  {(treadmillResult.blocks || []).map((b, i) => {
                    const typeColors = { warmup: theme.sage, steady: theme.teal, interval_work: theme.coral, interval_rest: theme.ink4, cooldown: theme.sage };
                    const typeLabels = { warmup: "WARM-UP", steady: "STEADY STATE", interval_work: "INTERVAL", interval_rest: "RECOVERY", cooldown: "COOL-DOWN" };
                    const c = typeColors[b.type] || theme.ink3;
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `0.5px solid ${theme.line}` }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: c + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: c, flexShrink: 0 }}>
                          {b.minutes}m
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="ry-mono" style={{ fontSize: 10, fontWeight: 700, color: c, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 3 }}>{typeLabels[b.type] || b.type}</div>
                          {(b.speed_kmh != null || b.incline_pct != null) && (
                            <div className="ry-mono" style={{ fontSize: 13, color: theme.ink2 }}>
                              {[b.speed_kmh != null ? `${b.speed_kmh} km/h` : null, b.incline_pct != null ? `${b.incline_pct}% incline` : null].filter(Boolean).join(" · ")}
                            </div>
                          )}
                          {b.notes && <div style={{ fontSize: 11, color: theme.ink4, marginTop: 2 }}>{b.notes}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ height: 100 }} />
              </>
            ) : treadmillError ? (
              <>
                <div style={{ padding: "14px 16px", borderRadius: 12, background: `${theme.coral}20`, border: `1px solid ${theme.coral}50`, color: theme.coral, fontSize: 13 }}>{treadmillError}</div>
                <div style={{ height: 80 }} />
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, color: theme.ink3, lineHeight: 1.7 }}>Gemini will design a 30-minute structured treadmill session, adjusted for today's training readiness and Mounjaro cycle day.</div>
                <div style={{ height: 80 }} />
              </>
            )}
          </div>
          <div style={{ padding: "12px 20px", borderTop: `0.5px solid ${theme.line}`, flexShrink: 0, background: theme.bg0 }}>
            {treadmillResult ? (
              <button onClick={() => { try { hapticTap(); } catch {} setTreadmillResult(null); }} style={{ width: "100%", padding: "14px", borderRadius: 14, border: `1px solid ${theme.border}`, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: "transparent", color: theme.ink3 }}>Regenerate</button>
            ) : (
              <button onClick={() => { try { hapticConfirm(); } catch {} handleTreadmillGenerate(); }} disabled={treadmillLoading} style={{ width: "100%", padding: "16px", borderRadius: 14, border: "none", cursor: treadmillLoading ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: 15, fontWeight: 700, background: treadmillLoading ? theme.bg3 : theme.amber, color: treadmillLoading ? theme.ink3 : theme.bg0, letterSpacing: "0.2px" }}>
                {treadmillLoading ? "Planning…" : "Generate Treadmill Plan"}
              </button>
            )}
          </div>
        </div>,
        document.body)}

      {mobilityOpen && createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 200, background: theme.bg0, display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px 10px", borderBottom: `0.5px solid ${theme.line}`, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <span style={{ fontSize: 18 }}>🧘</span>
              <span style={{ fontSize: 17, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {mobilityResult?.title || "Mobility & Stretching"}
              </span>
            </div>
            <button onClick={() => { setMobilityOpen(false); }} style={{ background: theme.bg3, border: "none", borderRadius: 20, padding: "7px 14px", color: theme.ink3, fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0, marginLeft: 10 }}>Close</button>
          </div>
          <div className="ry-sb" style={{ flex: 1, overflowY: "auto", padding: "20px 20px 0" }}>
            {mobilityLoading ? (
              <div style={{ paddingTop: 4 }}>
                <AIAnalysingPipeline
                  label="Designing mobility flow"
                  computeLabel="Synthesising"
                  feature="mobility-flow"
                  accent="teal"
                  theme={theme}
                />
              </div>
            ) : mobilityError ? (
              <>
                <div style={{ padding: "14px 16px", borderRadius: 12, background: `${theme.coral}20`, border: `1px solid ${theme.coral}50`, color: theme.coral, fontSize: 13, lineHeight: 1.55 }}>{mobilityError}</div>
                <div style={{ height: 80 }} />
              </>
            ) : mobilityResult ? (
              <>
                {(() => {
                  const total = (mobilityResult.stretches || []).length;
                  const doneN = mobilityDone.size;
                  const allDone = total > 0 && doneN === total;
                  return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
                      <div style={{ fontSize: 13, color: theme.ink4 }}>
                        {mobilityResult.duration_min || 12} min · {total} stretches
                      </div>
                      <div className="ry-mono" style={{
                        fontSize: 11, fontWeight: 700,
                        color: allDone ? theme.teal : theme.ink3,
                        letterSpacing: "0.06em",
                      }}>
                        {doneN}/{total} done{allDone ? " ✓" : ""}
                      </div>
                    </div>
                  );
                })()}
                {mobilityResult.intro && (
                  <div style={{ padding: "14px 16px", borderRadius: 14, background: `${theme.sage}10`, border: `1px solid ${theme.sage}28`, marginBottom: 20 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: theme.sage, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 8 }}>Focus</div>
                    <div style={{ fontSize: 14, color: theme.ink2, lineHeight: 1.7 }}>{mobilityResult.intro}</div>
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {(mobilityResult.stretches || []).map((s, i) => {
                    const sides = s.sides && s.sides !== "n/a"
                      ? (s.sides === "both" ? " · each side" : " · one side")
                      : "";
                    const sets = s.sets && s.sets > 1 ? ` · ${s.sets} rounds` : "";
                    const done = mobilityDone.has(i);
                    return (
                      <div key={i} style={{
                        padding: "16px 16px 14px", borderRadius: 14,
                        background: done ? `${theme.teal}0c` : theme.bg2,
                        border: `1px solid ${done ? theme.teal + "55" : theme.line}`,
                        transition: "background .2s, border-color .2s",
                        opacity: done ? 0.85 : 1,
                      }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 3 }}>
                              <button
                                onClick={() => toggleStretchDone(i)}
                                aria-label={done ? `Mark ${s.name} as not done` : `Mark ${s.name} as done`}
                                style={{
                                  width: 30, height: 30, borderRadius: "50%",
                                  border: `1.5px solid ${done ? theme.teal : theme.line}`,
                                  background: done ? theme.teal : "transparent",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  cursor: "pointer", flexShrink: 0, padding: 0,
                                  transition: "all .2s",
                                  WebkitTapHighlightColor: "transparent",
                                }}
                              >
                                {done
                                  ? <Icon name="check" size={14} color="#0A0A0E"/>
                                  : <span style={{ fontSize: 12, fontWeight: 700, color: theme.teal, fontFamily: "inherit" }}>{i + 1}</span>}
                              </button>
                              <div style={{
                                fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em",
                                color: done ? theme.ink3 : theme.ink1,
                                textDecoration: done ? "line-through" : "none",
                                textDecorationColor: done ? theme.teal : undefined,
                              }}>{s.name}</div>
                            </div>
                            {s.target && (
                              <div style={{ fontSize: 11, color: theme.ink4, marginLeft: 40, letterSpacing: "0.03em", textTransform: "uppercase", fontWeight: 600 }}>{s.target}</div>
                            )}
                          </div>
                          <div className="ry-mono" style={{ fontSize: 12, fontWeight: 700, color: theme.amber, flexShrink: 0, textAlign: "right", lineHeight: 1.3 }}>
                            {s.duration_sec}s{sides}{sets}
                          </div>
                        </div>
                        {(s.cues || []).length > 0 && (
                          <ol style={{ margin: "10px 0 0", paddingLeft: 22, color: theme.ink2, fontSize: 13.5, lineHeight: 1.6 }}>
                            {(s.cues || []).map((c, j) => (
                              <li key={j} style={{ marginBottom: 4 }}>{c}</li>
                            ))}
                          </ol>
                        )}
                        {s.breath && (
                          <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, background: `${theme.teal}10`, border: `1px solid ${theme.teal}28`, display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 14 }}>🌬</span>
                            <span style={{ fontSize: 12, color: theme.ink2, lineHeight: 1.5 }}>{s.breath}</span>
                          </div>
                        )}
                        {s.avoid && (
                          <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: `${theme.coral}10`, border: `1px solid ${theme.coral}28`, display: "flex", alignItems: "flex-start", gap: 8 }}>
                            <span style={{ fontSize: 12, color: theme.coral, fontWeight: 700, letterSpacing: "0.1em" }}>AVOID</span>
                            <span style={{ fontSize: 12, color: theme.ink2, lineHeight: 1.5, flex: 1 }}>{s.avoid}</span>
                          </div>
                        )}
                        {/* Watch tutorial — search YouTube for the stretch by name.
                            Going through YouTube search (not a specific video ID)
                            because asking Gemini for video URLs always produces
                            hallucinated/dead IDs. Search lands on real videos. */}
                        <button
                          onClick={() => {
                            try { hapticTap(); } catch {}
                            const q = encodeURIComponent(`${s.name} stretch tutorial`);
                            const url = `https://www.youtube.com/results?search_query=${q}`;
                            if (typeof window !== "undefined") window.open(url, "_blank", "noopener,noreferrer");
                          }}
                          style={{
                            marginTop: 10, width: "100%",
                            padding: "9px 12px", borderRadius: 10,
                            border: `1px solid ${theme.amber}55`,
                            background: `${theme.amber}10`,
                            cursor: "pointer", fontFamily: "inherit",
                            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                            WebkitTapHighlightColor: "transparent",
                          }}
                        >
                          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 12, color: theme.amber, fontWeight: 700, letterSpacing: "0.04em" }}>▶ Watch on YouTube</span>
                          </span>
                          <svg width="12" height="12" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, color: theme.amber }}>
                            <path d="M7 4h9v9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M16 4 5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
                {mobilityResult.closing_note && (
                  <div style={{ marginTop: 18, padding: "12px 14px", borderRadius: 12, background: theme.bg2, border: `1px solid ${theme.line}`, fontSize: 12.5, color: theme.ink3, lineHeight: 1.6 }}>
                    {mobilityResult.closing_note}
                  </div>
                )}
                <div style={{ height: 100 }} />
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, color: theme.ink3, lineHeight: 1.7 }}>Gemini will design a guided mobility &amp; stretching flow with step-by-step instructions for every move.</div>
                <div style={{ height: 80 }} />
              </>
            )}
          </div>
          <div style={{ padding: "12px 20px", borderTop: `0.5px solid ${theme.line}`, flexShrink: 0, background: theme.bg0, display: "flex", gap: 10 }}>
            {mobilityResult && !mobilityLoading && (
              <>
                <button onClick={() => {
                    try { hapticTap(); } catch {}
                    setMobilityLogged(false);
                    setMobilityDone(new Set());
                    try { localStorage.removeItem(MOBILITY_DONE_KEY); } catch {}
                    handleMobilityGenerate();
                  }}
                  style={{ flex: "0 0 auto", padding: "14px 16px", borderRadius: 14, border: `1px solid ${theme.border}`, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: "transparent", color: theme.ink3 }}>
                  Regenerate
                </button>
                <button onClick={() => { handleMobilityLog(); }}
                  disabled={mobilityLogged}
                  style={{ flex: 1, padding: "14px", borderRadius: 14, border: "none", cursor: mobilityLogged ? "default" : "pointer", fontFamily: "inherit", fontSize: 15, fontWeight: 700, background: mobilityLogged ? theme.bg3 : theme.teal, color: mobilityLogged ? theme.ink2 : theme.bg0, letterSpacing: "0.2px" }}>
                  {mobilityLogged ? "✓ Logged" : "Log session"}
                </button>
              </>
            )}
            {!mobilityResult && !mobilityLoading && (
              <button onClick={() => { try { hapticConfirm(); } catch {} handleMobilityGenerate(); }}
                style={{ width: "100%", padding: "16px", borderRadius: 14, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 15, fontWeight: 700, background: theme.teal, color: theme.bg0, letterSpacing: "0.2px" }}>
                Generate mobility flow
              </button>
            )}
            {mobilityLoading && (
              <button disabled style={{ width: "100%", padding: "16px", borderRadius: 14, border: "none", cursor: "not-allowed", fontFamily: "inherit", fontSize: 15, fontWeight: 700, background: theme.bg3, color: theme.ink3 }}>
                Generating…
              </button>
            )}
          </div>
        </div>,
        document.body)}

      {editing && createPortal(
        <Sheet onClose={() => setEditing(null)} theme={theme}>
          <RoutineBuilder theme={theme} routine={editing}
            onSave={(r) => { upsertRoutine(r); refreshRoutines(); setEditing(null); }}
            onDelete={(id) => { deleteRoutine(id); refreshRoutines(); setEditing(null); }}
            onClose={() => setEditing(null)} />
        </Sheet>,
        document.body)}
    </div>
  );
}

function RoutineBuilder({ theme, routine, onSave, onDelete, onClose }) {
  const [name, setName] = useState(routine.name || "");
  const [emoji, setEmoji] = useState(routine.emoji || "💪");
  const [circuits, setCircuits] = useState(routine.circuits || 3);
  const [list, setList] = useState(routine.exercises_list || []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const isExisting = !!loadRoutines().find(r => r.id === routine.id);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const copy = list.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setList(copy);
    hapticHayahSelection();
  };
  const removeAt = (i) => { setList(list.filter((_, k) => k !== i)); hapticHayahSelection(); };
  const addEx = (ex) => {
    setList([...list, {
      block: "A", id: ex.id, name: ex.name, type: ex.type || "push",
      muscles: ex.muscles || ex.primary || "",
      defaultWeight: ex.defaults?.weight ?? 0,
      defaultReps:   ex.defaults?.reps   ?? 10,
    }]);
    setPickerOpen(false);
  };
  const canSave = name.trim().length > 0 && list.length > 0;
  const handleSave = () => {
    if (!canSave) return;
    hapticHayahSuccess();
    onSave({
      ...routine,
      name: name.trim(), emoji,
      circuits: Math.max(1, Math.min(5, parseInt(circuits, 10) || 3)),
      exercises_list: list,
      desc: `${list.length} exercises · ${circuits} circuits`,
      custom: true,
    });
  };

  return (
    <>
      <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.01em" }}>
          {isExisting ? "Edit routine" : "New routine"}
        </div>
        <button className="ry-btn" onClick={onClose}
          style={{ background: theme.bg3, border: `0.5px solid ${theme.line}`, borderRadius: 8, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: theme.ink3 }}>
          <Icon name="x" size={14}/>
        </button>
      </div>

      <div style={{ padding: "0 16px 12px", display: "flex", gap: 8 }}>
        <input value={emoji} onChange={(e) => setEmoji(e.target.value.slice(0, 2))}
          maxLength={2}
          style={{ width: 50, textAlign: "center", fontSize: 22, padding: "10px 0",
            background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 12, color: theme.ink1 }}/>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Routine name"
          style={{ flex: 1, fontSize: 14, padding: "10px 12px",
            background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 12, color: theme.ink1 }}/>
      </div>

      <div style={{ padding: "0 16px 12px", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 12, color: theme.ink3 }}>Circuits</span>
        <div style={{ display: "flex", gap: 6 }}>
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} className="ry-btn" onClick={() => setCircuits(n)}
              style={{ width: 32, height: 32, borderRadius: 8,
                background: circuits === n ? theme.amber : theme.bg2,
                border: `0.5px solid ${circuits === n ? theme.amber : theme.line}`,
                color: circuits === n ? "#0A0A0E" : theme.ink2, fontSize: 13, fontWeight: 600 }}>{n}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.ink4 }}>
          Exercises · {list.length}
        </span>
        <button className="ry-btn" onClick={() => setPickerOpen(true)}
          style={{ background: theme.amber, color: "#0A0A0E", border: 0, borderRadius: 8, padding: "5px 10px", fontSize: 11, fontWeight: 700 }}>+ Add</button>
      </div>

      <div style={{ padding: "0 16px", maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
        {list.length === 0 && (
          <div style={{ padding: "20px 12px", textAlign: "center", fontSize: 12, color: theme.ink4,
            background: theme.bg2, border: `0.5px dashed ${theme.line}`, borderRadius: 12 }}>
            No exercises yet — tap + Add
          </div>
        )}
        {list.map((ex, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6,
            background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 10, padding: "8px 10px" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.ink1 }}>{ex.name}</div>
              {ex.muscles && <div style={{ fontSize: 10, color: theme.ink4, marginTop: 1 }}>{ex.muscles}</div>}
            </div>
            <button className="ry-btn" onClick={() => move(i, -1)} disabled={i === 0}
              style={{ width: 26, height: 26, borderRadius: 6, background: theme.bg3,
                border: `0.5px solid ${theme.line}`, color: i === 0 ? theme.ink4 : theme.ink2, fontSize: 12 }}>↑</button>
            <button className="ry-btn" onClick={() => move(i, 1)} disabled={i === list.length - 1}
              style={{ width: 26, height: 26, borderRadius: 6, background: theme.bg3,
                border: `0.5px solid ${theme.line}`, color: i === list.length - 1 ? theme.ink4 : theme.ink2, fontSize: 12 }}>↓</button>
            <button className="ry-btn" onClick={() => removeAt(i)} aria-label="Remove"
              style={{ width: 26, height: 26, borderRadius: 6, background: "transparent",
                border: `0.5px solid rgba(217,122,106,0.3)`, color: theme.coral, fontSize: 12 }}>×</button>
          </div>
        ))}
      </div>

      <div style={{ padding: "16px", display: "flex", gap: 8 }}>
        {isExisting && (
          <button className="ry-btn" onClick={() => { try { hapticWarning(); } catch {} onDelete(routine.id); }}
            style={{ background: "transparent", color: theme.coral, border: `0.5px solid rgba(217,122,106,0.3)`,
              borderRadius: 12, padding: "11px 14px", fontSize: 13, fontWeight: 600 }}>Delete</button>
        )}
        <button className="ry-btn" onClick={onClose}
          style={{ flex: 1, background: theme.bg3, color: theme.ink1, border: `0.5px solid ${theme.line}`,
            borderRadius: 12, padding: "11px", fontSize: 13, fontWeight: 500 }}>Cancel</button>
        <button className="ry-btn" onClick={handleSave} disabled={!canSave}
          style={{ flex: 1.4, background: canSave ? theme.amber : theme.bg3,
            color: canSave ? "#0A0A0E" : theme.ink4, border: 0,
            borderRadius: 12, padding: "11px", fontSize: 13, fontWeight: 700 }}>Save</button>
      </div>

      {pickerOpen && createPortal(
        <Sheet onClose={() => setPickerOpen(false)} theme={theme}>
          <RoutineExercisePicker theme={theme} onPick={addEx} onClose={() => setPickerOpen(false)} />
        </Sheet>,
        document.body)}
    </>
  );
}

function RoutineExercisePicker({ theme, onPick, onClose }) {
  const [q, setQ] = useState("");
  const filtered = filterExercises(RIYADAH_PICKER_EXERCISES, q);
  return (
    <>
      <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: theme.ink1 }}>Add exercise</div>
        <button className="ry-btn" onClick={onClose}
          style={{ background: theme.bg3, border: `0.5px solid ${theme.line}`, borderRadius: 8, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: theme.ink3 }}>
          <Icon name="x" size={14}/>
        </button>
      </div>
      <div style={{ padding: "0 16px 10px" }}>
        <input
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoCorrect="on"
          autoCapitalize="none"
          autoComplete="off"
          spellCheck={true}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          placeholder="Search exercises…"
          style={{ width: "100%", fontSize: 16, padding: "12px 14px",
            background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 12,
            color: theme.ink1, WebkitAppearance: "none", appearance: "none" }}/>
      </div>
      <div style={{ padding: "0 16px 16px", maxHeight: 360, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
        {filtered.length === 0 && (
          <div style={{ padding: 16, textAlign: "center", fontSize: 12, color: theme.ink4 }}>No matches</div>
        )}
        {filtered.map(ex => (
          <button key={ex.id} className="ry-btn" onClick={() => onPick(ex)}
            style={{ background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 10,
              padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between",
              color: "inherit", textAlign: "left" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.ink1 }}>{ex.name}</div>
              <div style={{ fontSize: 10, color: theme.ink4, marginTop: 1 }}>{ex.primary || ex.category}</div>
            </div>
            <Icon name="plus" size={14}/>
          </button>
        ))}
      </div>
    </>
  );
}

function NavHd({ theme, title, sub, onBack, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 10px", flexShrink: 0 }}>
      <button className="ry-btn" onClick={onBack}
        style={{ background: "transparent", color: theme.ink2, border: 0, padding: "6px 0", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
        <Icon name="back" size={18}/>
      </button>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.03em" }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: theme.ink3 }}>{sub}</div>}
      </div>
      <div style={{ minWidth: 40, display: "flex", justifyContent: "flex-end" }}>{right}</div>
    </div>
  );
}

function ExBadge({ theme, type }) {
  const map = {
    push:   { bg: theme.amberDim, fg: theme.amber },
    pull:   { bg: theme.tealDim,  fg: theme.teal },
    legs:   { bg: theme.sageDim,  fg: theme.sage },
    cardio: { bg: theme.coralDim, fg: theme.coral },
    core:   { bg: "rgba(158,124,213,0.16)", fg: "#9e7cd5" },
  };
  const c = map[type] || map.push;
  return (
    <span className="ry-mono" style={{
      fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase",
      padding: "3px 7px", borderRadius: 4,
      background: c.bg, color: c.fg,
    }}>{type}</span>
  );
}

// ───────── ActiveScreen ─────────
function ActiveScreen({ theme, template, resume, onFinish, onBack, onDiscard }) {
  const toast = useToast();
  const lastData = useMemo(() => loadLastExerciseData(), []);
  const initial = useMemo(() => buildWorkout(template, lastData), [template, lastData]);
  // When auto-progression is on, pre-fill each exercise's sets with the
  // suggested next progression (last weight/reps + 2.5kg style nudge),
  // so the user sees the suggestion as the default instead of having to
  // tap the "Try" chip.
  const initialExercises = useMemo(() => {
    const list = resume?.exercises || initial.exercises;
    if (resume?.exercises) return list;     // resume keeps user's edits
    if (!autoProgressOn()) return list;
    return list.map(ex => {
      if (!ex.suggestion || ex.isCardio) return ex;
      const { weight, reps } = ex.suggestion;
      return { ...ex, sets: ex.sets.map(s => ({ ...s, weight, reps })), _autoApplied: true };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, resume]);
  const [exercises, setExercises] = useState(initialExercises);
  const [cardio, setCardio]       = useState(resume?.cardio    ?? initial.cardio);
  const [elapsed, setElapsed]     = useState(0);
  const [restTimer, setRestTimer] = useState(null); // { secs, max }
  const [showAddEx, setShowAddEx] = useState(false);
  const [addExQuery, setAddExQuery] = useState("");
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const startRef = useRef(resume?.startTs || Date.now());
  // Absolute end-of-rest timestamp — set when rest begins, used by the
  // Live Activity (which renders a self-ticking timerInterval). Cleared
  // when restTimer drops to null. Independent of the per-second secs
  // decrement so we don't redundantly push to ActivityKit each tick.
  const restEndsAtRef = useRef(null);

  // Detected Apple Watch workout (best-effort, doesn't block UI).
  const [watchActive, setWatchActive] = useState(false);
  useEffect(() => { isWatchAvailable().then(setWatchActive); }, []);

  // Live elapsed.
  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  // Persist the in-progress workout on every change so leaving Riyadah,
  // closing the app, or an iOS WebView reap doesn't lose it. The Riyadah
  // parent reads this back via loadActiveSession() on next mount and jumps
  // straight back into the active screen.
  useEffect(() => {
    saveActiveSession({
      template,
      exercises,
      cardio,
      startTs: startRef.current,
    });
  }, [template, exercises, cardio]);

  // Rest countdown.
  useEffect(() => {
    if (!restTimer) return;
    if (restTimer.secs <= 0) { setRestTimer(null); hapticConfirm(); return; }
    const t = setTimeout(() => setRestTimer(r => r ? { ...r, secs: r.secs - 1 } : null), 1000);
    return () => clearTimeout(t);
  }, [restTimer]);

  const updateSet = (exIdx, setIdx, field, val) => {
    setExercises(prev => prev.map((ex, ei) => {
      if (ei !== exIdx) return ex;
      const oldVal = ex.sets[setIdx]?.[field];
      // When the user edits set 1 (index 0), propagate the new value to
      // any later set that still matches the *old* value of set 1 — so
      // they don't have to retype if all sets share the same weight/reps,
      // but any set they've already customised is preserved.
      const shouldPropagate = setIdx === 0 && (field === "weight" || field === "reps") && ex.sets.length > 1;
      return {
        ...ex,
        sets: ex.sets.map((s, si) => {
          if (si === setIdx) return { ...s, [field]: val };
          if (shouldPropagate && s[field] === oldVal) return { ...s, [field]: val };
          return s;
        }),
      };
    }));
  };

  const startRest = (secs = 60) => {
    restEndsAtRef.current = Date.now() + secs * 1000;
    setRestTimer({ secs, max: secs });
  };

  const toggleSet = (exIdx, setIdx) => {
    setExercises(prev => prev.map((ex, ei) => ei !== exIdx ? ex : {
      ...ex,
      sets: ex.sets.map((s, si) => {
        if (si !== setIdx) return s;
        const nowDone = !s.done;
        if (nowDone) { hapticHayahSuccess(); startRest(defaultRestSec()); }
        else hapticHayahSelection();
        return { ...s, done: nowDone };
      }),
    }));
  };

  // Long-press on a Done circle marks every set of that exercise complete,
  // skipping any already-done. Starts rest from the moment of the press.
  const markAllSetsDone = (exIdx) => {
    setExercises(prev => prev.map((ex, ei) => {
      if (ei !== exIdx) return ex;
      const anyUndone = ex.sets.some(s => !s.done);
      if (!anyUndone) return ex;
      try { hapticMilestone(); } catch {}
      return { ...ex, sets: ex.sets.map(s => s.done ? s : { ...s, done: true }) };
    }));
    startRest(defaultRestSec());
  };

  // Bump weight/reps by the configured step. Reuses updateSet so the
  // auto-propagate-to-later-sets logic still applies when bumping set 1.
  const bumpField = (exIdx, setIdx, field, delta) => {
    setExercises(prev => prev.map((ex, ei) => {
      if (ei !== exIdx) return ex;
      const cur = ex.sets[setIdx]?.[field];
      const n = parseFloat(cur);
      const next = (Number.isFinite(n) ? n : 0) + delta;
      // Reuse updateSet's propagation rules: when bumping set 1 of a
      // multi-set exercise, propagate to later sets that still matched
      // the OLD value.
      const oldVal = cur;
      const shouldPropagate = setIdx === 0 && (field === "weight" || field === "reps") && ex.sets.length > 1;
      const newStr = field === "weight"
        ? (Number.isInteger(next) ? String(next) : String(+next.toFixed(2)))
        : String(Math.max(0, Math.round(next)));
      try { hapticSelectionChange(); } catch {}
      return {
        ...ex,
        sets: ex.sets.map((s, si) => {
          if (si === setIdx) return { ...s, [field]: newStr };
          if (shouldPropagate && s[field] === oldVal) return { ...s, [field]: newStr };
          return s;
        }),
      };
    }));
  };

  // Reorder an exercise within the active list — up/down chevrons on the
  // card header. Disabled at the edges.
  const moveExercise = (exIdx, dir) => {
    setExercises(prev => {
      const target = exIdx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const out = prev.slice();
      const [item] = out.splice(exIdx, 1);
      out.splice(target, 0, item);
      try { hapticHayahSelection(); } catch {}
      return out;
    });
  };

  // Edit a set by uid + set id from the Watch. Patches weight/reps in place
  // so the next push back to the watch reflects the edit.
  const editSetByIds = useCallback((uid, setId, weight, reps) => {
    setExercises(prev => prev.map(ex => {
      if (ex.uid !== uid) return ex;
      return {
        ...ex,
        sets: ex.sets.map(s => {
          if (s.id !== setId) return s;
          return {
            ...s,
            weight: weight != null && weight !== "" ? String(weight) : s.weight,
            reps:   reps   != null && reps   !== "" ? String(reps)   : s.reps,
          };
        }),
      };
    }));
    hapticHayahSelection();
  }, []);

  // Toggle by uid + set id — used by the Watch companion. Falls back silently
  // if the (uid, setId) pair has been removed (set deleted, exercise removed).
  const toggleSetByIds = useCallback((uid, setId) => {
    setExercises(prev => prev.map(ex => {
      if (ex.uid !== uid) return ex;
      return {
        ...ex,
        sets: ex.sets.map(s => s.id !== setId ? s : { ...s, done: !s.done }),
      };
    }));
    hapticHayahSuccess();
    restEndsAtRef.current = Date.now() + 60 * 1000;
    setRestTimer({ secs: 60, max: 60 });
  }, []);

  // ── Watch companion bridge ─────────────────────────────────────────────
  // Push the active workout to the watch on every state change (debounced),
  // and accept set-toggle and finish messages coming back from the watch.
  // Errors are logged so build/native-side issues are visible during dev —
  // silent optional-chains used to swallow the "method not registered" failure.
  const confirmFinishRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    let toggleHandle = null;
    let finishHandle = null;
    let editHandle = null;
    if (typeof SiratWatch?.addListener === "function") {
      try {
        const p1 = SiratWatch.addListener("riyadahToggleSet", ({ exerciseUid, setId }) => {
          if (cancelled || !exerciseUid || !setId) return;
          toggleSetByIds(exerciseUid, setId);
        });
        if (p1 && typeof p1.then === "function") {
          p1.then(h => { toggleHandle = h; }).catch(err => console.warn("[SiratWatch] addListener(toggle) failed:", err));
        } else { toggleHandle = p1; }
      } catch (err) { console.warn("[SiratWatch] addListener(toggle) threw:", err); }

      try {
        const p2 = SiratWatch.addListener("riyadahFinish", () => {
          if (cancelled) return;
          // Defer to the next tick so any in-flight set-toggle has settled
          // and confirmFinishRef.current has the freshest closure.
          setTimeout(() => { try { confirmFinishRef.current?.(); } catch {} }, 0);
        });
        if (p2 && typeof p2.then === "function") {
          p2.then(h => { finishHandle = h; }).catch(err => console.warn("[SiratWatch] addListener(finish) failed:", err));
        } else { finishHandle = p2; }
      } catch (err) { console.warn("[SiratWatch] addListener(finish) threw:", err); }

      try {
        const p3 = SiratWatch.addListener("riyadahEditSet", ({ exerciseUid, setId, weight, reps }) => {
          if (cancelled || !exerciseUid || !setId) return;
          editSetByIds(exerciseUid, setId, weight, reps);
        });
        if (p3 && typeof p3.then === "function") {
          p3.then(h => { editHandle = h; }).catch(err => console.warn("[SiratWatch] addListener(editSet) failed:", err));
        } else { editHandle = p3; }
      } catch (err) { console.warn("[SiratWatch] addListener(editSet) threw:", err); }
    }
    return () => {
      cancelled = true;
      try { toggleHandle?.remove?.(); } catch {}
      try { finishHandle?.remove?.(); } catch {}
      try { editHandle?.remove?.(); } catch {}
    };
  }, [toggleSetByIds, editSetByIds]);

  useEffect(() => {
    if (typeof SiratWatch?.sendRiyadahState !== "function") return;
    const payload = {
      workout: {
        title: template.name || "Workout",
        circuits: Number(template.circuits) || 1,
        startTs: startRef.current / 1000,
        exercises: exercises.map(ex => ({
          uid: String(ex.uid || ex.id),
          name: String(ex.name || ""),
          muscles: ex.muscles ? String(ex.muscles) : null,
          block: ex.block ? String(ex.block) : null,
          sets: (ex.sets || []).map(s => ({
            id: String(s.id),
            weight: String(s.weight ?? ""),
            reps:   String(s.reps   ?? ""),
            done:   !!s.done,
          })),
        })),
      },
    };
    const t = setTimeout(() => {
      try {
        const p = SiratWatch.sendRiyadahState(payload);
        if (p && typeof p.catch === "function") {
          p.catch(err => console.warn("[SiratWatch] sendRiyadahState rejected:", err));
        }
      } catch (err) { console.warn("[SiratWatch] sendRiyadahState threw:", err); }
    }, 250);
    return () => clearTimeout(t);
  }, [exercises, template.name, template.circuits]);

  useEffect(() => {
    return () => {
      if (typeof SiratWatch?.clearRiyadahState !== "function") return;
      try {
        const p = SiratWatch.clearRiyadahState();
        if (p && typeof p.catch === "function") {
          p.catch(err => console.warn("[SiratWatch] clearRiyadahState rejected:", err));
        }
      } catch (err) { console.warn("[SiratWatch] clearRiyadahState threw:", err); }
    };
  }, []);

  // ── Live Activity bridge ──────────────────────────────────────────────────
  // Drives the Riyadah Live Activity (lock screen + Dynamic Island). The
  // activity self-ticks via timerInterval views, so we only need to push
  // state when the structural fields change: exercise switches, sets toggled,
  // rest started/cleared. Per-second tick of restTimer.secs is intentionally
  // NOT a dependency — restEndsAtRef is set once when rest begins and the
  // widget's Text(timerInterval:) drives the countdown locally.
  const liveActivityStartedRef = useRef(false);
  useEffect(() => {
    // Find the next exercise that still has incomplete sets — that's what the
    // user is "on" right now. If everything is done, surface the last set
    // of the last exercise so the activity still reads sensibly until finish.
    let curEx = null, curExIdx = -1, curSetIdx = -1;
    for (let i = 0; i < exercises.length; i++) {
      const ex = exercises[i];
      const undoneIdx = ex.sets.findIndex(s => !s.done);
      if (undoneIdx >= 0) { curEx = ex; curExIdx = i; curSetIdx = undoneIdx; break; }
    }
    if (!curEx && exercises.length > 0) {
      const lastIdx = exercises.length - 1;
      curEx = exercises[lastIdx];
      curExIdx = lastIdx;
      curSetIdx = curEx.sets.length - 1;
    }
    const setsDone = exercises.reduce((t, e) => t + e.sets.filter(s => s.done).length, 0);
    const totalSetCount = exercises.reduce((t, e) => t + e.sets.length, 0);
    const restEndsAtMs = restTimer && restEndsAtRef.current ? restEndsAtRef.current : null;

    const payload = {
      workoutTitle: template.name || "Workout",
      exerciseName: curEx ? curEx.name : "—",
      setIndex: Math.max(1, curSetIdx + 1),
      totalSetsThisExercise: curEx ? curEx.sets.length : 1,
      setsDone,
      totalSetCount,
      restEndsAtMs,
      startedAtMs: startRef.current,
    };

    const fire = (method) => {
      if (!liveActivityOn()) return;
      if (typeof SiratRiyadahActivity?.[method] !== "function") return;
      try {
        const p = SiratRiyadahActivity[method](payload);
        if (p && typeof p.catch === "function") {
          p.catch(err => console.warn(`[SiratRiyadahActivity] ${method} rejected:`, err));
        }
      } catch (err) { console.warn(`[SiratRiyadahActivity] ${method} threw:`, err); }
    };

    if (!liveActivityStartedRef.current) {
      fire("start");
      liveActivityStartedRef.current = true;
    } else {
      fire("update");
    }
    // Suppressed-deps note: restTimer is a coarse "is rest active?" trigger.
    // We only re-run when it transitions null↔non-null. The actual countdown
    // ticks inside the widget via timerInterval views.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercises, !!restTimer, template.name]);

  // Clear restEndsAtRef when rest finishes naturally.
  useEffect(() => { if (!restTimer) restEndsAtRef.current = null; }, [restTimer]);

  // End the activity when ActiveScreen unmounts (Finish or Back).
  useEffect(() => {
    return () => {
      if (typeof SiratRiyadahActivity?.end !== "function") return;
      try {
        const p = SiratRiyadahActivity.end({});
        if (p && typeof p.catch === "function") {
          p.catch(err => console.warn("[SiratRiyadahActivity] end rejected:", err));
        }
      } catch (err) { console.warn("[SiratRiyadahActivity] end threw:", err); }
    };
  }, []);

  const [confirmDelEx, setConfirmDelEx] = useState(null); // exIdx | null
  const [swapIdx, setSwapIdx] = useState(null);           // exIdx being swapped | null

  // Per-exercise effort rating (RPE). Tapping the same level clears it. Feeds
  // the RPE-autoregulated progression engine (preferred over session RPE).
  const setExerciseRpe = (exIdx, rpe) => {
    try { hapticHayahSelection(); } catch {}
    setExercises(prev => prev.map((ex, ei) => ei !== exIdx ? ex : { ...ex, rpe: ex.rpe === rpe ? null : rpe }));
  };

  // Prepend warm-up sets (≈50% ×+3 reps, ≈70% ×−2 reps of the first working
  // set) to a loaded exercise. Flagged warmup:true so they're excluded from
  // volume, working-set counts, PRs and progression. No-op on bodyweight / 0kg
  // or when warm-ups already exist.
  const addWarmupSets = (exIdx) => {
    setExercises(prev => prev.map((ex, ei) => {
      if (ei !== exIdx || ex.isCardio) return ex;
      if (ex.sets.some(s => s.warmup)) return ex;
      const firstWork = ex.sets.find(s => !s.warmup) || ex.sets[0];
      const w = parseFloat(firstWork?.weight);
      if (!(w > 0)) return ex; // nothing to ramp from
      const reps = parseInt(firstWork?.reps, 10) || 10;
      const round = (x) => Math.round(x * 2) / 2;
      const uid = ex.uid || ex.id;
      const warm = [
        { id: `${uid}-wu0-${Date.now()}`, weight: String(round(w * 0.5)), reps: String(Math.min(15, reps + 3)), done: false, warmup: true },
        { id: `${uid}-wu1-${Date.now()}-1`, weight: String(round(w * 0.7)), reps: String(Math.max(3, reps - 2)), done: false, warmup: true },
      ];
      return { ...ex, sets: [...warm, ...ex.sets] };
    }));
    try { hapticHayahSelection(); } catch {}
  };

  // Replace the exercise at exIdx with a freshly-picked one, preserving its
  // block and working-set count. The new id won't be in plannedExerciseIds, so
  // confirmFinish records it as a swap automatically (feeds the swap learning).
  const replaceExercise = (exIdx, ex) => {
    try { hapticTap(); } catch {}
    const last = lastData[ex.name];
    const isCardio = ex.type === "cardio";
    setExercises(prev => prev.map((old, ei) => {
      if (ei !== exIdx) return old;
      const uid = `${ex.id}-swap-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const block = old.block;
      if (isCardio) {
        const dur = last?.minutes || "20";
        return { ...ex, uid, block, isCardio: true, lastMinutes: last?.minutes || null, sets: [{ id: `${uid}-s0`, minutes: dur, done: false }] };
      }
      const w = last ? last.weight : "0";
      const r = last ? last.reps : "10";
      const nSets = Math.max(1, old.sets.filter(s => !s.warmup).length || 2);
      return {
        ...ex, uid, block,
        lastWeight: last ? last.weight : null,
        lastReps:   last ? last.reps   : null,
        suggestion: last ? suggestNext(last.weight, last.reps) : null,
        sets: Array.from({ length: nSets }, (_, i) => ({ id: `${uid}-s${i}`, weight: w, reps: r, done: false })),
      };
    }));
    setShowAddEx(false); setAddExQuery(""); setSwapIdx(null);
  };

  // Unified picker handler — swap when swapIdx is set, else append.
  const onPickExercise = (ex) => {
    if (swapIdx != null) replaceExercise(swapIdx, ex);
    else addExerciseFromPicker(ex);
  };

  const deleteExercise = (exIdx) => {
    if (exercises.length <= 1) return;
    hapticConfirm();
    setExercises(prev => prev.filter((_, i) => i !== exIdx));
    setConfirmDelEx(null);
  };

  const applySuggestion = (exIdx) => {
    hapticTap();
    setExercises(prev => prev.map((ex, ei) => {
      if (ei !== exIdx || !ex.suggestion) return ex;
      const { weight, reps } = ex.suggestion;
      return { ...ex, sets: ex.sets.map(s => s.done ? s : { ...s, weight, reps }) };
    }));
  };

  const deleteSet = (exIdx, setIdx) => {
    setExercises(prev => prev.map((ex, ei) => {
      if (ei !== exIdx) return ex;
      if (ex.sets.length <= 1) return ex;
      return { ...ex, sets: ex.sets.filter((_, si) => si !== setIdx) };
    }));
    hapticHayahSelection();
  };

  const addSetRow = (exIdx) => {
    hapticHayahSelection();
    setExercises(prev => prev.map((ex, ei) => ei !== exIdx ? ex : {
      ...ex,
      sets: [...ex.sets, {
        id: `${ex.uid || ex.id}-s${ex.sets.length}-${Date.now()}`,
        weight: ex.sets[ex.sets.length - 1]?.weight || "0",
        reps:   ex.sets[ex.sets.length - 1]?.reps   || "10",
        done: false,
      }],
    }));
  };

  const addExerciseFromPicker = (ex) => {
    hapticTap();
    const last = lastData[ex.name];
    const isCardio = ex.type === "cardio";
    setExercises(prev => {
      const uid = `${ex.id}-add-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
      if (isCardio) {
        const dur = last?.minutes || "20";
        return [...prev, {
          ...ex,
          uid,
          isCardio: true,
          lastMinutes: last?.minutes || null,
          sets: [
            { id: `${uid}-s0`, minutes: dur, done: false },
          ],
        }];
      }
      const w = last ? last.weight : "0";
      const r = last ? last.reps   : "10";
      return [...prev, {
        ...ex,
        uid,
        lastWeight: last ? last.weight : null,
        lastReps:   last ? last.reps   : null,
        suggestion: last ? suggestNext(last.weight, last.reps) : null,
        sets: [
          { id: `${uid}-s0`, weight: w, reps: r, done: false },
          { id: `${uid}-s1`, weight: w, reps: r, done: false },
        ],
      }];
    });
    setShowAddEx(false);
    setAddExQuery("");
  };

  const totalSets    = calcSetsFlat(exercises);
  const totalSetsAll = exercises.reduce((t, ex) => t + ex.sets.filter(s => !s.warmup).length, 0);
  const progress     = totalSetsAll > 0 ? totalSets / totalSetsAll : 0;
  const C = 2 * Math.PI * 10;

  const confirmFinish = async () => {
    const volume = calcVolumeFlat(exercises);
    const prs = exercises.filter(ex => ex.sets.some(s => {
      const lw = parseFloat(ex.lastWeight) || 0;
      const cw = parseFloat(s.weight) || 0;
      return s.done && !s.warmup && lw > 0 && cw > lw;
    })).length;
    const endTs = Date.now();
    const watch = watchActive ? await fetchSessionWatchData(startRef.current, endTs) : null;
    if (watchActive && !watch) {
      toast.showInfo("No Apple Watch workout found in the last 2h. Make sure HIIT is started on the watch and Health permission is granted in Settings → Privacy → Health → Sirat.", 6000);
    } else if (watch) {
      toast.showSuccess(`⌚ ${watch.activityType} merged · ${watch.calories} kcal · avg ${watch.avgHr} bpm`);
    }
    // ── Feedback-loop capture (foundation) ──────────────────────────
    // Record what the user actually did vs the plan so future progression /
    // adaptation logic has clean signal. Keyed by exercise id (not name).
    const idOf = (ex) => ex.exerciseId || ex.id || null;
    const plannedIds = (template.plannedExerciseIds
      || (template.exercises_list || []).map(e => e.id)).filter(Boolean);
    const currentIds = exercises.map(idOf).filter(Boolean);
    const skippedExercises = exercises
      .filter(ex => !ex.sets.some(s => s.done))
      .map(ex => ({ id: idOf(ex), name: ex.name }));
    // Swapped in = exercises performed that weren't in the original plan;
    // removed = planned exercises that never made it into the session.
    const swappedExercises = plannedIds.length
      ? currentIds.filter(id => !plannedIds.includes(id))
      : [];
    const removedFromPlan = plannedIds.filter(id => !currentIds.includes(id));
    onFinish({
      name: template.name,
      // templateId carries the lineage so the +Log screen can identify
      // re-runs of the same workout ("last time you did this") even
      // after the user renamed the template or imported a fresh paste.
      templateId: template.id || null,
      date: todayLabel(),
      time: nowTimeLabel(),
      timestamp: endTs,
      duration: elapsed,
      sets: totalSets,
      volume,
      exercises: exercises.map(ex => ({
        name: ex.name,
        id: ex.exerciseId || ex.id,
        muscles: ex.muscles,
        type: ex.type,
        block: ex.block,
        rpe: typeof ex.rpe === "number" ? ex.rpe : null, // per-exercise effort
        sets: ex.sets.filter(s => s.done).map(s => ({ weight: s.weight, reps: s.reps, ...(s.warmup ? { warmup: true } : {}) })),
      })),
      prs,
      cardio: cardio?.done ? cardio : null,
      watch,
      // ── Feedback-loop fields (foundation; adaptation logic comes later) ──
      completed: true,                       // reached the Finish flow
      sessionRPE: null,                      // set on the Finish screen (1–10)
      equipmentMode: template.equipmentMode || null, // stored, not regex-inferred
      plannedExerciseIds: plannedIds,
      swappedExercises,                      // ids performed that weren't planned
      removedFromPlan,                       // planned ids never performed
      skippedExercises,                      // [{id,name}] with zero done sets
    });
  };
  // Expose the latest confirmFinish closure to the watch-finish listener
  // (which lives in a useEffect that captures stale state otherwise).
  confirmFinishRef.current = confirmFinish;

  return (
    <div className="ry-slide" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
      {/* Live header */}
      <div style={{
        background: `linear-gradient(180deg, rgba(224,160,48,0.08), transparent)`,
        borderBottom: `0.5px solid rgba(224,160,48,0.15)`,
        padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="ry-btn" onClick={onBack}
            style={{ background: "transparent", border: 0, color: theme.ink2, padding: "2px 0" }}>
            <Icon name="back" size={16}/>
          </button>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.01em" }}>{template.name}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
              <Icon name="clock" size={11} color={theme.ink3}/>
              <span className="ry-mono" style={{ fontSize: 12, color: theme.ink3 }}>{fmtDuration(elapsed)}</span>
              <span style={{ color: theme.line }}>·</span>
              <span className="ry-mono" style={{ fontSize: 12, color: theme.teal }}>{totalSets}/{totalSetsAll} sets</span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <svg width="28" height="28" viewBox="0 0 28 28">
            <circle cx="14" cy="14" r="10" fill="none" stroke={theme.line} strokeWidth="3"/>
            <circle cx="14" cy="14" r="10" fill="none" stroke={theme.teal}
              strokeWidth="3" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={C * (1 - progress)}
              style={{ transform: "rotate(-90deg)", transformOrigin: "center", transition: "stroke-dashoffset 1s linear" }}
            />
          </svg>
          <button className="ry-btn" onClick={() => { try { hapticTap(); } catch {} setShowFinishConfirm(true); }}
            style={{ background: progress >= 1 ? theme.teal : theme.amber, color: "#0A0A0E", border: 0, borderRadius: 10,
              fontSize: 12, fontWeight: 700, padding: "7px 12px", letterSpacing: "-0.01em",
              boxShadow: progress >= 1 ? "0 0 16px rgba(79,217,194,0.6)" : "none",
              transition: "background 0.25s ease, box-shadow 0.25s ease" }}>
            {progress >= 1 ? "Finish ✓" : "Finish"}
          </button>
        </div>
      </div>

      {/* Rest banner */}
      {restTimer && <RestBanner theme={theme} restTimer={restTimer} onSubtract={() => setRestTimer(r => r ? ({ ...r, secs: Math.max(0, r.secs - 15) }) : null)} onSkip={() => setRestTimer(null)} />}

      <div className="ry-sb" style={{ flex: 1, overflowY: "auto", paddingBottom: "calc(96px + env(safe-area-inset-bottom))" }}>
        {/* Circuit dots */}
        <div style={{ padding: "10px 16px 6px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.ink4 }}>Circuits</span>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {Array.from({ length: template.circuits || 2 }).map((_, i) => {
              const circuitDone = exercises.length > 0 && exercises.every(ex => ex.sets[i]?.done);
              const circuitActive = !circuitDone && exercises.some(ex => ex.sets[i] && !ex.sets[i].done);
              const w = circuitActive ? 18 : 7;
              const bg = circuitDone ? theme.teal : circuitActive ? theme.amber : theme.line;
              return <div key={i} style={{ width: w, height: 7, borderRadius: circuitActive ? 4 : 999, background: bg, transition: "all .2s" }} />;
            })}
          </div>
          <span style={{ fontSize: 10, color: theme.ink4 }}>{template.circuits} circuits · {exercises.length} exercises</span>
        </div>

        {/* Exercise cards, grouped by block */}
        {exercises.map((ex, exIdx) => {
          const prevBlock = exIdx > 0 ? exercises[exIdx - 1].block : null;
          const showHeader = ex.block && ex.block !== prevBlock;
          return (
            <Fragment key={ex.uid || `${ex.id}-${exIdx}`}>
              {showHeader && (
                <BlockHd theme={theme} letter={ex.block}
                  label={template.blockLabels?.[ex.block]}
                  rounds={template.circuits}/>
              )}
              <ExerciseCard theme={theme} ex={ex} exIdx={exIdx}
                onAddSet={addSetRow}
                onUpdate={updateSet}
                onToggle={toggleSet}
                canDelete={exercises.length > 1}
                confirmDel={confirmDelEx === exIdx}
                onAskDelete={() => { try { hapticWarning(); } catch {} setConfirmDelEx(exIdx); }}
                onCancelDelete={() => setConfirmDelEx(null)}
                onConfirmDelete={() => deleteExercise(exIdx)}
                onDeleteSet={deleteSet}
                onApplySuggestion={() => applySuggestion(exIdx)}
                onSetRpe={(rpe) => setExerciseRpe(exIdx, rpe)}
                onSwap={() => { try { hapticTap(); } catch {} setSwapIdx(exIdx); setShowAddEx(true); }}
                onAddWarmup={() => addWarmupSets(exIdx)}
                onBumpField={(setIdx, field, delta) => bumpField(exIdx, setIdx, field, delta)}
                onMarkAllDone={() => markAllSetsDone(exIdx)}
                canMoveUp={exIdx > 0}
                canMoveDown={exIdx < exercises.length - 1}
                onMoveUp={() => moveExercise(exIdx, -1)}
                onMoveDown={() => moveExercise(exIdx, 1)}
              />
            </Fragment>
          );
        })}

        {/* Cardio */}
        {cardio && <CardioCard theme={theme} cardio={cardio} setCardio={setCardio} />}

        {/* Add exercise */}
        <button className="ry-btn" onClick={() => { try { hapticTap(); } catch {} setShowAddEx(true); }}
          style={{
            margin: "0 16px 10px", padding: "13px 16px",
            background: "transparent", border: `0.5px dashed rgba(224,160,48,0.3)`,
            borderRadius: 14, color: theme.amber,
            fontSize: 14, fontWeight: 500,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            width: "calc(100% - 32px)",
          }}>
          <Icon name="plus" size={16} color={theme.amber}/>
          Add exercise
        </button>

        {/* Apple Watch / HealthKit status */}
        <WatchCard theme={theme} available={watchActive} live={watchActive} compact />
      </div>

      {/* Add / swap exercise sheet */}
      {showAddEx && createPortal(
        <Sheet onClose={() => { setShowAddEx(false); setAddExQuery(""); setSwapIdx(null); }} theme={theme}>
          <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.01em" }}>
              {swapIdx != null ? `Swap ${exercises[swapIdx]?.name || "exercise"}` : "Add Exercise"}
            </div>
            <button className="ry-btn" onClick={() => { setShowAddEx(false); setAddExQuery(""); setSwapIdx(null); }}
              style={{ background: theme.bg3, border: `0.5px solid ${theme.line}`, borderRadius: 8, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: theme.ink3 }}>
              <Icon name="x" size={14}/>
            </button>
          </div>
          <div style={{ padding: "0 16px 10px" }}>
            <input
              autoFocus
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoCorrect="on"
              autoCapitalize="none"
              autoComplete="off"
              spellCheck={true}
              value={addExQuery}
              onChange={(e) => setAddExQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              placeholder="Search exercises…"
              style={{ width: "100%", fontSize: 16, padding: "12px 14px",
                background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 12,
                color: theme.ink1, WebkitAppearance: "none", appearance: "none" }}/>
          </div>
          <div className="ry-sb" style={{ maxHeight: 380, overflowY: "auto", padding: "0 16px 8px" }}>
            {(() => {
              const filtered = filterExercises(RIYADAH_PICKER_EXERCISES, addExQuery);
              if (filtered.length === 0) {
                return <div style={{ padding: "30px 8px", textAlign: "center", color: theme.ink3, fontSize: 13 }}>
                  No exercises match "{addExQuery}".
                </div>;
              }
              return filtered.map(ex => (
                <button key={ex.id} className="ry-btn"
                  onClick={() => { onPickExercise(ex); setAddExQuery(""); }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "12px 0", width: "100%", textAlign: "left",
                    borderBottom: `0.5px solid ${theme.lineSoft}`, background: "transparent", border: 0,
                    color: "inherit",
                  }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: theme.ink1 }}>{ex.name}</div>
                    <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1 }}>{ex.muscles}</div>
                  </div>
                  <ExBadge theme={theme} type={ex.type}/>
                </button>
              ));
            })()}
          </div>
        </Sheet>,
        document.body)}

      {/* Finish confirm sheet */}
      {showFinishConfirm && createPortal(
        <Sheet onClose={() => setShowFinishConfirm(false)} theme={theme}>
          <div style={{ padding: "20px 20px 8px", textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🏁</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.02em", marginBottom: 6 }}>Finish workout?</div>
            <div style={{ fontSize: 13, color: theme.ink3, lineHeight: 1.5, marginBottom: 20 }}>
              {fmtDuration(elapsed)} · {totalSets} sets completed
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 4px" }}>
              <button className="ry-btn" onClick={confirmFinish}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  background: theme.amber, color: "#0A0A0E", border: 0, borderRadius: 14,
                  fontSize: 15, fontWeight: 700, padding: "15px 20px", width: "100%" }}>
                Save & finish
              </button>
              <button className="ry-btn" onClick={() => setShowFinishConfirm(false)}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  background: theme.bg3, color: theme.ink1, border: `0.5px solid ${theme.line}`, borderRadius: 14,
                  fontSize: 15, fontWeight: 500, padding: "14px 20px", width: "100%" }}>
                Keep going
              </button>
              <button className="ry-btn"
                onClick={() => {
                  try { hapticTap(); } catch {}
                  const name = typeof window !== "undefined"
                    ? window.prompt("Save this workout as a template. Name it:", template?.name && template.id !== "quick-start" ? `${template.name} v2` : "My Routine")
                    : null;
                  if (!name) return;
                  try {
                    const routine = snapshotSessionAsRoutine(name, exercises);
                    upsertRoutine(routine);
                    try { hapticHayahSuccess(); } catch {}
                    if (typeof window !== "undefined") {
                      // Confirm and stay open so the user can still finish/keep going.
                      try { window.alert(`Saved "${routine.name}" — it'll appear under "Your routines" on the Hub.`); } catch {}
                    }
                  } catch (e) { console.warn("save template", e); }
                }}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  background: "transparent", color: theme.amber, border: `0.5px dashed ${theme.amberSoft || theme.line}`, borderRadius: 14,
                  fontSize: 13, fontWeight: 600, padding: "12px 20px", width: "100%" }}>
                ⭐ Save as template
              </button>
              {onDiscard && (
                <button className="ry-btn"
                  onClick={() => {
                    if (typeof window !== "undefined"
                      && !window.confirm("Discard this workout? Sets and time won't be saved.")) return;
                    setShowFinishConfirm(false);
                    onDiscard();
                  }}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    background: "transparent", color: theme.coral, border: 0,
                    fontSize: 13, fontWeight: 600, padding: "8px 20px 4px", width: "100%",
                    textDecoration: "underline" }}>
                  Discard workout
                </button>
              )}
            </div>
          </div>
        </Sheet>,
        document.body)}
    </div>
  );
}

function ExerciseCard({ theme, ex, exIdx, onAddSet, onUpdate, onToggle,
  canDelete, confirmDel, onAskDelete, onCancelDelete, onConfirmDelete, onDeleteSet, onApplySuggestion,
  onBumpField, onMarkAllDone, canMoveUp, canMoveDown, onMoveUp, onMoveDown,
  onSetRpe, onSwap, onAddWarmup }) {
  const canDeleteSet = ex.sets.length > 1;
  // Collapsed state: auto-collapse when all sets done, but let the user
  // override in either direction (force-collapse mid-workout, or keep open
  // after finishing). null = follow auto; true/false = explicit override.
  const allDone = ex.sets.length > 0 && ex.sets.every(s => s.done);
  const [userCollapsed, setUserCollapsed] = useState(null);
  const collapsed = userCollapsed !== null ? userCollapsed : allDone;
  const toggleCollapsed = () => setUserCollapsed(collapsed ? false : true);
  // Long-press timer for "mark all sets done".
  const longPressRef = useRef(null);
  const startLongPress = (setIdx) => {
    if (longPressRef.current) clearTimeout(longPressRef.current);
    longPressRef.current = setTimeout(() => {
      longPressRef.current = null;
      onMarkAllDone && onMarkAllDone();
    }, 550);
  };
  const cancelLongPress = () => {
    if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
  };
  return (
    <div className="ry-fade" style={{
      background: theme.bg2, border: `0.5px solid ${theme.line}`,
      borderRadius: 16, margin: "0 16px 10px", overflow: "hidden",
    }}>
      {confirmDel ? (
        <div style={{ padding: "13px 14px 12px", borderBottom: `0.5px solid ${theme.line}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          background: "rgba(217,122,106,0.06)" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.coral, letterSpacing: "-0.01em", flex: 1, minWidth: 0 }}>
            Remove {ex.name}?
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="ry-btn" onClick={onCancelDelete}
              style={{ background: "transparent", color: theme.ink3, border: `0.5px solid ${theme.line}`,
                borderRadius: 8, fontSize: 11, fontWeight: 600, padding: "5px 10px" }}>Cancel</button>
            <button className="ry-btn" onClick={onConfirmDelete}
              style={{ background: theme.coral, color: "#0A0A0E", border: 0,
                borderRadius: 8, fontSize: 11, fontWeight: 700, padding: "5px 12px" }}>Confirm</button>
          </div>
        </div>
      ) : (
        <div onClick={collapsed ? toggleCollapsed : undefined}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 14px 12px",
            borderBottom: collapsed ? "none" : `0.5px solid ${theme.line}`,
            cursor: collapsed ? "pointer" : "default" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }}
              style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 15, fontWeight: 600, color: theme.ink1, letterSpacing: "-0.01em", cursor: "pointer", userSelect: "none" }}>
              {allDone && <span style={{ color: theme.teal, fontSize: 13, flexShrink: 0, marginTop: 1 }}>✓</span>}
              {/* Full name, wrapping to up to 2 lines instead of truncating to "Leg…" */}
              <span style={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: 1.25 }}>{ex.name}</span>
            </div>
            {collapsed ? (
              <div style={{ fontSize: 11, color: theme.ink3, marginTop: 2 }}>
                {ex.isCardio
                  ? `${ex.sets[0]?.minutes || "—"} min · done`
                  : `${ex.sets.length} sets · ${ex.sets.map(s => s.weight === "BW" ? "BW" : `${s.weight}×${s.reps}`).join(" / ")}`}
              </div>
            ) : ((ex.muscles || (ex._autoApplied && !allDone)) && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 3 }}>
                {ex.muscles && <span style={{ fontSize: 11, color: theme.ink3 }}>{ex.muscles}</span>}
                {ex._autoApplied && !allDone && (
                  <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", padding: "2px 6px", borderRadius: 4, background: "rgba(79,217,194,0.14)", color: theme.teal, border: `0.5px solid ${theme.teal}40` }}>
                    ↑ {ex.suggestion?.delta || "progressed"}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {!collapsed && showHowToLinks() && (
              <button className="ry-btn" onClick={(e) => { e.stopPropagation(); try { hapticTap(); } catch {} window.open(youtubeHowToUrl(ex.name), "_blank"); }}
                aria-label="Watch how-to"
                style={{ width: 34, height: 34, borderRadius: 10, background: theme.bg3,
                  border: `0.5px solid ${theme.line}`, color: theme.coral, fontSize: 13,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                ▶
              </button>
            )}
            {!collapsed && onSwap && (
              <button className="ry-btn" onClick={(e) => { e.stopPropagation(); onSwap(); }}
                aria-label="Swap exercise"
                style={{ width: 34, height: 34, borderRadius: 10, background: theme.bg3,
                  border: `0.5px solid ${theme.line}`, color: theme.ink2, fontSize: 15,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                ⇄
              </button>
            )}
            {!collapsed && ex.type && <ExBadge theme={theme} type={ex.type}/>}
            {!collapsed && (canMoveUp || canMoveDown) && (
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <button className="ry-btn" onClick={(e) => { e.stopPropagation(); onMoveUp && onMoveUp(); }}
                  disabled={!canMoveUp} aria-label="Move up"
                  style={{ width: 24, height: 13, borderRadius: 6, background: theme.bg3,
                    border: `0.5px solid ${theme.line}`, color: canMoveUp ? theme.ink2 : theme.ink4,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, opacity: canMoveUp ? 1 : 0.4 }}>
                  ▲
                </button>
                <button className="ry-btn" onClick={(e) => { e.stopPropagation(); onMoveDown && onMoveDown(); }}
                  disabled={!canMoveDown} aria-label="Move down"
                  style={{ width: 24, height: 13, borderRadius: 6, background: theme.bg3,
                    border: `0.5px solid ${theme.line}`, color: canMoveDown ? theme.ink2 : theme.ink4,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, opacity: canMoveDown ? 1 : 0.4 }}>
                  ▼
                </button>
              </div>
            )}
            {!collapsed && !ex.isCardio && (
              <button className="ry-btn" onClick={(e) => { e.stopPropagation(); onAddSet(exIdx); }}
                aria-label="Add set"
                style={{ width: 34, height: 34, borderRadius: 10, background: theme.bg3,
                  border: `0.5px solid ${theme.line}`, color: theme.ink1, fontSize: 20, fontWeight: 500,
                  lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                +
              </button>
            )}
            {!collapsed && (
              <button className="ry-btn" onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }}
                aria-label="Collapse exercise"
                style={{ width: 26, height: 26, marginLeft: 6, borderRadius: 8, background: theme.bg3,
                  border: `0.5px solid ${theme.line}`, color: theme.ink2, fontSize: 12,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                ▴
              </button>
            )}
            {!collapsed && canDelete && (
              <button className="ry-btn" onClick={(e) => { e.stopPropagation(); onAskDelete(); }} aria-label="Remove exercise"
                style={{ width: 26, height: 26, marginLeft: 10, borderRadius: 8, background: theme.bg3,
                  border: `0.5px solid ${theme.line}`, color: theme.ink3,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="trash" size={13}/>
              </button>
            )}
            {collapsed && (
              <span style={{ color: theme.ink2, fontSize: 14 }}>▾</span>
            )}
          </div>
        </div>
      )}
      {!collapsed && (<>

      <div style={{ display: "grid", gridTemplateColumns: ex.isCardio ? "28px 1fr 44px 22px" : "28px 1fr 1fr 44px 22px", padding: "6px 14px", gap: 6,
        fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.ink2 }}>
        <span style={{ textAlign: "center" }}>{ex.isCardio ? "•" : "Set"}</span>
        {ex.isCardio ? (
          <span style={{ textAlign: "center" }}>Minutes</span>
        ) : (<>
          <span style={{ textAlign: "center" }}>Weight</span>
          <span style={{ textAlign: "center" }}>Reps</span>
        </>)}
        <span style={{ textAlign: "center" }}>Done</span>
        <span/>
      </div>

      {/* Last session ghost row */}
      {!ex.isCardio && ex.lastWeight && (
        <div style={{
          display: "grid", gridTemplateColumns: "28px 1fr 1fr 44px 22px",
          padding: "5px 14px", gap: 6,
          background: "rgba(224,160,48,0.04)",
          borderTop: `0.5px solid ${theme.lineSoft}`,
        }}>
          <span className="ry-mono" style={{ fontSize: 10, color: theme.ink4, textAlign: "center", lineHeight: "20px" }}>↑</span>
          <GhostCell theme={theme} value={ex.lastWeight} />
          <GhostCell theme={theme} value={ex.lastReps} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase", color: theme.ink4 }}>last</span>
          </div>
          <span/>
        </div>
      )}
      {ex.isCardio && ex.lastMinutes && (
        <div style={{
          display: "grid", gridTemplateColumns: "28px 1fr 44px 22px",
          padding: "5px 14px", gap: 6,
          background: "rgba(224,160,48,0.04)",
          borderTop: `0.5px solid ${theme.lineSoft}`,
        }}>
          <span className="ry-mono" style={{ fontSize: 10, color: theme.ink4, textAlign: "center", lineHeight: "20px" }}>↑</span>
          <GhostCell theme={theme} value={`${ex.lastMinutes} min`} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase", color: theme.ink4 }}>last</span>
          </div>
          <span/>
        </div>
      )}
      {!ex.isCardio && ex.suggestion && onApplySuggestion && (
        <button className="ry-btn" onClick={onApplySuggestion}
          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "7px 14px", background: "rgba(79,217,194,0.08)",
            borderTop: `0.5px solid ${theme.lineSoft}`,
            border: "none", color: "inherit", textAlign: "left", cursor: "pointer" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.teal }}>Try</span>
            <span className="ry-mono" style={{ fontSize: 12, color: theme.ink1, fontWeight: 600 }}>
              {ex.suggestion.weight === "BW" ? "BW" : `${ex.suggestion.weight}kg`} × {ex.suggestion.reps}
            </span>
            <span style={{ fontSize: 10, color: theme.teal, fontWeight: 600 }}>{ex.suggestion.delta}</span>
          </span>
          <span style={{ fontSize: 9, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase" }}>tap to apply</span>
        </button>
      )}

      {ex.sets.map((set, setIdx) => {
        const lw = parseFloat(ex.lastWeight) || 0;
        const cw = parseFloat(set.weight) || 0;
        const isPR = set.done && !set.warmup && lw > 0 && cw > lw;
        // Working sets are renumbered 1..n; warm-ups show "W".
        const workNo = ex.sets.slice(0, setIdx).filter(s => !s.warmup).length + 1;
        if (ex.isCardio) {
          return (
            <div key={set.id} style={{
              display: "grid", gridTemplateColumns: "28px 1fr 44px 22px",
              padding: "8px 14px", gap: 6, alignItems: "center",
              borderTop: `0.5px solid ${theme.lineSoft}`,
              background: set.done ? "rgba(79,217,194,0.04)" : "transparent",
              transition: "background .15s",
            }}>
              <span className="ry-mono" style={{ fontSize: 12, fontWeight: 500, color: theme.ink3, textAlign: "center" }}>•</span>
              <input className="ry-input"
                value={set.minutes || ""}
                onChange={e => onUpdate(exIdx, setIdx, "minutes", e.target.value)}
                onFocus={e => e.target.select()}
                placeholder="min"
                inputMode="decimal"
              />
              <button className="ry-btn"
                onClick={() => onToggle(exIdx, setIdx)}
                onPointerDown={() => startLongPress(setIdx)}
                onPointerUp={cancelLongPress}
                onPointerLeave={cancelLongPress}
                onPointerCancel={cancelLongPress}
                style={{
                  width: 38, height: 38, borderRadius: "50%",
                  border: `1.5px solid ${set.done ? theme.teal : theme.line}`,
                  background: set.done ? theme.teal : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto",
                  transition: "all .2s",
                }}>
                {set.done && <Icon name="check" size={15} color="#0A0A0E"/>}
              </button>
              <span/>
            </div>
          );
        }
        return (
          <div key={set.id} style={{
            display: "grid", gridTemplateColumns: "28px 1fr 1fr 44px 22px",
            padding: "8px 14px", gap: 6, alignItems: "center",
            borderTop: `0.5px solid ${theme.lineSoft}`,
            background: set.done ? "rgba(79,217,194,0.04)" : "transparent",
            transition: "background .15s",
          }}>
            <span className="ry-mono" style={{ fontSize: set.warmup ? 9 : 12, fontWeight: 700, color: set.warmup ? theme.amber : theme.ink3, textAlign: "center", letterSpacing: set.warmup ? "0.06em" : 0 }}>{set.warmup ? "W" : workNo}</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, opacity: set.warmup ? 0.7 : 1 }}>
              <div style={{ display: "flex", alignItems: "stretch", gap: 2 }}>
                <button className="ry-btn" onClick={() => onBumpField && onBumpField(setIdx, "weight", -2.5)}
                  aria-label="Decrease weight"
                  style={{ width: 26, background: theme.bg3, border: `0.5px solid ${theme.line}`,
                    borderRadius: 6, color: theme.ink2, fontSize: 13, lineHeight: 1, padding: 0 }}>−</button>
                <input className="ry-input"
                  value={set.weight}
                  onChange={e => onUpdate(exIdx, setIdx, "weight", e.target.value)}
                  onFocus={e => e.target.select()}
                  placeholder="kg"
                  inputMode="decimal"
                  style={{ flex: 1, minWidth: 0, ...(isPR ? { borderColor: theme.amber, background: "rgba(224,160,48,0.08)" } : {}) }}
                />
                <button className="ry-btn" onClick={() => onBumpField && onBumpField(setIdx, "weight", 2.5)}
                  aria-label="Increase weight"
                  style={{ width: 26, background: theme.bg3, border: `0.5px solid ${theme.line}`,
                    borderRadius: 6, color: theme.ink2, fontSize: 13, lineHeight: 1, padding: 0 }}>+</button>
              </div>
              {isPR && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
                  <span className="ry-mono" style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: theme.amber }}>↑ PR</span>
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "stretch", gap: 2 }}>
              <button className="ry-btn" onClick={() => onBumpField && onBumpField(setIdx, "reps", -1)}
                aria-label="Decrease reps"
                style={{ width: 26, background: theme.bg3, border: `0.5px solid ${theme.line}`,
                  borderRadius: 6, color: theme.ink2, fontSize: 13, lineHeight: 1, padding: 0 }}>−</button>
              <input className="ry-input"
                value={set.reps}
                onChange={e => onUpdate(exIdx, setIdx, "reps", e.target.value)}
                onFocus={e => e.target.select()}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                    if (!set.done) onToggle(exIdx, setIdx);
                  }
                }}
                placeholder="reps"
                inputMode="numeric"
                style={{ flex: 1, minWidth: 0 }}
              />
              <button className="ry-btn" onClick={() => onBumpField && onBumpField(setIdx, "reps", 1)}
                aria-label="Increase reps"
                style={{ width: 26, background: theme.bg3, border: `0.5px solid ${theme.line}`,
                  borderRadius: 6, color: theme.ink2, fontSize: 13, lineHeight: 1, padding: 0 }}>+</button>
            </div>
            <button className="ry-btn"
              onClick={() => onToggle(exIdx, setIdx)}
              onPointerDown={() => startLongPress(setIdx)}
              onPointerUp={cancelLongPress}
              onPointerLeave={cancelLongPress}
              onPointerCancel={cancelLongPress}
              aria-label={set.done ? `Set ${setIdx + 1} done` : `Mark set ${setIdx + 1} done`}
              aria-pressed={!!set.done}
              title="Tap to mark done · long-press to mark all sets done"
              style={{
                width: 38, height: 38, borderRadius: "50%",
                border: `1.5px solid ${set.done ? theme.teal : theme.line}`,
                background: set.done ? theme.teal : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto",
                transition: "all .2s",
              }}>
              {set.done && <Icon name="check" size={15} color="#0A0A0E"/>}
            </button>
            {canDeleteSet ? (
              <button className="ry-btn" onClick={() => onDeleteSet(exIdx, setIdx)} aria-label="Remove set"
                style={{
                  width: 22, height: 22, borderRadius: 6, background: "transparent",
                  border: 0, color: theme.ink4, padding: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                <Icon name="x" size={11}/>
              </button>
            ) : <span/>}
          </div>
        );
      })}

      {/* Footer: warm-up ramp (loaded lifts only) + per-exercise effort (RPE) */}
      {!ex.isCardio && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 14px 12px", borderTop: `0.5px solid ${theme.lineSoft}`, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: theme.ink4 }}>Effort</span>
            {[{ k: "Easy", v: 5 }, { k: "Solid", v: 7 }, { k: "Hard", v: 9 }].map(o => {
              const sel = ex.rpe === o.v;
              return (
                <button key={o.k} className="ry-btn" onClick={() => onSetRpe && onSetRpe(o.v)}
                  style={{ padding: "5px 11px", borderRadius: 14, fontSize: 11, fontWeight: 700,
                    border: `0.5px solid ${sel ? theme.teal : theme.line}`,
                    background: sel ? theme.teal : theme.bg3, color: sel ? theme.bg0 : theme.ink3 }}>
                  {o.k}
                </button>
              );
            })}
          </div>
          {onAddWarmup && !ex.sets.some(s => s.warmup) && (parseFloat((ex.sets.find(s => !s.warmup) || ex.sets[0])?.weight) > 0) && (
            <button className="ry-btn" onClick={onAddWarmup}
              style={{ padding: "5px 11px", borderRadius: 14, fontSize: 11, fontWeight: 600,
                border: `0.5px dashed ${theme.amber}66`, background: "transparent", color: theme.amber }}>
              + Warm-up
            </button>
          )}
        </div>
      )}
      </>)}
    </div>
  );
}

function GhostCell({ theme, value }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <span className="ry-mono" style={{ fontSize: 11, color: theme.amber, opacity: 0.85 }}>{value}</span>
      <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.ink3 }}>last</span>
    </div>
  );
}

function CardioCard({ theme, cardio, setCardio }) {
  const C = 2 * Math.PI * 14;
  return (
    <div className="ry-fade" style={{
      margin: "0 16px 10px", background: theme.bg2,
      border: `0.5px solid ${theme.line}`, borderRadius: 16, overflow: "hidden",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 14px 12px", borderBottom: `0.5px solid ${theme.line}` }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.ink1, letterSpacing: "-0.01em" }}>🚶 {cardio.name}</div>
          <div style={{ fontSize: 11, color: theme.ink3, marginTop: 2 }}>Incline {cardio.incline}% · {cardio.speed} km/h</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ExBadge theme={theme} type="cardio"/>
          <button className="ry-btn"
            onClick={() => { hapticHayahSelection(); setCardio(c => ({ ...c, done: !c.done })); }}
            style={{
              width: 30, height: 30, borderRadius: "50%",
              border: `1.5px solid ${cardio.done ? theme.teal : theme.line}`,
              background: cardio.done ? theme.teal : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
            {cardio.done && <Icon name="check" size={13} color="#0A0A0E"/>}
          </button>
          {/* unused dasharray to silence lint warning if any */}
          <span style={{ display: "none" }}>{C}</span>
        </div>
      </div>
      <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 8 }}>
        {[5, 10, 15, 20].map(min => (
          <button key={min} className="ry-btn" onClick={() => setCardio(c => ({ ...c, duration: min }))}
            style={{
              flex: 1, padding: "8px 6px", borderRadius: 10,
              background: cardio.duration === min ? theme.coralDim : theme.bg3,
              border: `0.5px solid ${cardio.duration === min ? "rgba(217,122,106,0.30)" : theme.line}`,
            }}>
            <div className="ry-mono" style={{ fontSize: 13, fontWeight: 600, color: cardio.duration === min ? theme.coral : theme.ink2 }}>{min}m</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function RestBanner({ theme, restTimer, onSubtract, onSkip }) {
  const C = 2 * Math.PI * 14;
  return (
    <div style={{
      margin: "0 16px 10px", padding: "12px 16px",
      background: `linear-gradient(135deg, rgba(79,217,194,0.10), rgba(79,217,194,0.05))`,
      border: `0.5px solid rgba(79,217,194,0.25)`, borderRadius: 12,
      display: "flex", alignItems: "center", justifyContent: "space-between",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ position: "relative", width: 36, height: 36 }}>
          <svg width="36" height="36" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(79,217,194,0.20)" strokeWidth="3"/>
            <circle cx="18" cy="18" r="14" fill="none" stroke={theme.teal}
              strokeWidth="3" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={C * (1 - restTimer.secs / restTimer.max)}
              style={{ transform: "rotate(-90deg)", transformOrigin: "center", transition: "stroke-dashoffset 1s linear" }}
            />
          </svg>
          <div className="ry-mono" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: theme.teal }}>
            {restTimer.secs}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: theme.teal }}>Rest timer</div>
          <div style={{ fontSize: 11, color: theme.ink3 }}>Next set in {restTimer.secs}s</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button className="ry-btn" onClick={onSubtract}
          style={{ background: "rgba(79,217,194,0.12)", color: theme.teal, border: `0.5px solid rgba(79,217,194,0.25)`, borderRadius: 8, fontSize: 11, fontWeight: 600, padding: "5px 10px" }}>−15s</button>
        <button className="ry-btn" onClick={onSkip}
          style={{ background: "transparent", color: theme.ink3, border: `0.5px solid ${theme.line}`, borderRadius: 8, fontSize: 11, fontWeight: 600, padding: "5px 10px" }}>Skip</button>
      </div>
    </div>
  );
}

// ───────── FuelForm ─────────
// Pre / post workout fuel logger. Items chosen here are appended directly to
// today's `calorieLog` localStorage entry so they appear in Ghidha.
function FuelForm({ theme, onClose, mode = "pre" }) {
  const toast = useToast();
  const [pre,   setPre]   = useState(mode === "pre");
  const [oats,  setOats]  = useState(false);
  const [scoops, setScoops] = useState(mode === "post" ? 2 : 1);
  const [wheyBrand, setWheyBrand] = useState("usn"); // "usn" | "on"
  // User mixes whey with 250ml Clover full cream milk by default. Track an
  // explicit override so they can still log a water-mixed shake if needed.
  const [milkOverride, setMilkOverride] = useState(null); // null = follow scoops, true/false = user-set
  const milk = milkOverride === null ? scoops > 0 : milkOverride;

  const wheyKey = wheyBrand === "on"
    ? (scoops === 1 ? "onWhey1" : scoops === 2 ? "onWhey2" : null)
    : (scoops === 1 ? "usn1"    : scoops === 2 ? "usn2"    : null);
  const wheyFood = wheyKey ? FUEL_FOODS[wheyKey] : null;

  const items = [];
  if (pre)               items.push(FUEL_FOODS.pre);
  if (oats)              items.push(FUEL_FOODS.oats);
  if (wheyFood)          items.push(wheyFood);
  if (milk && scoops > 0) items.push(FUEL_FOODS.milk);

  const totals = items.reduce((t, f) => ({
    calories: t.calories + f.calories,
    protein:  t.protein  + f.protein,
    carbs:    t.carbs    + f.carbs,
    fat:      t.fat      + f.fat,
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  const submit = () => {
    if (items.length === 0) return;
    logFuelItems(items);
    hapticHayahSuccess();
    toast.showSuccess(`Logged to Ghidha · ${totals.calories} kcal · ${totals.protein}g protein`);
    onClose();
  };

  const Card = ({ active, onTap, title, sub }) => (
    <button className="ry-btn" onClick={onTap}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", width: "100%",
        textAlign: "left", borderRadius: 12,
        background: active ? theme.amberDim : theme.bg3,
        border: `0.5px solid ${active ? "rgba(224,160,48,0.35)" : theme.line}`,
        color: "inherit" }}>
      <div style={{ width: 22, height: 22, borderRadius: 6,
        border: `1.5px solid ${active ? theme.amber : theme.line}`,
        background: active ? theme.amber : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {active && <Icon name="check" size={11} color="#0A0A0E"/>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: active ? theme.amber : theme.ink1 }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1 }}>{sub}</div>}
      </div>
    </button>
  );

  return (
    <>
      <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.01em" }}>
            Log {mode === "post" ? "post-workout" : "pre-workout"}
          </div>
          <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1 }}>Adds to today's Ghidha log</div>
        </div>
        <button className="ry-btn" onClick={onClose}
          style={{ background: theme.bg3, border: `0.5px solid ${theme.line}`, borderRadius: 8, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: theme.ink3 }}>
          <Icon name="x" size={14}/>
        </button>
      </div>

      <div style={{ padding: "8px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        <Card active={pre}  onTap={() => { try { hapticHayahToggle(); } catch {} setPre(p => !p); }}
          title="ON Pre-Workout" sub={`${FUEL_FOODS.pre.servingLabel} · ${FUEL_FOODS.pre.calories} kcal`}/>
        <Card active={oats} onTap={() => { try { hapticHayahToggle(); } catch {} setOats(o => !o); }}
          title="Oats 30g" sub={`${FUEL_FOODS.oats.calories} kcal · ${FUEL_FOODS.oats.protein}g protein`}/>

        {/* Whey: brand toggle + scoop stepper */}
        <div style={{ padding: "12px 14px", borderRadius: 12,
          background: scoops > 0 ? theme.amberDim : theme.bg3,
          border: `0.5px solid ${scoops > 0 ? "rgba(224,160,48,0.35)" : theme.line}` }}>
          <div style={{ display: "flex", gap: 6, marginBottom: scoops > 0 ? 10 : 0 }}>
            {[
              { id: "usn", label: "USN Iso" },
              { id: "on",  label: "ON Gold Standard" },
            ].map(b => (
              <button key={b.id} className="ry-btn"
                onClick={() => { hapticHayahSelection(); setWheyBrand(b.id); }}
                style={{ flex: 1, padding: "6px 8px", borderRadius: 8,
                  background: wheyBrand === b.id ? theme.amber : theme.bg2,
                  color: wheyBrand === b.id ? "#0A0A0E" : theme.ink2,
                  border: `0.5px solid ${wheyBrand === b.id ? theme.amber : theme.line}`,
                  fontSize: 11, fontWeight: 700, letterSpacing: "-0.01em" }}>
                {b.label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: scoops > 0 ? theme.amber : theme.ink1 }}>
                {wheyBrand === "on" ? "ON Gold Standard Whey" : "USN Iso Whey"}
              </div>
              <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1 }}>
                {scoops === 0
                  ? "None"
                  : `${scoops} scoop${scoops > 1 ? "s" : ""} · ${wheyFood?.calories || 0} kcal · ${wheyFood?.protein || 0}g protein`}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {[0, 1, 2].map(n => (
                <button key={n} className="ry-btn" onClick={() => { hapticHayahSelection(); setScoops(n); }}
                  style={{ width: 32, height: 32, borderRadius: 8,
                    background: scoops === n ? theme.amber : theme.bg2,
                    color: scoops === n ? "#0A0A0E" : theme.ink2,
                    border: `0.5px solid ${scoops === n ? theme.amber : theme.line}`,
                    fontSize: 13, fontWeight: 700 }}>
                  {n}
                </button>
              ))}
            </div>
          </div>
          {scoops > 0 && (
            <button className="ry-btn"
              onClick={() => { hapticHayahSelection(); setMilkOverride(!milk); }}
              style={{ marginTop: 10, paddingTop: 10, width: "100%",
                borderTop: `0.5px solid rgba(224,160,48,0.20)`,
                background: "transparent", border: 0, color: "inherit",
                display: "flex", alignItems: "center", gap: 10, textAlign: "left" }}>
              <div style={{ width: 18, height: 18, borderRadius: 5,
                border: `1.5px solid ${milk ? theme.amber : theme.line}`,
                background: milk ? theme.amber : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {milk && <Icon name="check" size={10} color="#0A0A0E"/>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: milk ? theme.amber : theme.ink2 }}>
                  Mix with 250ml full cream milk
                </div>
                <div style={{ fontSize: 10, color: theme.ink3, marginTop: 1 }}>
                  Clover · {FUEL_FOODS.milk.calories} kcal · {FUEL_FOODS.milk.protein}g protein
                </div>
              </div>
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: "12px 16px 0" }}>
        <div style={{ background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 12,
          padding: "10px 14px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6 }}>
          <div style={{ textAlign: "center" }}>
            <div className="ry-mono" style={{ fontSize: 14, color: theme.amber }}>{totals.calories}</div>
            <div style={{ fontSize: 9, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 1 }}>kcal</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div className="ry-mono" style={{ fontSize: 14, color: theme.teal }}>{totals.protein}g</div>
            <div style={{ fontSize: 9, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 1 }}>Protein</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div className="ry-mono" style={{ fontSize: 14, color: theme.ink1 }}>{totals.carbs}g</div>
            <div style={{ fontSize: 9, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 1 }}>Carbs</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div className="ry-mono" style={{ fontSize: 14, color: theme.ink1 }}>{totals.fat}g</div>
            <div style={{ fontSize: 9, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 1 }}>Fat</div>
          </div>
        </div>
      </div>

      <div style={{ padding: "12px 16px 8px", display: "flex", flexDirection: "column", gap: 8 }}>
        <button className="ry-btn" onClick={submit} disabled={items.length === 0}
          style={{ background: theme.amber, color: "#0A0A0E", border: 0, borderRadius: 14,
            fontSize: 15, fontWeight: 700, padding: "14px 20px", width: "100%",
            opacity: items.length === 0 ? 0.5 : 1 }}>
          Log to Ghidha
        </button>
        <button className="ry-btn" onClick={onClose}
          style={{ background: theme.bg3, color: theme.ink1, border: `0.5px solid ${theme.line}`, borderRadius: 14,
            fontSize: 15, fontWeight: 500, padding: "13px 20px", width: "100%" }}>
          Cancel
        </button>
      </div>
    </>
  );
}

function Sheet({ theme, onClose, children }) {
  // Bottom sheet over the whole app — covers tab bar.
  const innerRef = useRef(null);
  useEffect(() => { if (innerRef.current) innerRef.current.scrollTop = 0; }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(0,0,0,0.6)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }}>
      <div onClick={(e) => e.stopPropagation()} ref={innerRef}
        className="ry-sb"
        style={{
          background: theme.bg1, color: theme.ink1,
          borderRadius: "24px 24px 0 0",
          borderTop: `0.5px solid ${theme.line}`,
          width: "100%", maxWidth: 480,
          paddingBottom: "calc(40px + env(safe-area-inset-bottom))",
          maxHeight: "85vh", overflowY: "auto",
          animation: "ry-slide-up 320ms cubic-bezier(.22,.8,.36,1) both",
        }}>
        <div style={{ width: 36, height: 4, background: theme.line, borderRadius: 2, margin: "12px auto 0" }}/>
        {children}
      </div>
    </div>
  );
}

// ───────── FinishScreen ─────────
function FinishScreen({ theme, workout, onDone, onSetRPE }) {
  const [shown, setShown] = useState(false);
  const [rpe, setRpe] = useState(workout?.sessionRPE || null);
  const [sharing, setSharing] = useState(false);
  const [fuelOpen, setFuelOpen] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisError, setAiAnalysisError] = useState(null);

  useEffect(() => { const t = setTimeout(() => setShown(true), 50); return () => clearTimeout(t); }, []);

  useEffect(() => {
    if (!getGeminiKey()) return;
    const run = async () => {
      setAiAnalysisLoading(true);
      try {
        // v2: rich post-workout context — HR zones from Apple Watch,
        // sleep last night, today's nutrition so far, macro goals,
        // structured prior-session comparisons.
        const allData = readAllData();
        const ctx = buildPostWorkoutContext(workout, allData);
        try { publishSources("post-workout", postWorkoutSources(ctx)); } catch {}
        const profile = getUserProfile();
        const sys = composeSystem([
          COACH_VOICE,
          formatProfileForPrompt(profile),
          EVIDENCE_STANDARD,
          OUTPUT_BULLETS_BOLD,
          "Task: Read this workout vs the user's prior sessions and return bullets covering: (1) what they achieved today — PRs, volume change, intensity, with specific numbers; (2) one interesting pattern from priorSessions comparison; (3) if session.watch.hrRecoveryBpm is present, compare today's 1-min HR recovery against the prior-session average and translate it into a fitness verdict (HRR ≥25 bpm = strong conditioning, 15–24 = solid, <15 = sluggish — likely under-recovered or fasted), citing the actual bpm; (4) one specific actionable focus for the next session with an exercise + sets/reps or target weight. Use HR zones, sleep, and today's nutrition where they sharpen the point.",
        ]);
        const text = await callGemini(`Workout session payload:\n\n${JSON.stringify(ctx, null, 2)}`, {
          systemInstruction: sys,
          model: MODELS.postWorkout,
          // 4096 — postWorkout uses gemini-2.5-pro; an 800 cap was at
          // risk of being eaten by Pro's thinking budget before output.
          maxOutputTokens: 4096,
          feature: "postWorkout",
        });
        setAiAnalysis(text);
        try { hapticHayahSuccess(); } catch {}
      } catch (e) {
        setAiAnalysisError(e.message || "Could not load analysis");
        try { hapticError(); } catch {}
      } finally {
        setAiAnalysisLoading(false);
      }
    };
    // Short delay so celebration animation plays first
    const t = setTimeout(run, 1200);
    return () => clearTimeout(t);
  }, []);

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      hapticTap();
      // Pick top sets: highest weight×reps per exercise (cap at 6).
      const topSets = (workout.exercises || [])
        .filter(e => e.sets?.length)
        .map(e => {
          const best = e.sets.reduce((b, s) => {
            const bv = (parseFloat(b.weight) || 0) * (parseFloat(b.reps) || 0);
            const sv = (parseFloat(s.weight) || 0) * (parseFloat(s.reps) || 0);
            return sv > bv ? s : b;
          }, e.sets[0]);
          return {
            name: e.name,
            muscleGroup: (e.muscles || "").split(",")[0]?.trim() || "",
            weight: best.weight,
            reps: parseInt(best.reps, 10) || 0,
            sets: e.sets.length,
          };
        })
        .sort((a, b) => (parseFloat(b.weight) || 0) * b.reps - (parseFloat(a.weight) || 0) * a.reps)
        .slice(0, 6);

      const ts = workout.timestamp ? new Date(workout.timestamp) : new Date();
      const startTime = new Date(ts.getTime() - (workout.duration || 0) * 1000);
      const fmtTime = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

      const canvas = drawExerciseShareCard({
        workoutName: workout.name || "Workout",
        dateStr: workout.date || ts.toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" }),
        startTime: fmtTime(startTime),
        endTime: fmtTime(ts),
        durationMin: Math.max(1, Math.round((workout.duration || 0) / 60)),
        calories: 0,
        avgHr: 0, maxHr: 0,
        hrZoneSeconds: null,
        volume: workout.volume || 0,
        totalSets: workout.sets || 0,
        totalExercises: (workout.exercises || []).filter(e => e.sets?.length).length,
        withTrainer: false,
        topSets,
      });

      const blob = await new Promise(res => canvas.toBlob(b => res(b), "image/png"));
      if (!blob) return;
      const file = new File([blob], `${(workout.name || "workout").replace(/\s+/g, "-").toLowerCase()}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        try { await navigator.share({ files: [file] }); hapticHayahSuccess(); }
        catch { /* user cancelled */ }
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = file.name; a.click();
        URL.revokeObjectURL(url);
        hapticHayahSuccess();
      }
    } finally {
      setSharing(false);
    }
  };
  return (
    <div className="ry-slide" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div className="ry-sb" style={{ flex: 1, overflowY: "auto", paddingBottom: "calc(96px + env(safe-area-inset-bottom))" }}>
        <div className={shown ? "ry-celebrate" : ""} style={{ padding: "28px 20px 0", textAlign: "center" }}>
          <div style={{ fontSize: 44, marginBottom: 6 }}>💪</div>
          <div className="ry-serif" style={{ fontSize: 28, fontWeight: 300, color: theme.ink1, letterSpacing: "-0.04em", lineHeight: 1.1, marginBottom: 4 }}>
            Session Complete
          </div>
          <div style={{ fontSize: 13, color: theme.ink3, marginBottom: 20 }}>{workout.date} · {workout.time}</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: "0 16px", marginBottom: 8 }}>
          <div style={{ gridColumn: "1/-1", padding: "16px 12px", background: theme.bg2, borderRadius: 14,
            display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 12 }}>
            <div className="ry-mono" style={{ fontSize: 36, fontWeight: 300, color: theme.amber, letterSpacing: "-0.03em" }}>{fmtDurHuman(workout.duration)}</div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: theme.ink4 }}>Duration</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "16px 12px", background: theme.bg2, borderRadius: 14 }}>
            <div className="ry-mono" style={{ fontSize: 24, fontWeight: 500, color: theme.teal }}>{workout.sets}</div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.10em", textTransform: "uppercase", color: theme.ink4 }}>Sets</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "16px 12px", background: theme.bg2, borderRadius: 14 }}>
            <div className="ry-mono" style={{ fontSize: 24, fontWeight: 500, color: theme.ink1 }}>
              {workout.volume > 0 ? (workout.volume >= 1000 ? `${(workout.volume / 1000).toFixed(1)}t` : `${workout.volume}kg`) : "—"}
            </div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.10em", textTransform: "uppercase", color: theme.ink4 }}>Volume</div>
          </div>
        </div>

        {workout.watch && <WatchSummary theme={theme} watch={workout.watch} />}

        <SecHd theme={theme} num="01" label="Exercises logged" />
        <div style={{ margin: "0 16px", background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 16, overflow: "hidden" }}>
          {workout.exercises.filter(e => e.sets.length > 0).map((ex, i, arr) => (
            <div key={i} style={{
              padding: "11px 14px",
              borderBottom: i < arr.length - 1 ? `0.5px solid ${theme.lineSoft}` : "none",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: theme.ink1 }}>{ex.name}</div>
              <div className="ry-mono" style={{ fontSize: 12, color: theme.ink3 }}>
                {ex.sets.length} × {ex.sets.map(s => s.reps).join("/")} reps
                {ex.sets[0]?.weight && ex.sets[0].weight !== "BW" && ex.sets[0].weight !== "0" ? ` @ ${ex.sets[0].weight}kg` : ""}
              </div>
            </div>
          ))}
          {workout.cardio && (
            <div style={{ padding: "11px 14px", borderTop: `0.5px solid ${theme.lineSoft}`,
              display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: theme.ink1 }}>🚶 Incline Walk</div>
              <div className="ry-mono" style={{ fontSize: 12, color: theme.coral }}>
                {workout.cardio.duration}m · {workout.cardio.incline}%
              </div>
            </div>
          )}
        </div>

        {getGeminiKey() && (
          <div style={{ margin: "12px 16px 0" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: theme.teal }}>✦ AI Analysis</span>
              <AICostBadge feature="postWorkout" prefix="·" fontSize={9} color={theme.ink4} />
            </div>
            <div style={{ padding: "14px 16px", borderRadius: 14, background: `${theme.teal}0e`, border: `0.5px solid ${theme.teal}30`, minHeight: 80 }}>
              {aiAnalysisLoading ? (
                <div style={{ fontSize: 13, color: theme.ink4, fontStyle: "italic" }}>Analysing your session…</div>
              ) : aiAnalysis ? (
                <AIBulletText text={aiAnalysis} accent={theme.teal} textColor={theme.ink2} fontSize={13.5} />
              ) : aiAnalysisError ? (
                <div style={{ fontSize: 12, color: theme.coral }}>{aiAnalysisError}</div>
              ) : null}
            </div>
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <SnackRow theme={theme} icon="🥤" lead="Post-workout" body={POST_WORKOUT_SNACK} action="Log"
            onClick={() => { hapticHayahSelection(); setFuelOpen(true); }}/>
        </div>

        <div style={{ margin: "10px 16px 10px", padding: 14, background: `linear-gradient(135deg, rgba(224,160,48,0.07), rgba(224,160,48,0.03))`, border: `0.5px solid rgba(224,160,48,0.15)`, borderRadius: 12 }}>
          <div className="ry-mono" style={{ fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: theme.amber, marginBottom: 6 }}>Strength · {HADITH.ref}</div>
          <div className="ry-serif" style={{ fontStyle: "italic", fontSize: 14, color: theme.ink2, lineHeight: 1.5 }}>{HADITH.en}</div>
        </div>

        {/* Session RPE — optional 1–10 effort rating. Captured for future
            progression/adaptation; tapping a number stores it on the session. */}
        <div style={{ margin: "4px 16px 0", padding: 14, background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 12 }}>
          <div className="ry-mono" style={{ fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: theme.ink4, marginBottom: 10 }}>
            How hard was that? <span style={{ opacity: 0.6 }}>· RPE (optional)</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: 4 }}>
            {Array.from({ length: 10 }, (_, i) => i + 1).map(n => {
              const sel = rpe === n;
              return (
                <button key={n} className="ry-btn"
                  onClick={() => { try { hapticHayahSelection(); } catch {} setRpe(n); onSetRPE?.(n); }}
                  style={{ padding: "9px 0", borderRadius: 8, border: `0.5px solid ${sel ? theme.amber : theme.line}`,
                    background: sel ? theme.amber : theme.bg3, color: sel ? "#0A0A0E" : theme.ink2,
                    fontSize: 12, fontWeight: 700, fontFamily: "JetBrains Mono, ui-monospace, monospace" }}>
                  {n}
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 9, color: theme.ink4 }}>
            <span>Easy</span><span>All-out</span>
          </div>
        </div>

        <div style={{ padding: "16px 16px 0", display: "flex", flexDirection: "column", gap: 8 }}>
          <button className="ry-btn" onClick={onDone}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              background: theme.amber, color: "#0A0A0E", border: 0, borderRadius: 14,
              fontSize: 15, fontWeight: 700, padding: "15px 20px", width: "100%" }}>
            <Icon name="check" size={17} color="#0A0A0E"/> Done
          </button>
          <button className="ry-btn" onClick={handleShare} disabled={sharing}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              background: theme.bg3, color: theme.ink1, border: `0.5px solid ${theme.line}`, borderRadius: 14,
              fontSize: 15, fontWeight: 500, padding: "14px 20px", width: "100%",
              opacity: sharing ? 0.6 : 1 }}>
            <Icon name="share" size={17}/> {sharing ? "Generating…" : "Share workout"}
          </button>
        </div>
      </div>

      {fuelOpen && createPortal(
        <Sheet onClose={() => setFuelOpen(false)} theme={theme}>
          <FuelForm theme={theme} onClose={() => setFuelOpen(false)} mode="post" />
        </Sheet>,
        document.body)}
    </div>
  );
}

// Horizontal swipe-to-delete row. Left swipe past 72px commits a delete;
// the row slides off the right before firing onDelete. Tap falls through to
// onTap when the gesture didn't move. Mirrors Ghidha's SwipeRow.
function SwipeRow({ children, onDelete, onTap, danger, disabled }) {
  const [dx, setDx] = useState(0);
  const dxRef = useRef(0);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const movedRef  = useRef(false);
  const lockedRef = useRef(null); // 'horizontal' | 'vertical' | null
  const lastTierRef = useRef(0);
  const containerRef = useRef(null);
  const DELETE_T = -72;

  // Native touch listeners (passive:false so we can preventDefault once the
  // gesture is locked horizontal). Pointer events were unreliable in iOS
  // WKWebView once the page's scroll container started consuming the touch.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const setX = (x) => {
      dxRef.current = x;
      setDx(x);
    };

    const onStart = (e) => {
      if (disabled) return;
      const t = e.touches[0];
      startXRef.current = t.clientX;
      startYRef.current = t.clientY;
      movedRef.current = false;
      lockedRef.current = null;
      lastTierRef.current = 0;
      // Don't set dx yet — wait until we know the gesture is horizontal.
    };

    const onMove = (e) => {
      if (disabled) return;
      const t = e.touches[0];
      const ddx = t.clientX - startXRef.current;
      const ddy = t.clientY - startYRef.current;
      if (!lockedRef.current) {
        if (Math.abs(ddx) > 8 && Math.abs(ddx) > Math.abs(ddy)) lockedRef.current = 'horizontal';
        else if (Math.abs(ddy) > 8) lockedRef.current = 'vertical';
      }
      if (lockedRef.current === 'horizontal') {
        if (e.cancelable) e.preventDefault();
        movedRef.current = true;
        const clamped = Math.max(-160, Math.min(40, ddx));
        setX(clamped);
        const tier = clamped <= DELETE_T ? -1 : 0;
        if (tier !== lastTierRef.current) {
          try { hapticHayahSelection(); } catch {}
          lastTierRef.current = tier;
        }
      }
    };

    const onEnd = () => {
      if (disabled) return;
      const finalDx = dxRef.current;
      if (movedRef.current && finalDx <= DELETE_T && onDelete) {
        try { hapticHayahSwipe(); } catch {}
        setX(-360);
        setTimeout(() => { onDelete(); }, 180);
      } else {
        setX(0);
      }
      lockedRef.current = null;
      // Keep movedRef set briefly so the click that follows touchend is
      // suppressed; then clear it for the next gesture.
      setTimeout(() => { movedRef.current = false; }, 50);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove',  onMove,  { passive: false });
    el.addEventListener('touchend',   onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove',  onMove);
      el.removeEventListener('touchend',   onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [disabled, onDelete]);

  const onClickCapture = (e) => {
    if (movedRef.current) { e.preventDefault(); e.stopPropagation(); }
  };
  const onClick = () => {
    if (movedRef.current) return;
    onTap && onTap();
  };

  return (
    <div style={{ position:'relative', overflow:'hidden', borderRadius:16 }}>
      <div style={{ position:'absolute', inset:0, background:`linear-gradient(90deg, transparent 30%, ${danger}99 70%, ${danger} 100%)`, display:'flex', alignItems:'center', justifyContent:'flex-end', padding:'0 22px', borderRadius:16, opacity: dx < -8 ? 1 : 0, transition:'opacity 0.15s ease' }}>
        <span style={{ fontSize:11, fontWeight:800, color:'#fff', letterSpacing:'0.16em', fontFamily:'monospace' }}>DELETE</span>
      </div>
      <div
        ref={containerRef}
        onClick={onClick}
        onClickCapture={onClickCapture}
        style={{ transform:`translateX(${dx}px)`, transition: dx === 0 || dx === -360 ? 'transform 0.22s cubic-bezier(0.22,0.61,0.36,1)' : 'none', touchAction:'pan-y', position:'relative' }}
      >
        {children}
      </div>
    </div>
  );
}

// ───────── ActivityScreen ─────────
// Renders an AI bullet list. Splits on lines that start with `•`, then
// renders **bold** segments inline. Plain-text fallback if no bullets.
function InsightBullets({ text, theme }) {
  if (!text) return null;
  const lines = text.split(/\n+/).map(s => s.trim()).filter(Boolean);
  const bullets = lines.filter(l => l.startsWith("•")).map(l => l.replace(/^•\s*/, ""));
  const items = bullets.length > 0 ? bullets : lines;
  const renderInline = (s) => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} style={{ color: theme.ink1, fontWeight: 700 }}>{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {items.map((b, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <span style={{ color: theme.teal, fontWeight: 700, lineHeight: 1.55, flex: "0 0 auto" }}>•</span>
          <span style={{ flex: 1 }}>{renderInline(b)}</span>
        </div>
      ))}
    </div>
  );
}

// Analyses the user's MOST RECENT workout (history[0]) through a body-
// recomposition lens (fat loss + lean-mass retention). Cached by workout
// timestamp so a fresh workout busts the cache automatically.
function useLastWorkoutInsight(history) {
  const [recap, setRecap] = useState({ text: "", loading: false });
  const lastWorkout = history && history.length > 0 ? history[0] : null;
  const lastTs = lastWorkout?.timestamp || 0;
  useEffect(() => {
    if (!getGeminiKey() || !lastWorkout) return;
    const CACHE_KEY = "riyadah:lastWorkoutInsight";
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null"); } catch {}
    const fresh = cached && cached.workoutTs === lastTs && cached.text;
    if (fresh) { setRecap({ text: cached.text, loading: false }); return; }
    setRecap(r => ({ ...r, loading: true }));
    const allData = readAllData();
    const ctx = buildPostWorkoutContext(lastWorkout, allData);
    try { publishSources("last-session", postWorkoutSources(ctx)); } catch {}
    const sys = composeSystem([
      COACH_VOICE,
      formatProfileForPrompt(getUserProfile()),
      EVIDENCE_STANDARD,
      OUTPUT_BULLETS_BOLD,
      // Body-recomp lens. The user is a 47yo male chasing visible abs —
      // lean-mass retention is the priority over raw calorie burn, so the
      // critique should reward hypertrophy-range work on compounds and
      // flag low-intensity / low-volume sessions, not the other way round.
      [
        "Task: Analyse the user's most recent workout (`session`) through a body-recomposition lens — fat loss while preserving lean mass to reveal a six-pack. Compare against `priorSessions` to spot trends.",
        "",
        "Return 3 bullets in this order (no leading label — start the sentence directly):",
        "  1. The single best thing this session did for the goal (PR, heavy compound, sets in the 6–12 hypertrophy range, strong intensity).",
        "  2. The one thing that worked against the goal (low volume vs prior sessions, muscle imbalance, missed compound, too short to drive adaptation, no progressive overload).",
        "  3. ONE concrete prescription for next session — name a specific exercise plus sets×reps or a target weight derived from the data.",
        "",
        "Never recommend cutting resistance training in favour of cardio — at 25% bodyfat preserving muscle outranks burning calories. If today was a cardio-only session, still anchor the prescription on resistance work for next time.",
      ].join("\n"),
    ]);
    callGemini(
      `Last workout payload:\n\n${JSON.stringify(ctx, null, 2)}`,
      {
        systemInstruction: sys,
        model: MODELS.lastWorkoutInsight,
        // 4096 — gemini-2.5-pro thinking tokens easily overflow a 300 cap
        // before output starts.
        maxOutputTokens: 4096,
        feature: "lastWorkoutInsight",
      }
    ).then(text => {
      const trimmed = (text || "").trim();
      if (!trimmed) { setRecap({ text: "", loading: false }); return; }
      setRecap({ text: trimmed, loading: false });
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ text: trimmed, generatedAt: Date.now(), workoutTs: lastTs })); } catch {}
      try { hapticHayahSuccess(); } catch {}
    }).catch(() => { setRecap({ text: "", loading: false }); try { hapticError(); } catch {} });
  }, [lastTs]);
  return recap;
}

function ActivityScreen({ theme, history, onStartNew, onSubTab, onDeleteSession, onSyncWatch, watchSyncing, watchSyncToast }) {
  const [selected, setSelected] = useState(null);
  const [streakInfoOpen, setStreakInfoOpen] = useState(false);
  const [shareToast, setShareToast] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // index | null
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedEx, setSelectedEx] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [perExOpen, setPerExOpen] = useState(() => {
    try { return localStorage.getItem("riyadah:perExerciseOpen") === "1"; } catch { return false; }
  });
  const togglePerEx = () => {
    hapticHayahSelection();
    setPerExOpen(prev => {
      const next = !prev;
      try { localStorage.setItem("riyadah:perExerciseOpen", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  const totals = useMemo(() => {
    const sessions = history.length;
    const sets = history.reduce((t, w) => t + (w.sets || 0), 0);
    const volume = history.reduce((t, w) => t + workoutVolume(w), 0);
    const avgDuration = sessions > 0 ? Math.round(history.reduce((t, w) => t + (w.duration || 0), 0) / sessions) : 0;
    return { sessions, sets, volume, avgDuration };
  }, [history]);

  const streaks = useMemo(() => computeWorkoutStreaks(history), [history]);

  const weeklyVol = useMemo(() => {
    const thisWeekStart = startOfWeekMon(Date.now());
    const buckets = [];
    for (let i = 7; i >= 0; i--) {
      const ws = thisWeekStart - i * 7 * 86400000;
      buckets.push({ ws, volume: 0, sessions: [] });
    }
    for (const w of history) {
      if (!w.timestamp) continue;
      const ws = startOfWeekMon(w.timestamp);
      const b = buckets.find(b => b.ws === ws);
      if (b) { b.volume += workoutVolume(w); b.sessions.push(w); }
    }
    return buckets;
  }, [history]);

  const maxWeekVol = Math.max(1, ...weeklyVol.map(b => b.volume));

  // History aggregator keyed by CANONICAL exercise id (resolveExerciseId)
  // not the raw ex.name string. This means "Dumbbell Goblet Squats" /
  // "DB Goblet Squat" / "Goblet Squats" all flow into the SAME stat line
  // and PR tracking accumulates across name variants. The display name
  // is the canonical library name so the UI stays clean.
  // Also surfaces an `aliasNames` set so the detail view can show the
  // user every spelling that's contributed to this exercise's history.
  const byExercise = useMemo(() => {
    const map = new Map();
    history.forEach(w => {
      (w.exercises || []).forEach(ex => {
        if (!ex.sets?.length) return;
        const canonId   = resolveExerciseId(ex.name, ex.id);
        const canonName = resolveExerciseName(ex.name, ex.id);
        const entry = map.get(canonId) || {
          id: canonId,
          name: canonName,
          type: ex.type,
          muscles: ex.muscles,
          sessions: [], totalSets: 0, totalReps: 0, bestWeight: 0, bestSet: null,
          aliasNames: new Set(),
        };
        if (ex.name && ex.name !== canonName) entry.aliasNames.add(ex.name);
        const setsCount = ex.sets.length;
        let bestInSession = ex.sets[0];
        let sessionVol = 0;
        let sessionReps = 0;
        for (const s of ex.sets) {
          const sw = parseFloat(s.weight) || 0;
          const sr = parseInt(s.reps, 10) || 0;
          sessionReps += sr;
          sessionVol += sw * sr;
          if (sw > (parseFloat(bestInSession.weight) || 0)) bestInSession = s;
          if (sw > entry.bestWeight) {
            entry.bestWeight = sw;
            entry.bestSet = { weight: sw, reps: sr, date: w.date, timestamp: w.timestamp };
          }
        }
        entry.totalSets += setsCount;
        entry.totalReps += sessionReps;
        entry.sessions.push({
          date: w.date, timestamp: w.timestamp, sets: setsCount,
          bestWeight: parseFloat(bestInSession.weight) || 0,
          reps: parseInt(bestInSession.reps, 10) || 0,
          volume: sessionVol,
        });
        map.set(canonId, entry);
      });
    });
    // Convert aliasNames Set → Array so it serialises cleanly to consumers.
    return Array.from(map.values())
      .map(e => ({ ...e, aliasNames: Array.from(e.aliasNames) }))
      .sort((a, b) => b.totalSets - a.totalSets);
  }, [history]);

  const heatmap = useMemo(() => {
    const todayStart = startOfDay(Date.now());
    const dow = new Date(todayStart).getDay();
    const back = dow === 0 ? 6 : dow - 1;
    const lastMon = todayStart - back * 86400000;
    const rows = [];
    for (let r = 11; r >= 0; r--) {
      const weekStart = lastMon - r * 7 * 86400000;
      const week = [];
      for (let c = 0; c < 7; c++) {
        const dayStart = weekStart + c * 86400000;
        if (dayStart > todayStart) { week.push({ ts: dayStart, vol: 0, future: true, sessionIdx: -1 }); continue; }
        let vol = 0; let sessionIdx = -1;
        history.forEach((w, i) => {
          if (!w.timestamp) return;
          if (startOfDay(w.timestamp) === dayStart) {
            vol += workoutVolume(w);
            if (sessionIdx < 0) sessionIdx = i;
          }
        });
        week.push({ ts: dayStart, vol, future: false, sessionIdx });
      }
      rows.push(week);
    }
    return rows;
  }, [history]);
  const maxDayVol = Math.max(1, ...heatmap.flat().map(c => c.vol));

  const recap = useLastWorkoutInsight(history);

  const thisWeek = weeklyVol[7];
  const lastWeek = weeklyVol[6];
  const delta = (thisWeek?.volume || 0) - (lastWeek?.volume || 0);
  const showDelta = (lastWeek?.volume || 0) > 0;

  // Current-month breakdown by ISO week (Mon-start). One bucket per week
  // that touches the month, with sessions tallied for any workout whose
  // calendar date falls inside the month — the user can see at a glance
  // how the month is shaping up vs the bar showing the current week.
  const monthByWeek = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth();
    const firstOfMonth = new Date(y, m, 1).getTime();
    const lastOfMonth  = new Date(y, m + 1, 0).getTime();
    const buckets = [];
    let cursor = startOfWeekMon(firstOfMonth);
    while (cursor <= lastOfMonth) {
      buckets.push({ ws: cursor, sessions: 0, volume: 0 });
      cursor += 7 * 86400000;
    }
    for (const w of history) {
      if (!w.timestamp) continue;
      const wd = new Date(w.timestamp);
      if (wd.getFullYear() !== y || wd.getMonth() !== m) continue;
      const ws = startOfWeekMon(w.timestamp);
      const b = buckets.find(b => b.ws === ws);
      if (b) { b.sessions += 1; b.volume += workoutVolume(w); }
    }
    return buckets;
  }, [history]);
  const monthTotal = monthByWeek.reduce((s, b) => s + b.sessions, 0);
  const monthMaxSessions = Math.max(1, ...monthByWeek.map(b => b.sessions));
  const currentWeekStart = startOfWeekMon(Date.now());

  // Builds the WhatsApp-ready text summary that Steve (PT) gets so he
  // can adjust the next session without having to scroll the app. The
  // shape is deliberately scannable on a phone screen: per-session
  // header + per-exercise top set with PR/trend markers, then PR
  // window list, then short "what's lacking" bullets driven by
  // muscleRecency + simple plateau detection.
  function buildTrainerSummary(n = 3) {
    const recent = [...history]
      .filter(w => w.timestamp)
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, n);
    if (recent.length === 0) return null;

    const fmtDate = (ts) => new Date(ts).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
    const fmtTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const fmtVol  = (kg) => kg >= 1000 ? `${(kg / 1000).toFixed(1)}t` : `${Math.round(kg)}kg`;
    const fmtDur  = (sec) => sec ? `${Math.max(1, Math.round(sec / 60))}m` : null;

    const sep = "━━━━━━━━━━━━━━━━━";
    const lines = [];
    lines.push(`🏋️ Sirat — last ${recent.length} session${recent.length === 1 ? "" : "s"}`);
    lines.push("");

    const prWindow = []; // collected PRs across the N sessions

    recent.forEach((w, idx) => {
      lines.push(sep);
      lines.push(`${idx + 1}. ${w.name || "Workout"}`);
      const metaBits = [
        fmtDate(w.timestamp),
        fmtTime(w.timestamp),
        fmtDur(w.duration),
        w.sets ? `${w.sets} sets` : null,
        w.volume ? fmtVol(w.volume) : null,
      ].filter(Boolean);
      lines.push(metaBits.join(" · "));
      if (w.watch && (w.watch.avgHr || w.watch.maxHr)) {
        const hrBits = [];
        if (w.watch.avgHr) hrBits.push(`avg ${w.watch.avgHr}`);
        if (w.watch.maxHr) hrBits.push(`max ${w.watch.maxHr}`);
        if (w.watch.calories) hrBits.push(`${w.watch.calories} kcal`);
        lines.push(`HR: ${hrBits.join(" · ")}`);
      }
      lines.push("");

      const exes = (w.exercises || []).filter(e => e.sets?.length);
      for (const ex of exes) {
        const setsArr = ex.sets;
        const top = setsArr.reduce((b, s) => {
          const bw = parseFloat(b?.weight) || 0;
          const sw = parseFloat(s?.weight) || 0;
          if (sw > bw) return s;
          if (sw === bw && (parseInt(s?.reps, 10) || 0) > (parseInt(b?.reps, 10) || 0)) return s;
          return b;
        }, setsArr[0]);
        const topW = parseFloat(top?.weight) || 0;
        const topR = parseInt(top?.reps, 10) || 0;
        const isBW = top?.weight === "BW" || topW === 0;
        const valueStr = isBW
          ? `${setsArr.length}×${topR}`
          : `${setsArr.length}×${topR} @ ${topW}kg`;

        // PR / trend marker against prior history (same logic as the
        // per-exercise row in the Activity screen — beat last session
        // for PR, beat all-time for ALL-TIME tag).
        const canonId = resolveExerciseId(ex.name, ex.id);
        const entry   = byExercise.find(e => e.id === canonId);
        const allSess = entry?.sessions || [];
        const priorSess = allSess
          .filter(s => s.timestamp !== w.timestamp)
          .sort((a, b) => b.timestamp - a.timestamp);
        const prevSessBest = priorSess[0]?.bestWeight || 0;
        const priorBest    = priorSess.reduce((m, s) => Math.max(m, s.bestWeight || 0), 0);
        const isFirstEver  = allSess.length === 1;
        const isPR         = !isFirstEver && topW > 0 && topW > prevSessBest;
        const isAllTimePR  = isPR && topW > priorBest;

        let marker = "";
        if (isPR) {
          const delta = +(topW - prevSessBest).toFixed(1);
          marker = isAllTimePR ? `🏆+${delta}kg ATIME` : `🏆+${delta}kg`;
          prWindow.push({
            name: ex.name,
            delta,
            topW, topR,
            allTime: isAllTimePR,
          });
        } else if (isFirstEver && topW > 0) {
          marker = "BASELINE";
        } else if (!isFirstEver && priorSess[0]) {
          if (topW < prevSessBest) marker = "▼";
          else                     marker = "—";
        }
        // Pad the name + marker so the value column lines up nicely.
        lines.push(`• ${ex.name}${marker ? `  ${marker}` : ""}`);
        lines.push(`    ${valueStr}`);
      }
      lines.push("");
    });

    // PR window summary
    if (prWindow.length > 0) {
      lines.push(sep);
      lines.push(`🏆 PRs in this window (${prWindow.length})`);
      for (const p of prWindow) {
        const tag = p.allTime ? " · ALL-TIME" : "";
        lines.push(`• ${p.name}: +${p.delta}kg → ${p.topW}kg × ${p.topR}${tag}`);
      }
      lines.push("");
    }

    // "What's lacking" — deterministic, actionable bullets:
    //   • Muscle regions with no work in the last 7 days
    //   • Exercises plateaued (same top weight 3+ sessions in a row)
    //   • Exercises trending down across the last 2-3 sessions
    //   • Session-volume comparison vs 4-week avg if low
    const lacking = [];

    // Muscle group recency across full history (not just the window)
    const groupRecency = {}; // region → days ago of most recent work
    const now = Date.now();
    for (const w of history) {
      const ts = w.timestamp; if (!ts) continue;
      const daysAgo = Math.round((now - ts) / 86400000);
      for (const ex of (w.exercises || [])) {
        const muscles = String(ex.muscles || ex.primary || "")
          .split(",").map(s => s.trim()).filter(Boolean);
        for (const mu of muscles) {
          const region = (
            /chest|shoulder|tricep/i.test(mu) ? "Push" :
            /back|lat|bicep|rear delt|forearm/i.test(mu) ? "Pull" :
            /leg|quad|hamstring|glute|calv|abductor|adductor/i.test(mu) ? "Legs" :
            /core|oblique|lower back/i.test(mu) ? "Core" : null
          );
          if (!region) continue;
          if (groupRecency[region] == null || daysAgo < groupRecency[region]) {
            groupRecency[region] = daysAgo;
          }
        }
      }
    }
    for (const region of ["Push", "Pull", "Legs", "Core"]) {
      const d = groupRecency[region];
      if (d == null) lacking.push(`${region}: not trained in recent history`);
      else if (d >= 7) lacking.push(`${region}: ${d} days since last set`);
    }

    // Plateau / trend-down detection across byExercise
    for (const ex of byExercise.slice(0, 25)) { // bound the scan
      const sess = [...ex.sessions].sort((a, b) => b.timestamp - a.timestamp).slice(0, 3);
      if (sess.length < 3) continue;
      const weights = sess.map(s => s.bestWeight || 0).filter(w => w > 0);
      if (weights.length < 3) continue;
      const allSame = weights.every(w => w === weights[0]);
      const allDown = weights[0] < weights[1] && weights[1] <= weights[2];
      if (allSame) lacking.push(`${ex.name} plateaued at ${weights[0]}kg × 3 sessions`);
      else if (allDown) lacking.push(`${ex.name} trending down: ${weights[2]}→${weights[1]}→${weights[0]}kg`);
    }

    // Session-volume signal — if avg volume of last 3 sessions is below
    // 70% of the prior 4-week average, flag it.
    if (recent.length === 3) {
      const recentVolAvg = recent.reduce((s, w) => s + (w.volume || workoutVolume(w)), 0) / 3;
      const prior = history.filter(w => w.timestamp && !recent.find(r => r.timestamp === w.timestamp));
      const prior4wkCut = now - 28 * 86400000;
      const prior4wk = prior.filter(w => w.timestamp >= prior4wkCut);
      if (prior4wk.length >= 3) {
        const priorAvg = prior4wk.reduce((s, w) => s + (w.volume || workoutVolume(w)), 0) / prior4wk.length;
        if (priorAvg > 0 && recentVolAvg < priorAvg * 0.7) {
          lacking.push(`Volume down: last 3 avg ${fmtVol(recentVolAvg)} vs prior 4-week avg ${fmtVol(priorAvg)}`);
        }
      }
    }

    if (lacking.length > 0) {
      lines.push(sep);
      lines.push("⚠️ What's lacking");
      for (const l of lacking.slice(0, 8)) lines.push(`• ${l}`);
      lines.push("");
    }

    lines.push(sep);
    lines.push("Sent from Sirat");

    return lines.join("\n");
  }

  const shareWithTrainer = async () => {
    const text = buildTrainerSummary(3);
    if (!text) { setShareToast("No sessions to share yet"); setTimeout(() => setShareToast(null), 1800); return; }
    try { hapticTap(); } catch {}
    // Native share sheet — WhatsApp shows up there. On web, fall back to
    // clipboard so the user can paste anywhere.
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ text, title: "Last 3 sessions · for Steve" });
        try { hapticHayahSuccess(); } catch {}
        return;
      } catch (e) { /* user cancelled — fall through to clipboard */ }
    }
    try {
      await navigator.clipboard?.writeText(text);
      setShareToast("Copied to clipboard — paste in WhatsApp");
      try { hapticHayahSuccess(); } catch {}
    } catch {
      setShareToast("Could not share — clipboard blocked");
    }
    setTimeout(() => setShareToast(null), 2200);
  };

  return (
    <div className="ry-slide" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <SubTabs theme={theme} active={2} onChange={onSubTab} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px 6px", gap: 8 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.03em" }}>Activity</div>
          <div style={{ fontSize: 11, color: theme.ink3 }}>{history.length} sessions</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {history.length > 0 && (
            <button className="ry-btn" onClick={shareWithTrainer} aria-label="Share last 3 sessions with trainer"
              style={{ background: theme.bg3, color: theme.ink2, border: `0.5px solid ${theme.line}`,
                borderRadius: 10, fontSize: 12, fontWeight: 600, padding: "6px 10px",
                display: "flex", alignItems: "center", gap: 4 }}>
              📤 Steve
            </button>
          )}
          {onSyncWatch && (
            <button className="ry-btn" onClick={() => { try { hapticTap(); } catch {} onSyncWatch(); }} disabled={watchSyncing} aria-label="Sync Apple Watch"
              style={{ background: theme.bg3, color: theme.ink2, border: `0.5px solid ${theme.line}`,
                borderRadius: 10, fontSize: 12, fontWeight: 600, padding: "6px 10px",
                opacity: watchSyncing ? 0.5 : 1, display: "flex", alignItems: "center", gap: 4 }}>
              ⌚ {watchSyncing ? "Syncing…" : "Sync"}
            </button>
          )}
          <button className="ry-btn" onClick={onStartNew}
            style={{ background: theme.amber, color: "#0A0A0E", border: 0, borderRadius: 10, fontSize: 12, fontWeight: 700, padding: "6px 12px" }}>+ New</button>
        </div>
      </div>
      {shareToast && (
        <div role="status" aria-live="polite" style={{
          margin: "0 16px 8px", padding: "8px 12px", borderRadius: 10,
          background: theme.bg3, border: `0.5px solid ${theme.line}`,
          color: theme.ink2, fontSize: 12, textAlign: "center",
        }}>{shareToast}</div>
      )}
      {watchSyncToast && (
        <div style={{ margin: "0 16px 8px", padding: "8px 12px", background: theme.bg2,
          border: `0.5px solid ${theme.line}`, borderRadius: 10, fontSize: 12, color: theme.ink2, textAlign: "center" }}>
          {watchSyncToast}
        </div>
      )}

      <div className="ry-sb" style={{ flex: 1, overflowY: "auto", paddingBottom: "calc(96px + env(safe-area-inset-bottom))" }}>
        {history.length === 0 && (
          <div style={{ padding: "60px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.ink1, marginBottom: 6 }}>No sessions yet</div>
            <div style={{ fontSize: 13, color: theme.ink3, lineHeight: 1.5, marginBottom: 20 }}>Your completed workouts will appear here</div>
            <button className="ry-btn" onClick={onStartNew}
              style={{ background: theme.amber, color: "#0A0A0E", border: 0, borderRadius: 14, fontSize: 15, fontWeight: 700, padding: "15px 20px" }}>
              Start first workout
            </button>
          </div>
        )}

        {history.length > 0 && (
          <>
            {/* Section 1 — Hero strip */}
            <div style={{ padding: "0 16px" }}>
              <div style={{ background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 16,
                padding: "14px 16px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <div style={{ textAlign: "center" }}>
                  <div className="ry-mono" style={{ fontSize: 20, fontWeight: 500, color: theme.amber }}>{monthTotal}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.ink4, marginTop: 2 }}>This month</div>
                  {/* Per-week sessions sparkline — current week is amber,
                      prior weeks of the month are teal. Empty weeks
                      render at 25% opacity so the month outline is
                      still legible. */}
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end", gap: 3, marginTop: 6, height: 14 }}>
                    {monthByWeek.map((b, i) => {
                      const h = b.sessions === 0 ? 2 : Math.max(3, Math.round((b.sessions / monthMaxSessions) * 14));
                      const isCurrent = b.ws === currentWeekStart;
                      return (
                        <div key={i} title={`Week ${i + 1}: ${b.sessions} session${b.sessions === 1 ? '' : 's'}`}
                          style={{
                            width: 6, height: h, borderRadius: 2,
                            background: isCurrent ? theme.amber : theme.teal,
                            opacity: b.sessions === 0 ? 0.25 : 1,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div className="ry-mono" style={{ fontSize: 20, fontWeight: 500, color: theme.teal }}>{fmtVolKg(thisWeek?.volume || 0)}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.ink4, marginTop: 2 }}>Volume</div>
                  {showDelta && (
                    <div className="ry-mono" style={{ fontSize: 10, color: delta >= 0 ? theme.teal : theme.coral, marginTop: 2 }}>
                      {delta >= 0 ? "▲" : "▼"} {fmtVolKg(Math.abs(delta))}
                    </div>
                  )}
                </div>
                <BigStat
                  theme={theme}
                  value={`🔥 ${streaks.current}`}
                  label="Streak"
                  color={theme.amber}
                  onClick={() => { try { hapticTap(); } catch {} setStreakInfoOpen(true); }}
                />
              </div>
            </div>

            {getGeminiKey() && (recap.loading || recap.text) && (
              <div style={{ margin: "12px 16px 0", padding: "12px 14px",
                background: `linear-gradient(135deg, rgba(79,217,194,0.08), rgba(79,217,194,0.02))`,
                border: `0.5px solid rgba(79,217,194,0.25)`, borderRadius: 12,
                fontSize: 13, color: theme.ink2, lineHeight: 1.55 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.teal }}>✦ Last workout</span>
                  <AICostBadge feature="lastWorkoutInsight" prefix="·" fontSize={9} color={theme.ink4} />
                </div>
                {recap.loading
                  ? <span style={{ color: theme.ink3, fontStyle: "italic" }}>Analysing your last session…</span>
                  : <AIBulletText text={recap.text} accent={theme.teal} textColor={theme.ink2} fontSize={13} />}
              </div>
            )}

            {/* Section 2 — Weekly volume bar chart */}
            <SecHd theme={theme} num="" label="Weekly volume · 8 weeks" />
            <div style={{ padding: "0 16px" }}>
              <div style={{ background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 16, padding: "14px 14px 10px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(8,1fr)", gap: 6, alignItems: "end", height: 80 }}>
                  {weeklyVol.map((b, i) => {
                    const h = b.volume > 0 ? Math.max(4, Math.round((b.volume / maxWeekVol) * 100)) : 2;
                    const isSel = selectedWeek === b.ws;
                    return (
                      <button key={b.ws} className="ry-btn" onClick={() => { hapticHayahSelection(); setSelectedWeek(isSel ? null : b.ws); }}
                        style={{ background: "transparent", border: 0, padding: 0,
                          display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", height: "100%" }}>
                        <div style={{
                          width: "100%",
                          height: `${h}%`,
                          background: isSel ? theme.amber : (b.volume > 0 ? theme.teal : theme.line),
                          borderRadius: "4px 4px 2px 2px",
                          transition: "all .2s",
                        }}/>
                        <div className="ry-mono" style={{ fontSize: 9, color: i === 7 ? theme.amber : theme.ink4, marginTop: 4 }}>
                          {i === 7 ? "now" : `−${7 - i}`}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {selectedWeek !== null && (() => {
                  const b = weeklyVol.find(b => b.ws === selectedWeek);
                  if (!b) return null;
                  const wsLabel = new Date(b.ws).toLocaleDateString([], { day: "numeric", month: "short" });
                  return (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `0.5px solid ${theme.lineSoft}` }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: theme.ink1 }}>Week of {wsLabel}</div>
                        <div className="ry-mono" style={{ fontSize: 12, color: theme.amber }}>{fmtVolKg(b.volume)}</div>
                      </div>
                      {b.sessions.length === 0 && (
                        <div style={{ fontSize: 11, color: theme.ink3 }}>No sessions this week</div>
                      )}
                      {b.sessions.map((w, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 0" }}>
                          <span style={{ fontSize: 12, color: theme.ink2 }}>{w.name}</span>
                          <span className="ry-mono" style={{ fontSize: 11, color: theme.ink3 }}>{w.date} · {w.sets} sets</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Section 4 — Sessions list */}
            <SecHd theme={theme} num="" label={`Sessions · ${history.length}`} />
            {history.map((w, i) => (
              <div key={i} className="ry-fade" style={{ margin: "0 16px 10px", borderRadius: 16 }}>
                <SwipeRow
                  danger={theme.coral}
                  disabled={selected === i}
                  onTap={() => { hapticHayahSelection(); setSelected(selected === i ? null : i); }}
                  onDelete={() => { setSelected(null); setConfirmDel(i); }}
                >
                  <div style={{ background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 16, overflow: "hidden", cursor: "pointer", userSelect: "none", WebkitUserSelect: "none" }}>
                    <div style={{ width: "100%", padding: "14px 16px 12px", borderBottom: `0.5px solid ${theme.line}`,
                        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: theme.ink1, letterSpacing: "-0.01em" }}>{w.aiName || w.name}</div>
                          {w.prs > 0 && <PrPill theme={theme}>🏆 {w.prs} PR{w.prs > 1 ? "s" : ""}</PrPill>}
                        </div>
                        <div style={{ fontSize: 11, color: theme.ink3, marginTop: 2 }}>{w.date} · {w.time}</div>
                        <MuscleChips theme={theme} muscles={extractMuscleGroups(w)} />
                      </div>
                      <div style={{ color: theme.ink4, fontSize: 14 }}>{selected === i ? "▲" : "▼"}</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", padding: "12px 16px", gap: 8 }}>
                      <Stat theme={theme} value={fmtDurHuman(w.duration)} label="Duration" />
                      <Stat theme={theme} value={w.sets} label="Sets" valueColor={theme.teal} />
                      <Stat theme={theme} value={w.volume > 0 ? (w.volume >= 1000 ? `${(w.volume / 1000).toFixed(1)}t` : `${w.volume}kg`) : "—"} label="Volume" />
                    </div>
                    <WatchInlineRow theme={theme} watch={w.watch} />
                  </div>
                </SwipeRow>
                {selected === i && (
                  <div style={{ marginTop: 4, background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 12, padding: "10px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                    {w.exercises.filter(e => e.sets.length > 0).map((ex, ei) => {
                      // ── Top set (heaviest, tie-broken by reps) ──
                      const setsArr = ex.sets || [];
                      const top = setsArr.reduce((b, s) => {
                        const bw = parseFloat(b?.weight) || 0;
                        const sw = parseFloat(s?.weight) || 0;
                        if (sw > bw) return s;
                        if (sw === bw && (parseInt(s?.reps, 10) || 0) > (parseInt(b?.reps, 10) || 0)) return s;
                        return b;
                      }, setsArr[0]);
                      const topW    = parseFloat(top?.weight) || 0;
                      const topR    = parseInt(top?.reps, 10) || 0;
                      const isBW    = top?.weight === "BW" || topW === 0;
                      // Compact format only if EVERY set matches the top —
                      // varied weights/reps deserve the explicit "top X kg
                      // × Y reps" so the heaviest is unambiguous.
                      const uniform = setsArr.every(s =>
                        (parseFloat(s?.weight) || 0) === topW &&
                        (parseInt(s?.reps, 10) || 0) === topR
                      );
                      let valueStr;
                      if (isBW) {
                        valueStr = `${setsArr.length}×${topR}`;
                      } else if (uniform) {
                        valueStr = `${setsArr.length}×${topR} @ ${topW}kg`;
                      } else {
                        valueStr = `${setsArr.length} sets · top ${topW}kg × ${topR}`;
                      }
                      // ── Canonical lookup + history slice ──
                      const canonId = resolveExerciseId(ex.name, ex.id);
                      const canonNm = resolveExerciseName(ex.name, ex.id);
                      const entry   = byExercise.find(e => e.id === canonId);
                      const allSess = entry?.sessions || [];
                      // Prior sessions (everything except this one), sorted
                      // newest-first for the trend lookup.
                      const priorSess = allSess
                        .filter(s => s.timestamp !== w.timestamp)
                        .sort((a, b) => b.timestamp - a.timestamp);
                      const priorBest = priorSess.reduce((m, s) => Math.max(m, s.bestWeight || 0), 0);
                      const prevSessBest = priorSess[0]?.bestWeight || 0;
                      const isFirstEver = allSess.length === 1; // only this workout has logged it
                      // PR semantics aligned with the session-level pill
                      // (confirmFinish in the Active screen): an exercise
                      // counts as a PR when today's top weight beats the
                      // immediately previous session. Lifetime best is
                      // surfaced separately via the ALL-TIME badge so the
                      // user can still see the rarer milestone.
                      const isPR        = !isFirstEver && topW > 0 && topW > prevSessBest;
                      const isAllTimePR = isPR && topW > priorBest;
                      const prDelta     = isPR ? +(topW - prevSessBest).toFixed(1) : 0;
                      // Trend dot for non-PR / non-baseline rows: only
                      // surfaces down/flat now — "up" cases are already
                      // upgraded to the 🏆 PR badge above.
                      let trend = null;
                      if (!isPR && !isFirstEver && priorSess[0]) {
                        if (topW < prevSessBest) trend = "down";
                        else                     trend = "flat";
                      }
                      const trendChar  = trend === "down" ? "▼" : trend === "flat" ? "—" : null;
                      const trendColor = trend === "down" ? "#ff8a65" : theme.ink4;
                      return (
                        <button
                          key={ei}
                          onClick={() => { hapticHayahSelection(); setSelectedEx(canonNm); }}
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "4px 0", gap: 8, background: "none", border: 0,
                            textAlign: "left", cursor: "pointer", width: "100%",
                            WebkitTapHighlightColor: "transparent",
                          }}
                        >
                          <span style={{ fontSize: 12, color: theme.ink2, display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ex.name}</span>
                            {isPR && (
                              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: theme.amber, background: "rgba(224,160,48,0.12)", border: `0.5px solid rgba(224,160,48,0.42)`, borderRadius: 4, padding: "1px 5px", flexShrink: 0, whiteSpace: "nowrap" }}>
                                🏆 +{prDelta}kg{isAllTimePR ? " · ALL-TIME" : ""}
                              </span>
                            )}
                            {isFirstEver && topW > 0 && (
                              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", color: theme.ink3, background: "rgba(255,255,255,0.04)", border: `0.5px solid rgba(255,255,255,0.10)`, borderRadius: 4, padding: "1px 5px", flexShrink: 0 }}>
                                BASELINE
                              </span>
                            )}
                          </span>
                          {trendChar && (
                            <span style={{ fontSize: 10, color: trendColor, marginRight: 4, fontWeight: 700, flexShrink: 0 }}>{trendChar}</span>
                          )}
                          <span className="ry-mono" style={{ fontSize: 11, color: isPR ? theme.amber : theme.ink3, fontWeight: isPR ? 700 : 400, flexShrink: 0 }}>
                            {valueStr}
                          </span>
                        </button>
                      );
                    })}
                    {w.cardio && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0" }}>
                        <span style={{ fontSize: 12, color: theme.ink2 }}>🚶 Incline Walk</span>
                        <span className="ry-mono" style={{ fontSize: 11, color: theme.coral }}>{w.cardio.duration}m incline</span>
                      </div>
                    )}
                    {w.watch && <WatchSummary theme={theme} watch={w.watch} compact />}
                    <button className="ry-btn" onClick={() => { try { hapticWarning(); } catch {} setConfirmDel(i); }}
                      style={{ marginTop: 6, alignSelf: "flex-start",
                        background: "transparent", color: theme.coral,
                        border: `0.5px solid rgba(217,122,106,0.3)`, borderRadius: 8,
                        fontSize: 11, fontWeight: 600, padding: "6px 10px",
                        display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Icon name="trash" size={11}/> Delete session
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Section 5 — Per-exercise leaderboard (collapsible) */}
            <div style={{ padding: "18px 16px 0" }}>
              <button className="ry-btn" onClick={togglePerEx}
                style={{ width: "100%", background: theme.bg2, border: `0.5px solid ${theme.line}`,
                  borderRadius: 12, padding: "10px 14px", display: "flex", alignItems: "center",
                  justifyContent: "space-between", color: theme.ink1, fontSize: 12, fontWeight: 600 }}>
                <span>Per exercise · {byExercise.length}</span>
                <span style={{ color: theme.ink4, fontSize: 12 }}>{perExOpen ? "▾" : "▸"}</span>
              </button>
            </div>
            {perExOpen && (
              <div style={{ padding: "8px 16px 0" }}>
                <div style={{ background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 16, overflow: "hidden" }}>
                  {byExercise.map((entry, i) => {
                    const sparkVals = entry.sessions.slice()
                      .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
                      .slice(-8)
                      .map(s => s.bestWeight || 0);
                    return (
                      <button key={entry.name} className="ry-btn"
                        onClick={() => { hapticHayahSelection(); setSelectedEx(entry.name); }}
                        style={{ width: "100%", padding: "12px 14px",
                          borderBottom: i < byExercise.length - 1 ? `0.5px solid ${theme.lineSoft}` : "none",
                          background: "transparent", border: 0, color: "inherit", textAlign: "left",
                          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: theme.ink1 }}>{entry.name}</div>
                          <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1 }}>{entry.sessions.length} sessions · {entry.totalSets} sets</div>
                        </div>
                        <Sparkline values={sparkVals} color={theme.amber} />
                        <div style={{ textAlign: "right", minWidth: 50 }}>
                          <div className="ry-mono" style={{ fontSize: 12, color: theme.amber }}>
                            {entry.bestWeight > 0 ? `${entry.bestWeight}kg` : "—"}
                          </div>
                          <div style={{ fontSize: 9, color: theme.ink4, marginTop: 1, letterSpacing: "0.08em", textTransform: "uppercase" }}>Best</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Section 6 — All-time totals footer */}
            <div style={{ padding: "18px 16px 16px" }}>
              <div style={{ background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 12,
                padding: "8px 12px", fontSize: 11, color: theme.ink3, textAlign: "center" }}>
                All-time · {totals.sessions} sessions · {totals.sets} sets · {fmtVolKg(totals.volume)} · avg {fmtDurHuman(totals.avgDuration)} · longest streak {streaks.longest}
              </div>
            </div>

            {/* Section 7 — 12-week heatmap (moved to the bottom so the
                Sessions list + leaderboard are above-the-fold and the
                long calendar grid doesn't dominate the screen). */}
            <SecHd theme={theme} num="" label="12 weeks · activity" />
            <div style={{ padding: "0 16px 16px" }}>
              <div style={{ background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 16, padding: "14px 14px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "20px repeat(7, 1fr)", gap: 3, alignItems: "center", marginBottom: 4 }}>
                  <span/>
                  {["M","T","W","T","F","S","S"].map((d, i) => (
                    <div key={i} style={{ fontSize: 9, color: theme.ink4, textAlign: "center" }}>{d}</div>
                  ))}
                </div>
                {heatmap.map((row, ri) => (
                  <div key={ri} style={{ display: "grid", gridTemplateColumns: "20px repeat(7, 1fr)", gap: 3, marginBottom: 3 }}>
                    <div style={{ fontSize: 9, color: theme.ink4, textAlign: "right", lineHeight: "16px" }}>
                      {ri === 0 ? "12w" : ri === 11 ? "now" : ""}
                    </div>
                    {row.map((cell, ci) => {
                      const intensity = cell.vol > 0 ? Math.max(0.25, cell.vol / maxDayVol) : 0;
                      const bg = cell.future ? "transparent" : intensity > 0
                        ? `rgba(124,196,160,${intensity * 0.85 + 0.15})`
                        : theme.bg3;
                      return (
                        <button key={ci} className="ry-btn"
                          disabled={cell.future || cell.sessionIdx < 0}
                          onClick={() => { hapticHayahSelection(); setSelectedDay(cell.sessionIdx); }}
                          style={{
                            aspectRatio: "1", background: bg,
                            border: cell.future ? `0.5px dashed ${theme.line}` : `0.5px solid ${theme.lineSoft}`,
                            borderRadius: 4, padding: 0,
                            opacity: cell.future ? 0.4 : 1,
                            cursor: cell.sessionIdx >= 0 ? "pointer" : "default",
                          }}/>
                      );
                    })}
                  </div>
                ))}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                  <span style={{ fontSize: 9, color: theme.ink4 }}>less</span>
                  {[0, 0.3, 0.6, 0.9].map((t, i) => (
                    <div key={i} style={{ width: 10, height: 10, borderRadius: 2,
                      background: t === 0 ? theme.bg3 : `rgba(124,196,160,${t * 0.85 + 0.15})`,
                      border: `0.5px solid ${theme.lineSoft}` }}/>
                  ))}
                  <span style={{ fontSize: 9, color: theme.ink4 }}>more</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Per-exercise detail sheet */}
      {selectedEx && createPortal(
        <Sheet onClose={() => setSelectedEx(null)} theme={theme}>
          <ExerciseDetail theme={theme} entry={byExercise.find(e => e.name === selectedEx)} allEntries={byExercise} onClose={() => setSelectedEx(null)} />
        </Sheet>,
        document.body)}

      {/* Day-cell summary sheet */}
      {selectedDay !== null && history[selectedDay] && createPortal(
        <Sheet onClose={() => setSelectedDay(null)} theme={theme}>
          <DaySummary theme={theme} workout={history[selectedDay]} onClose={() => setSelectedDay(null)} />
        </Sheet>,
        document.body)}

      {confirmDel !== null && createPortal(
        <Sheet onClose={() => setConfirmDel(null)} theme={theme}>
          <div style={{ padding: "20px 20px 8px", textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🗑</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.02em", marginBottom: 6 }}>Delete this session?</div>
            <div style={{ fontSize: 13, color: theme.ink3, lineHeight: 1.5, marginBottom: 20 }}>
              {history[confirmDel]?.name} · {history[confirmDel]?.date}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 4px" }}>
              <button className="ry-btn"
                onClick={() => { onDeleteSession(confirmDel); setSelected(null); setConfirmDel(null); }}
                style={{ background: theme.coral, color: "#0A0A0E", border: 0, borderRadius: 14,
                  fontSize: 15, fontWeight: 700, padding: "15px 20px", width: "100%" }}>
                Delete
              </button>
              <button className="ry-btn" onClick={() => setConfirmDel(null)}
                style={{ background: theme.bg3, color: theme.ink1, border: `0.5px solid ${theme.line}`, borderRadius: 14,
                  fontSize: 15, fontWeight: 500, padding: "14px 20px", width: "100%" }}>
                Cancel
              </button>
            </div>
          </div>
        </Sheet>,
        document.body)}
      <StreakInfoSheet
        open={streakInfoOpen}
        onClose={() => setStreakInfoOpen(false)}
        title="Workout streak"
        accent={theme.amber}
        lines={[
          `Counts your most recent run of workout sessions where no gap between sessions exceeds 4 days. Rest days inside that window don't break it.`,
          `🔥 ${streaks.current} = your last ${streaks.current} session${streaks.current === 1 ? '' : 's'} each happened within 4 days of the previous one.`,
          { label: 'Current', value: `${streaks.current} session${streaks.current === 1 ? '' : 's'}` },
          { label: 'Longest', value: `${streaks.longest} session${streaks.longest === 1 ? '' : 's'}` },
          `A 5+ day gap resets the chain — log a session within 4 days of your last one to keep it alive.`,
        ]}
      />
    </div>
  );
}

function BigStat({ theme, value, label, color, onClick }) {
  const inner = (
    <>
      <div className="ry-mono" style={{ fontSize: 20, fontWeight: 500, color: color || theme.ink1 }}>{value}</div>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.ink4, marginTop: 2 }}>{label}</div>
    </>
  );
  if (!onClick) return <div style={{ textAlign: "center" }}>{inner}</div>;
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: "center", background: "none", border: 0,
        padding: 0, margin: 0, cursor: "pointer", fontFamily: "inherit",
        WebkitTapHighlightColor: "transparent",
      }}
    >{inner}</button>
  );
}

function MuscleChips({ theme, muscles }) {
  if (!muscles || muscles.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
      {muscles.map(m => (
        <span key={m} style={{
          fontSize: 9, fontWeight: 600, letterSpacing: "0.05em",
          padding: "2px 7px", borderRadius: 4,
          background: theme.bg3, color: theme.ink2,
          border: `0.5px solid ${theme.line}`,
        }}>{m}</span>
      ))}
    </div>
  );
}

function WatchInlineRow({ theme, watch }) {
  if (!watch) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 16px",
      borderTop: `0.5px solid ${theme.lineSoft}`,
      background: "rgba(79,217,194,0.04)",
      fontSize: 11, color: theme.ink3 }}>
      <span style={{ color: theme.teal, fontWeight: 600, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" }}>⌚ Watch</span>
      {watch.calories > 0 && <span><span className="ry-mono" style={{ color: theme.ink1 }}>{watch.calories}</span> kcal</span>}
      {watch.avgHr > 0 && <span><span className="ry-mono" style={{ color: theme.ink1 }}>{watch.avgHr}</span> avg</span>}
      {watch.maxHr > 0 && <span><span className="ry-mono" style={{ color: theme.coral }}>{watch.maxHr}</span> max</span>}
    </div>
  );
}

function Sparkline({ values, width = 50, height = 16, color }) {
  if (!values || values.length < 2) return <div style={{ width, height }}/>;
  const max = Math.max(...values), min = Math.min(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <polyline fill="none" stroke={color || "currentColor"} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" points={pts}/>
    </svg>
  );
}

// ───────── Activity helpers ─────────
// Helpers — pure, kept inline to mirror the rest of this file's style.
function startOfDay(ts) { const d = new Date(ts); d.setHours(0,0,0,0); return d.getTime(); }
function startOfWeekMon(ts) {
  const d = new Date(ts); d.setHours(0,0,0,0);
  const dow = d.getDay(); const back = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - back); return d.getTime();
}
function fmtVolKg(v) {
  if (!v) return "0kg";
  return v >= 1000 ? `${(v / 1000).toFixed(1)}t` : `${Math.round(v)}kg`;
}
function workoutVolume(w) {
  return (w.exercises || []).reduce((tot, ex) =>
    tot + (ex.sets || []).reduce((s, set) =>
      s + (Number(set.weight) || 0) * (Number(set.reps) || 0), 0), 0);
}

function ExerciseDetail({ theme, entry, allEntries, onClose }) {
  const [metric, setMetric] = useState("e1rm"); // "volume" | "weight" | "e1rm"
  const [mergeOpen, setMergeOpen] = useState(false);
  if (!entry) return null;
  const last12 = entry.sessions.slice().sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)).slice(-12);
  const e1rm = (w, r) => (w > 0 && r > 0) ? Math.round(w * (1 + r / 30) * 10) / 10 : 0;
  const seriesValue = (s) => metric === "volume" ? s.volume
    : metric === "weight" ? (s.bestWeight || 0)
    : e1rm(s.bestWeight || 0, s.reps || 0);
  const series = last12.map(seriesValue);
  const maxV = Math.max(1, ...series);
  const minV = Math.min(0, ...series);
  const W = 280, H = 80, pad = 6;
  const pts = last12.map((s, i) => {
    const x = pad + (i / Math.max(1, last12.length - 1)) * (W - pad * 2);
    const v = seriesValue(s);
    const y = H - pad - ((v - minV) / (maxV - minV || 1)) * (H - pad * 2);
    return [x, y, v];
  });
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const areaPath = pts.length > 0
    ? `${path} L${pts[pts.length-1][0].toFixed(1)},${H} L${pts[0][0].toFixed(1)},${H} Z`
    : "";
  const trend = pts.length >= 2 ? (pts[pts.length-1][2] - pts[0][2]) : 0;
  const trendPct = pts.length >= 2 && pts[0][2] > 0 ? Math.round((trend / pts[0][2]) * 100) : 0;
  const fmtVal = (v) => metric === "volume" ? fmtVolKg(v) : `${Math.round(v * 10) / 10}kg`;
  const metrics = [
    { k: "e1rm",   l: "Est. 1RM" },
    { k: "weight", l: "Top weight" },
    { k: "volume", l: "Volume" },
  ];

  return (
    <>
      <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.01em" }}>{entry.name}</div>
          {entry.muscles && <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1 }}>{entry.muscles}</div>}
        </div>
        <button className="ry-btn" onClick={onClose}
          style={{ background: theme.bg3, border: `0.5px solid ${theme.line}`, borderRadius: 8, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: theme.ink3 }}>
          <Icon name="x" size={14}/>
        </button>
      </div>

      <div style={{ padding: "8px 16px 0", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <div style={{ background: theme.bg2, borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
          <div className="ry-mono" style={{ fontSize: 16, color: theme.amber }}>
            {entry.bestWeight > 0 ? `${entry.bestWeight}kg` : "—"}
          </div>
          <div style={{ fontSize: 9, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 1 }}>Best set</div>
          {entry.bestSet && (
            <div style={{ fontSize: 9, color: theme.ink3, marginTop: 2 }}>× {entry.bestSet.reps} · {entry.bestSet.date}</div>
          )}
        </div>
        <div style={{ background: theme.bg2, borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
          <div className="ry-mono" style={{ fontSize: 16, color: theme.teal }}>{entry.totalSets}</div>
          <div style={{ fontSize: 9, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 1 }}>Sets</div>
        </div>
        <div style={{ background: theme.bg2, borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
          <div className="ry-mono" style={{ fontSize: 16, color: theme.ink1 }}>{entry.totalReps}</div>
          <div style={{ fontSize: 9, color: theme.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 1 }}>Reps</div>
        </div>
      </div>

      <div style={{ padding: "12px 16px 0" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.ink4 }}>
            Progression · last {last12.length}
          </div>
          {pts.length >= 2 && (
            <div className="ry-mono" style={{ fontSize: 11, color: trend >= 0 ? theme.teal : theme.coral, fontWeight: 600 }}>
              {trend >= 0 ? "▲" : "▼"} {Math.abs(trendPct)}%
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {metrics.map(m => (
            <button key={m.k} className="ry-btn"
              onClick={() => { hapticHayahSelection(); setMetric(m.k); }}
              style={{ flex: 1, padding: "7px 10px", borderRadius: 8,
                background: metric === m.k ? theme.amber : theme.bg2,
                color: metric === m.k ? "#0A0A0E" : theme.ink2,
                border: `0.5px solid ${metric === m.k ? theme.amber : theme.line}`,
                fontSize: 11, fontWeight: 600 }}>{m.l}</button>
          ))}
        </div>
        <div style={{ background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 12, padding: "10px 8px" }}>
          {pts.length === 0 ? (
            <div style={{ height: H, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: theme.ink4 }}>No sessions yet</div>
          ) : (
            <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
              <defs>
                <linearGradient id={`ry-grad-${metric}`} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={theme.amber} stopOpacity="0.28"/>
                  <stop offset="100%" stopColor={theme.amber} stopOpacity="0"/>
                </linearGradient>
              </defs>
              {areaPath && <path d={areaPath} fill={`url(#ry-grad-${metric})`} stroke="none"/>}
              <path d={path} fill="none" stroke={theme.amber} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              {pts.map((p, i) => (
                <g key={i}>
                  <circle cx={p[0]} cy={p[1]} r={i === pts.length - 1 ? 3 : 2}
                    fill={i === pts.length - 1 ? theme.amber : theme.bg2}
                    stroke={theme.amber} strokeWidth="1.2"/>
                </g>
              ))}
            </svg>
          )}
          {pts.length > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: theme.ink4, fontFamily: "monospace" }}>
              <span>{last12[0]?.date || ""}</span>
              <span style={{ color: theme.ink2, fontWeight: 600 }}>{fmtVal(pts[pts.length-1][2])}</span>
              <span>{last12[last12.length-1]?.date || ""}</span>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: "12px 16px 8px" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.ink4, marginBottom: 6 }}>Past sessions</div>
        <div style={{ background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 0.6fr 0.8fr", padding: "8px 12px",
            fontSize: 9, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase",
            color: theme.ink4, borderBottom: `0.5px solid ${theme.lineSoft}` }}>
            <span>Date</span><span style={{ textAlign: "center" }}>Sets</span><span style={{ textAlign: "right" }}>Best</span>
          </div>
          {entry.sessions.slice().sort((a,b) => (b.timestamp||0) - (a.timestamp||0)).map((s, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1.4fr 0.6fr 0.8fr", padding: "8px 12px",
              borderBottom: `0.5px solid ${theme.lineSoft}` }}>
              <span style={{ fontSize: 12, color: theme.ink2 }}>{s.date}</span>
              <span className="ry-mono" style={{ fontSize: 12, color: theme.ink3, textAlign: "center" }}>{s.sets}</span>
              <span className="ry-mono" style={{ fontSize: 12, color: theme.amber, textAlign: "right" }}>
                {s.bestWeight > 0 ? `${s.bestWeight}kg` : "—"}
              </span>
            </div>
          ))}
        </div>
        {/* Merge tool — user-confirmed alias when the auto-canonicaliser
            missed two equivalent exercises. Folds this exercise's entire
            history into a target id; takes effect on next app launch
            (mergeExercise bumps the migration version so the workout
            walk re-canonicalises on relaunch). */}
        <div style={{ marginTop: 14, padding: "10px 12px", background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 10 }}>
          {!mergeOpen ? (
            <button
              onClick={() => setMergeOpen(true)}
              style={{
                width: "100%", background: "none", border: 0, color: theme.ink3,
                fontSize: 11.5, fontFamily: "inherit", letterSpacing: "-0.005em",
                cursor: "pointer", textAlign: "left", padding: 0,
                WebkitTapHighlightColor: "transparent",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              <span style={{ color: theme.ink4 }}>⋮</span>
              <span>Merge into another exercise</span>
              <span style={{ marginLeft: "auto", color: theme.ink4, fontSize: 10 }}>→</span>
            </button>
          ) : (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: theme.ink3 }}>Pick the exercise to merge <b style={{ color: theme.ink1 }}>{entry.name}</b> into:</span>
                <button onClick={() => setMergeOpen(false)} style={{ background: "none", border: 0, color: theme.ink4, fontSize: 16, lineHeight: 1, cursor: "pointer", padding: "0 4px" }}>×</button>
              </div>
              <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                {(allEntries || []).filter(e => e.id !== entry.id).map(e => (
                  <button
                    key={e.id}
                    onClick={() => {
                      const ok = window.confirm(`Merge "${entry.name}" into "${e.name}"?\n\nAll ${entry.sessions.length} session(s) from "${entry.name}" will count toward "${e.name}".`);
                      if (!ok) return;
                      try { hapticHayahSelection(); } catch {}
                      mergeExercise(entry.id, e.id);
                      setMergeOpen(false);
                      onClose && onClose();
                      setTimeout(() => { try { window.location.reload(); } catch {} }, 100);
                    }}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "8px 10px", background: theme.bg1 || theme.bg, border: `0.5px solid ${theme.lineSoft}`,
                      borderRadius: 8, cursor: "pointer", textAlign: "left",
                      WebkitTapHighlightColor: "transparent",
                    }}
                  >
                    <span style={{ fontSize: 12, color: theme.ink1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{e.name}</span>
                    <span className="ry-mono" style={{ fontSize: 10, color: theme.ink4, flexShrink: 0, marginLeft: 8 }}>
                      {e.sessions.length}× · {e.bestWeight > 0 ? `${e.bestWeight}kg` : "—"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function DaySummary({ theme, workout, onClose }) {
  const vol = workoutVolume(workout);
  return (
    <>
      <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.01em" }}>{workout.name}</div>
          <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1 }}>{workout.date} · {workout.time}</div>
        </div>
        <button className="ry-btn" onClick={onClose}
          style={{ background: theme.bg3, border: `0.5px solid ${theme.line}`, borderRadius: 8, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: theme.ink3 }}>
          <Icon name="x" size={14}/>
        </button>
      </div>
      <div style={{ padding: "8px 16px 0", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <Stat theme={theme} value={fmtDurHuman(workout.duration || 0)} label="Duration"/>
        <Stat theme={theme} value={workout.sets || 0} label="Sets" valueColor={theme.teal}/>
        <Stat theme={theme} value={fmtVolKg(vol)} label="Volume"/>
      </div>
      {workout.watch && (
        <div style={{ padding: "0 16px" }}>
          <WatchSummary theme={theme} watch={workout.watch} compact/>
        </div>
      )}
      <div style={{ padding: "12px 16px 8px" }}>
        <div style={{ background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 12, overflow: "hidden" }}>
          {(workout.exercises || []).filter(e => e.sets?.length).map((ex, i, arr) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 12px", borderBottom: i < arr.length - 1 ? `0.5px solid ${theme.lineSoft}` : "none" }}>
              <span style={{ fontSize: 13, color: theme.ink1 }}>{ex.name}</span>
              <span className="ry-mono" style={{ fontSize: 12, color: theme.ink3 }}>
                {ex.sets.length}×{ex.sets[0]?.reps}
                {ex.sets[0]?.weight && ex.sets[0].weight !== "BW" && ex.sets[0].weight !== "0" ? ` @ ${ex.sets[0].weight}kg` : ""}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ───────── GoalsScreen ─────────
const GOAL_TYPES = [
  { id: "frequency", label: "Frequency",   desc: "Sessions per week",   unit: "sessions", icon: "📅" },
  { id: "volume",    label: "Volume",      desc: "Cumulative kilograms", unit: "kg",      icon: "🏋️" },
  { id: "pr",        label: "Exercise PR", desc: "Reach a target weight", unit: "kg",     icon: "🏆" },
  { id: "streak",    label: "Streak",      desc: "Days in a row",        unit: "days",    icon: "🔥" },
];

function goalProgress(goal, history) {
  const target = Number(goal.target) || 0;
  if (target <= 0) return { current: 0, pct: 0 };
  if (goal.type === "frequency") {
    const ws = startOfWeekMon(Date.now());
    const days = new Set();
    history.forEach(w => {
      if (!w.timestamp) return;
      if (startOfDay(w.timestamp) >= ws) days.add(startOfDay(w.timestamp));
    });
    const current = days.size;
    return { current, pct: Math.min(1, current / target) };
  }
  if (goal.type === "volume") {
    const since = goal.createdAt || 0;
    const current = history.filter(w => (w.timestamp || 0) >= since).reduce((t, w) => t + workoutVolume(w), 0);
    return { current: Math.round(current), pct: Math.min(1, current / target) };
  }
  if (goal.type === "pr") {
    let best = 0;
    for (const w of history) {
      for (const ex of (w.exercises || [])) {
        if (ex.name !== goal.exerciseName) continue;
        for (const s of (ex.sets || [])) {
          const sw = parseFloat(s.weight) || 0;
          if (sw > best) best = sw;
        }
      }
    }
    return { current: best, pct: Math.min(1, best / target) };
  }
  if (goal.type === "streak") {
    const { current } = computeWorkoutStreaks(history);
    return { current, pct: Math.min(1, current / target) };
  }
  return { current: 0, pct: 0 };
}

function goalStatus(pct) {
  if (pct >= 1) return { label: "Complete", color: "#7CC4A0" };
  if (pct >= 0.6) return { label: "On track", color: "#4FD9C2" };
  return { label: "At risk", color: "#D97A6A" };
}

// Body Composition card hosted in Exercise → Goals. Reuses the canonical
// panel from CalorieCounter so weight/body-fat trends + AI analysis live
// alongside training goals (recomp belongs with lifting, not calories).
function RiyadahBodyCompositionCard({ theme, apiRef }) {
  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  }, []);
  const parse = (k, fb) => { try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(fb)); } catch { return fb; } };
  const [calorieLog, setCalorieLog] = useState(() => parse("calorieLog", {}));
  const [weightLog]  = useState(() => parse("calorieWeightLog", []));
  const profile = useMemo(() => parse("ghidhaProfile", {}), []);
  const [hasHealth, setHasHealth] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    (async () => {
      try { await GhidhaHealth.isAvailable(); setHasHealth(true); } catch { setHasHealth(false); }
    })();
    const onStorage = (e) => {
      if (!e || e.key === "calorieLog" || e.key == null) {
        try { setCalorieLog(JSON.parse(localStorage.getItem("calorieLog") || "{}")); } catch {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const onSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const bc = await GhidhaHealth.readBodyComposition();
      const log = parse("calorieLog", {});
      const entry = log[today] || {};
      const bodyHistory = {
        weight:  (bc?.weightSamples  || []).map(s => ({ date: s.date, value: s.value })),
        bodyFat: (bc?.bodyFatSamples || []).map(s => ({ date: s.date, value: s.value })),
      };
      const bodyMassKg = bodyHistory.weight.length  ? bodyHistory.weight[bodyHistory.weight.length-1].value  : (entry.bodyMassKg || 0);
      const bodyFatPct = bodyHistory.bodyFat.length ? bodyHistory.bodyFat[bodyHistory.bodyFat.length-1].value : (entry.bodyFatPct || 0);
      log[today] = { ...entry, bodyMassKg, bodyFatPct, bodyHistory };
      localStorage.setItem("calorieLog", JSON.stringify(log));
      setCalorieLog(log);
    } catch {}
    setSyncing(false);
  };

  useEffect(() => {
    if (apiRef) apiRef.current = { onSync, hasHealth };
  });

  const colors = {
    text: theme.ink1,
    muted: theme.ink3,
    border: theme.line,
    s1: theme.bg2,
    s2: theme.bg3,
    gold: theme.amber,
    teal: theme.teal,
    accent: theme.amber,
    danger: theme.coral,
    darkMode: theme.bg0 === "#050507",
  };

  return (
    <div style={{ padding: "12px 16px 0" }}>
      <BodyCompositionPanel
        calorieLog={calorieLog}
        today={today}
        weightLog={weightLog}
        heightCm={parseFloat(profile?.height) || 0}
        goalWeightKg={parseFloat(profile?.weight) || 0}
        hasHealth={hasHealth}
        syncing={syncing}
        onSync={onSync}
        colors={colors}
      />
    </div>
  );
}

// Pull-to-refresh "composition scan" animation — a body silhouette being
// scanned inside a body-fat-style ring. Themed to the data being pulled
// (weight + body fat from Apple Health).
function BodyScanIndicator({ pullY, isSyncing, threshold, theme }) {
  if (pullY <= 2 && !isSyncing) return null;
  const progress = Math.min(pullY / threshold, 1);
  const armed = isSyncing || pullY >= threshold;
  const gold = theme.amber;
  const teal = theme.teal;
  const muted = theme.ink3;
  const FM = "SFMono-Regular, ui-monospace, 'Fira Code', monospace";
  const height = isSyncing ? threshold : pullY;

  // Ring geometry
  const R = 19, C = 2 * Math.PI * R;
  const fillOffset = C * (1 - progress);

  const caption = isSyncing ? 'SCANNING COMPOSITION' : armed ? 'RELEASE TO SCAN' : 'PULL TO SCAN';
  const accent = armed ? gold : muted;
  const scanY = isSyncing ? null : 16 + progress * 18; // static scan-line while pulling

  return (
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 6, height, overflow: 'hidden', pointerEvents: 'none',
      transition: pullY === 0 && !isSyncing ? 'height 0.25s ease' : 'none',
    }}>
      <style>{`
        @keyframes bscan-spin{to{transform:rotate(360deg)}}
        @keyframes bscan-sweep{0%{transform:translateY(-10px);opacity:0}12%{opacity:1}88%{opacity:1}100%{transform:translateY(10px);opacity:0}}
        @keyframes bscan-rise{0%{transform:translateY(7px) scale(0.6);opacity:0}25%{opacity:1}100%{transform:translateY(-9px) scale(1);opacity:0}}
        @keyframes bscan-pulse{0%,100%{opacity:.55}50%{opacity:1}}
      `}</style>
      <div style={{
        position: 'relative', width: 46, height: 46,
        opacity: isSyncing ? 1 : Math.max(0.25, progress),
        transform: `scale(${isSyncing ? 1 : 0.7 + progress * 0.3})`,
        transition: 'transform 0.12s ease',
      }}>
        <svg viewBox="0 0 46 46" width={46} height={46}>
          <defs>
            <linearGradient id="bscan-body" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={gold} />
              <stop offset="100%" stopColor={teal} />
            </linearGradient>
            <clipPath id="bscan-fig">
              <circle cx="23" cy="16" r="4" />
              <path d="M14,34 C14,25 17.5,21.5 23,21.5 C28.5,21.5 32,25 32,34 Z" />
            </clipPath>
          </defs>

          {/* Track ring */}
          <circle cx="23" cy="23" r={R} fill="none" stroke={theme.line} strokeWidth="2.5" />

          {/* Progress / spinner arc */}
          {isSyncing ? (
            <g style={{ transformOrigin: '23px 23px', animation: 'bscan-spin 0.9s linear infinite' }}>
              <circle cx="23" cy="23" r={R} fill="none" stroke={gold} strokeWidth="2.5"
                strokeLinecap="round" strokeDasharray={`${C * 0.28} ${C}`} />
            </g>
          ) : (
            <circle cx="23" cy="23" r={R} fill="none" stroke={accent} strokeWidth="2.5"
              strokeLinecap="round" strokeDasharray={C} strokeDashoffset={fillOffset}
              transform="rotate(-90 23 23)"
              style={{ transition: 'stroke-dashoffset 0.1s linear, stroke 0.15s ease' }} />
          )}

          {/* Body silhouette */}
          <g clipPath="url(#bscan-fig)">
            <rect x="13" y="11" width="20" height="25" fill="url(#bscan-body)"
              opacity={isSyncing ? 1 : 0.35 + progress * 0.45}
              style={isSyncing ? { animation: 'bscan-pulse 1.4s ease-in-out infinite' } : undefined} />
            {/* Scan line sweeping over the figure */}
            {isSyncing ? (
              <rect x="11" y="22" width="24" height="2" fill="#fff"
                style={{ animation: 'bscan-sweep 1.3s ease-in-out infinite' }} />
            ) : (
              <rect x="11" y={scanY} width="24" height="1.6" fill={gold} opacity={0.9} />
            )}
          </g>

          {/* Rising particles (fat melting / data points) while syncing */}
          {isSyncing && [0, 1, 2].map(i => (
            <circle key={i} cx={16 + i * 7} cy="30" r="1.3" fill={i === 1 ? teal : gold}
              style={{ animation: `bscan-rise 1.4s ease-in-out ${i * 0.32}s infinite` }} />
          ))}
        </svg>
      </div>
      <span style={{
        fontFamily: FM, fontSize: 8, letterSpacing: '2.4px', color: accent,
        textTransform: 'uppercase', whiteSpace: 'nowrap',
        opacity: isSyncing ? 1 : Math.max(0.3, progress),
        animation: isSyncing ? 'bscan-pulse 1.4s ease-in-out infinite' : 'none',
      }}>{caption}</span>
    </div>
  );
}

function GoalsScreen({ theme, history, goals, setGoals, onSubTab }) {
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // id | null

  const enriched = useMemo(() =>
    goals.map(g => ({ ...g, ...goalProgress(g, history) })),
    [goals, history]);
  const active = enriched.filter(g => g.pct < 1);
  const completed = enriched.filter(g => g.pct >= 1);

  const addGoal = (goal) => {
    const next = [...goals, { id: `g-${Date.now()}`, createdAt: Date.now(), ...goal }];
    setGoals(next);
    setShowAdd(false);
    hapticHayahSuccess();
  };
  const removeGoal = (id) => {
    hapticWarning();
    setGoals(goals.filter(g => g.id !== id));
    setConfirmDel(null);
  };

  // ── Pull-to-refresh → Apple Health body-composition scan ──
  const syncApiRef = useRef(null);
  const scrollRef = useRef(null);
  const pullRef = useRef({ startY: 0, active: false });
  const [pullY, setPullY] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const THRESHOLD = 72;

  const handleTouchStart = (e) => {
    if (isSyncing) return;
    const el = scrollRef.current;
    if (!el || el.scrollTop > 0) return;
    pullRef.current = { startY: e.touches[0].clientY, active: true };
  };
  const handleTouchMove = (e) => {
    if (!pullRef.current.active || isSyncing) return;
    const dy = e.touches[0].clientY - pullRef.current.startY;
    if (dy <= 0) { pullRef.current.active = false; setPullY(0); return; }
    setPullY(Math.min(dy * 0.5, THRESHOLD + 20));
  };
  const handleTouchEnd = async () => {
    if (!pullRef.current.active) return;
    pullRef.current.active = false;
    const api = syncApiRef.current;
    if (pullY >= THRESHOLD && api?.hasHealth && api?.onSync) {
      setPullY(THRESHOLD);
      setIsSyncing(true);
      hapticHayahSelection();
      const started = Date.now();
      try { await api.onSync(); } catch {}
      // Keep the scan animation up long enough to feel intentional
      const elapsed = Date.now() - started;
      if (elapsed < 900) await new Promise(r => setTimeout(r, 900 - elapsed));
      hapticHayahSuccess();
      setIsSyncing(false);
    }
    setPullY(0);
  };

  return (
    <div className="ry-slide" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <SubTabs theme={theme} active={3} onChange={onSubTab} />
      <div style={{ position: "relative", flex: 1, minHeight: 0, overflow: "hidden" }}>
        <BodyScanIndicator pullY={pullY} isSyncing={isSyncing} threshold={THRESHOLD} theme={theme} />
        <div
          ref={scrollRef}
          className="ry-sb"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          style={{
            height: "100%", overflowY: "auto",
            paddingBottom: "calc(96px + env(safe-area-inset-bottom))",
            transform: pullY > 0 ? `translateY(${pullY}px)` : "none",
            transition: pullY === 0 ? "transform 0.25s ease" : "none",
          }}>
        <RiyadahBodyCompositionCard theme={theme} apiRef={syncApiRef} />
        <div style={{ padding: "12px 16px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.03em" }}>Goals</div>
            <div style={{ fontSize: 13, color: theme.ink3, marginTop: 2 }}>Targets to chase</div>
          </div>
          <button className="ry-btn" onClick={() => { hapticHayahSelection(); setShowAdd(true); }}
            style={{ background: theme.amber, color: "#0A0A0E", border: 0, borderRadius: 10,
              fontSize: 12, fontWeight: 700, padding: "7px 12px", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Icon name="plus" size={13} color="#0A0A0E"/> Add goal
          </button>
        </div>

        {goals.length === 0 && (
          <div style={{ padding: "40px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🎯</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.ink1, marginBottom: 6 }}>No goals yet</div>
            <div style={{ fontSize: 13, color: theme.ink3, lineHeight: 1.5 }}>Tap Add goal to set a target</div>
          </div>
        )}

        {active.length > 0 && (
          <>
            <SecHd theme={theme} num="01" label={`Active · ${active.length}`} />
            <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 10 }}>
              {active.map(g => <GoalCard key={g.id} theme={theme} goal={g} onDelete={() => setConfirmDel(g.id)} />)}
            </div>
          </>
        )}

        {completed.length > 0 && (
          <>
            <SecHd theme={theme} num="02" label={`Completed · ${completed.length}`} />
            <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
              {completed.map(g => <GoalCard key={g.id} theme={theme} goal={g} onDelete={() => setConfirmDel(g.id)} />)}
            </div>
          </>
        )}
        </div>
      </div>

      {showAdd && createPortal(
        <Sheet onClose={() => setShowAdd(false)} theme={theme}>
          <AddGoalForm theme={theme} history={history} onCancel={() => setShowAdd(false)} onAdd={addGoal} />
        </Sheet>,
        document.body)}

      {confirmDel && createPortal(
        <Sheet onClose={() => setConfirmDel(null)} theme={theme}>
          <div style={{ padding: "20px 20px 8px", textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🗑</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.02em", marginBottom: 6 }}>Delete this goal?</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 4px", marginTop: 14 }}>
              <button className="ry-btn" onClick={() => removeGoal(confirmDel)}
                style={{ background: theme.coral, color: "#0A0A0E", border: 0, borderRadius: 14,
                  fontSize: 15, fontWeight: 700, padding: "15px 20px", width: "100%" }}>
                Delete
              </button>
              <button className="ry-btn" onClick={() => setConfirmDel(null)}
                style={{ background: theme.bg3, color: theme.ink1, border: `0.5px solid ${theme.line}`, borderRadius: 14,
                  fontSize: 15, fontWeight: 500, padding: "14px 20px", width: "100%" }}>
                Cancel
              </button>
            </div>
          </div>
        </Sheet>,
        document.body)}
    </div>
  );
}

function GoalCard({ theme, goal, onDelete }) {
  const status = goalStatus(goal.pct);
  const typeMeta = GOAL_TYPES.find(t => t.id === goal.type) || GOAL_TYPES[0];
  const unit = goal.type === "pr" ? "kg" : goal.type === "volume" ? "kg" : goal.type === "frequency" ? "" : "";
  const fmtCur = goal.type === "volume" ? fmtVolKg(goal.current) : `${goal.current}${unit ? unit : ""}`;
  const fmtTar = goal.type === "volume" ? fmtVolKg(goal.target) : `${goal.target}${unit ? unit : ""}`;
  return (
    <div className="ry-fade" style={{
      background: theme.bg2, border: `0.5px solid ${theme.line}`, borderRadius: 16, padding: "14px 14px",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: theme.bg3,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{typeMeta.icon}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.ink1, letterSpacing: "-0.01em" }}>{goal.name}</div>
            <div style={{ fontSize: 11, color: theme.ink3, marginTop: 1 }}>
              {typeMeta.label}{goal.type === "pr" && goal.exerciseName ? ` · ${goal.exerciseName}` : ""}
            </div>
          </div>
        </div>
        <button className="ry-btn" onClick={onDelete} aria-label="Delete goal"
          style={{ background: "transparent", border: 0, color: theme.ink4,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
          <Icon name="dots" size={14}/>
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span className="ry-mono" style={{ fontSize: 13, fontWeight: 500, color: theme.ink1 }}>
          {fmtCur} <span style={{ color: theme.ink4 }}>/ {fmtTar}</span>
        </span>
        <span className="ry-mono" style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.12em",
          textTransform: "uppercase", color: status.color }}>{status.label}</span>
      </div>

      <div style={{ height: 6, borderRadius: 3, background: theme.bg3, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${Math.round(goal.pct * 100)}%`,
          background: status.color,
          transition: "width .3s",
        }}/>
      </div>
    </div>
  );
}

function AddGoalForm({ theme, history, onCancel, onAdd }) {
  const [type, setType] = useState("frequency");
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [exerciseName, setExerciseName] = useState("");

  // Suggest exercises from history.
  const exerciseOptions = useMemo(() => {
    const names = new Set();
    history.forEach(w => (w.exercises || []).forEach(ex => ex.name && names.add(ex.name)));
    return Array.from(names);
  }, [history]);

  const placeholder = type === "frequency" ? "e.g. 4"
    : type === "volume"   ? "e.g. 10000"
    : type === "pr"       ? "e.g. 16"
    : "e.g. 20";
  const unitLabel = type === "frequency" ? "sessions / week"
    : type === "volume"   ? "kg total"
    : type === "pr"       ? "kg"
    : "days in a row";

  const valid = name.trim().length > 0 && Number(target) > 0
    && (type !== "pr" || exerciseName.trim().length > 0);

  const submit = () => {
    if (!valid) return;
    const goal = { type, name: name.trim(), target: Number(target) };
    if (type === "pr") goal.exerciseName = exerciseName.trim();
    onAdd(goal);
  };

  return (
    <>
      <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: theme.ink1, letterSpacing: "-0.01em" }}>New goal</div>
        <button className="ry-btn" onClick={onCancel}
          style={{ background: theme.bg3, border: `0.5px solid ${theme.line}`, borderRadius: 8, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: theme.ink3 }}>
          <Icon name="x" size={14}/>
        </button>
      </div>

      <div style={{ padding: "0 16px 8px" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.ink4, marginBottom: 6 }}>Type</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {GOAL_TYPES.map(t => (
            <button key={t.id} className="ry-btn" onClick={() => setType(t.id)}
              style={{ padding: "10px 10px", borderRadius: 12, textAlign: "left",
                background: type === t.id ? theme.amberDim : theme.bg3,
                border: `0.5px solid ${type === t.id ? "rgba(224,160,48,0.35)" : theme.line}`,
                color: "inherit",
                display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>{t.icon}</span>
              <span>
                <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: type === t.id ? theme.amber : theme.ink1 }}>{t.label}</span>
                <span style={{ display: "block", fontSize: 10, color: theme.ink3, marginTop: 1 }}>{t.desc}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: "8px 16px" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.ink4, marginBottom: 6 }}>Name</div>
        <input className="ry-input" value={name} onChange={e => setName(e.target.value)}
          placeholder="e.g. Lift 4× a week"
          style={{ textAlign: "left", padding: "10px 12px" }}/>
      </div>

      {type === "pr" && (
        <div style={{ padding: "8px 16px" }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.ink4, marginBottom: 6 }}>Exercise</div>
          {exerciseOptions.length > 0 ? (
            <div className="ry-sb" style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
              {exerciseOptions.map(n => (
                <button key={n} className="ry-btn" onClick={() => setExerciseName(n)}
                  style={{ padding: "6px 10px", borderRadius: 999,
                    background: exerciseName === n ? theme.amberDim : theme.bg3,
                    border: `0.5px solid ${exerciseName === n ? "rgba(224,160,48,0.35)" : theme.line}`,
                    color: exerciseName === n ? theme.amber : theme.ink2,
                    fontSize: 11, fontWeight: 500, whiteSpace: "nowrap" }}>
                  {n}
                </button>
              ))}
            </div>
          ) : (
            <input className="ry-input" value={exerciseName} onChange={e => setExerciseName(e.target.value)}
              placeholder="e.g. DB Curl"
              style={{ textAlign: "left", padding: "10px 12px" }}/>
          )}
        </div>
      )}

      <div style={{ padding: "8px 16px 4px" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.ink4, marginBottom: 6 }}>Target ({unitLabel})</div>
        <input className="ry-input" value={target} onChange={e => setTarget(e.target.value.replace(/[^0-9.]/g, ""))}
          inputMode="decimal" placeholder={placeholder}
          style={{ textAlign: "left", padding: "10px 12px" }}/>
      </div>

      <div style={{ padding: "12px 16px 8px", display: "flex", flexDirection: "column", gap: 8 }}>
        <button className="ry-btn" onClick={submit} disabled={!valid}
          style={{ background: theme.amber, color: "#0A0A0E", border: 0, borderRadius: 14,
            fontSize: 15, fontWeight: 700, padding: "14px 20px", width: "100%",
            opacity: valid ? 1 : 0.5 }}>
          Add goal
        </button>
        <button className="ry-btn" onClick={onCancel}
          style={{ background: theme.bg3, color: theme.ink1, border: `0.5px solid ${theme.line}`, borderRadius: 14,
            fontSize: 15, fontWeight: 500, padding: "13px 20px", width: "100%" }}>
          Cancel
        </button>
      </div>
    </>
  );
}
